// Iter 62da (Rayan msg 2026-09-03) — Nouveau module Tâches ASA complet.
// Design system ASA existant : navy #0B1330, bleu électrique #3E7BFA, Sora,
// glassmorphism, texture grain. Aucune italique.
//
// Structure :
//   /tasks → EntryScreen (Ta vue / Vue équipe)
//     ↓ profile choisi
//   TasksShell (badge + tabs Kanban / Calendrier)
//     - KanbanDailyView (4 colonnes, drag&drop, journalier)
//     - CalendarMonthView (mois, drag&drop entre jours)
//     - TaskDetailPanel (side panel)
//     - TaskCreateDialog (form avec toggles)
import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  DndContext, PointerSensor, MouseSensor, TouchSensor, useSensor, useSensors, closestCenter, closestCorners, rectIntersection, pointerWithin, DragOverlay, useDroppable, useDraggable,
} from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { motion, AnimatePresence } from 'framer-motion';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Plus, X, Check, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, Play, Pause, Search, Lock, Trash2, Sparkles, ExternalLink, Edit2, Paperclip, Download, File as FileIcon, MoreHorizontal, Calendar as CalendarIcon, Grid as GridIcon, Type, AlignLeft } from 'lucide-react';
import DayTimelineView from './DayTimelineView';
import { ManagedSopButtons } from '../components/ManagedSops';
import { TaskHistoryButton } from '../components/TaskHistoryPanel';
import { CopyField } from '../components/PublicationCopyBlock';
import { StuckVideosAlert } from '../components/StuckVideosAlert';
import { JoinCallButton } from '../components/JoinCallButton';
import { askDelivery, DeliveryBadge, deliveryToast } from '../components/DeliveryDialog';
import { PostDayButton } from '../components/PostDayButton';

// Monteur qui termine une vidéo projet → pop-up « Drive ou Frame ? » (null = annulé).
async function deliveryPayload(task, newStatus, userId) {
  const needs = task._migrated_from_tasks && ['fini', 'a_valider'].includes(newStatus) && newStatus !== task.status
    && !['rayan', 'hugo'].includes(userId) && !task.supervised;
  if (!needs) return { status: newStatus };
  const d = await askDelivery(task);
  return d ? { status: 'a_valider', ...d } : null;
}

const isPosterTask = (t) => t?.source === 'video_to_post' || t?.source === 'asa_client_preview';
const isPosted = (t) => isPosterTask(t) && t.status === 'fini';
const todayLocalIso = () => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`; };
// Post pas fait à sa date : il reste à sa date (rien ne bouge) mais s'affiche aussi aujourd'hui, en rouge.
// Vidéo « En cours » pas finie à sa date : reste affichée chaque jour (aujourd'hui) jusqu'à ce qu'elle soit faite.
// Tâche / vidéo « En cours » (ou À refaire) : affichée tous les jours, jusqu'à aujourd'hui, tant qu'elle ne quitte pas la colonne.
const isCarriedVideo = (t) => !isPosterTask(t) && ['en_cours', 'a_refaire'].includes(t.status);
const isLatePost = (t) => isPosterTask(t) && !['fini', 'a_valider'].includes(t.status) && (t.date_fin || t.date_debut || '9999') < todayLocalIso();

// ─── Design tokens ASA ───────────────────────────────────────────────────────
const NAVY       = '#0B1330';
const SURFACE    = 'rgba(20, 29, 66, 0.62)';
const SURFACE2   = 'rgba(28, 38, 80, 0.55)';
const BORDER     = 'rgba(62, 123, 250, 0.16)';
const BLUE       = '#3E7BFA';
const BLUE_LIGHT = '#5BBEFC';
const T1 = '#F1F4F9';
const T2 = 'rgba(200,215,240,0.72)';
const T3 = 'rgba(200,215,240,0.42)';

// Iter 62du — Detection mobile pour adapter la vue Tâches (Kanban 5 colonnes
// devient une seule colonne + tabs de statut en haut, plus lisible < 720px).
function useIsMobile(breakpoint = 720) {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(`(max-width: ${breakpoint}px)`).matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener?.('change', handler);
    return () => mq.removeEventListener?.('change', handler);
  }, [breakpoint]);
  return isMobile;
}

// Couleurs statut / priorité (dérivées de la base ASA)
const STATUS = {
  a_faire:    { label: 'À faire',    color: '#8FA5CA', bg: 'rgba(143,165,202,0.14)' },
  en_cours:   { label: 'En cours',   color: '#3E7BFA', bg: 'rgba(62,123,250,0.18)' },
  a_valider:  { label: 'À valider',  color: '#F5C46A', bg: 'rgba(245,196,106,0.16)' },
  a_refaire:  { label: 'En correction',  color: '#FF7B7B', bg: 'rgba(255,123,123,0.16)' },
  fini:       { label: 'Fini',       color: '#7FDDB8', bg: 'rgba(127,221,184,0.14)' },
};
// Iter 62ds — Priorités "vraiment visibles" : chaque niveau porte un dégradé
// sémantique unique (rouge → orange → jaune → gris) appliqué sur la carte.
// `grad` est un dégradé linéaire pour le fond de carte, `glow` sert au reflet
// d'ombre externe, `border` teinte la bordure gauche.
const PRIORITY = {
  urgent:    {
    label: 'Urgent',
    color: '#FF6B6B',
    grad: 'linear-gradient(135deg, rgba(255,107,107,0.28) 0%, rgba(255,107,107,0.08) 55%, rgba(28,38,80,0.55) 100%)',
    glow: '0 10px 26px -12px rgba(255,107,107,0.65)',
    accent: '#FF6B6B',
  },
  important: {
    label: 'Important',
    color: '#FF9F45',
    grad: 'linear-gradient(135deg, rgba(255,159,69,0.26) 0%, rgba(255,159,69,0.07) 55%, rgba(28,38,80,0.55) 100%)',
    glow: '0 10px 26px -12px rgba(255,159,69,0.55)',
    accent: '#FF9F45',
  },
  normal:    {
    label: 'Normal',
    color: '#F5C46A',
    grad: 'linear-gradient(135deg, rgba(245,196,106,0.22) 0%, rgba(245,196,106,0.06) 55%, rgba(28,38,80,0.55) 100%)',
    glow: '0 8px 22px -12px rgba(245,196,106,0.45)',
    accent: '#F5C46A',
  },
  repet:     {
    label: 'Faible',
    color: '#B7C3D9',
    grad: 'linear-gradient(135deg, rgba(200,215,240,0.18) 0%, rgba(200,215,240,0.05) 55%, rgba(28,38,80,0.55) 100%)',
    glow: '0 6px 18px -10px rgba(200,215,240,0.3)',
    accent: '#B7C3D9',
  },
};
// Iter 62dk — Ajout du statut "À refaire" (admin renvoie une tâche avec correctifs).
const NEXT_STATUS = { a_faire: 'en_cours', en_cours: 'a_valider', a_valider: 'fini', a_refaire: 'en_cours' };
const NEXT_LABEL  = { a_faire: 'Démarrer', en_cours: 'Envoyer à valider', a_valider: 'Valider', a_refaire: 'Reprendre' };

// Iter 62ff — Team dynamique. Ces objets sont MUTÉS au runtime à partir de
// `/api/users` (static USERS + dynamic self-signup approuvés). Les composants
// qui lisent `ALL_TEAM`/`ADMIN_IDS`/`TEAM` doivent appeler `useTeamVersion()`
// pour re-render dès qu'une nouvelle recrue est ajoutée (ex: Amine).
const TEAM = {
  // Fallback statique (le temps du 1er fetch) — gardé pour éviter un flash vide
  admins: [
    { id: 'rayan',  name: 'Rayan',         initial: 'R' },
    { id: 'hugo',   name: 'Hugo Roussel',  initial: 'H' },
  ],
  monteurs: [
    { id: 'axel',           name: 'Axel',              initial: 'A' },
    { id: 'aryan',          name: 'Elevate Influence', initial: 'E' },
    { id: 'ryan_derush',    name: 'Ryan Derush',       initial: 'R' },
    { id: 'mathis',         name: 'Mathis',            initial: 'M' },
    { id: 'hallil_yacine',  name: 'Hallil Yacine',     initial: 'H' },
  ],
};
let ALL_TEAM = [...TEAM.admins, ...TEAM.monteurs];
let ADMIN_IDS = new Set(['rayan', 'hugo']);
// Peuvent passer leurs propres tâches en « Fini » (founders + gestionnaire type Elevate Influence)
let SELF_VALIDATOR_IDS = new Set(['rayan', 'hugo', 'aryan']);
const _teamSubs = new Set();
function _rebuildTeamIndex() {
  ALL_TEAM = [...TEAM.admins, ...TEAM.monteurs];
  ADMIN_IDS = new Set(TEAM.admins.map((a) => a.id));
  _teamSubs.forEach((cb) => { try { cb(); } catch { /* noop */ } });
}
function _mapUserToMember(u) {
  const name = u.name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.id;
  const initial = (u.short || name[0] || '?').toUpperCase();
  return { id: u.id, name, initial };
}
async function hydrateTeam() {
  try {
    const r = await api.get('/users');
    const users = (r.data || []).filter((u) => !u.is_archived);
    // Admins = founders + gestionnaire (admin-lite type Aryan côté backend)
    const admins = users
      .filter((u) => u.role === 'founder')
      .map(_mapUserToMember);
    // Monteurs = tout contributor ou flaggué is_monteur (hors clients, hors founders)
    // On exclut également les users à rôle "client" (vue restreinte).
    const monteurs = users
      .filter((u) => u.role !== 'founder' && u.role !== 'client' && u.id !== 'rayan' && u.id !== 'hugo')
      .map(_mapUserToMember);
    // Déduplication par id (au cas où un user apparaîtrait dans les deux listes)
    const seen = new Set();
    const dedup = (arr) => arr.filter((m) => (seen.has(m.id) ? false : (seen.add(m.id), true)));
    SELF_VALIDATOR_IDS = new Set(users.filter((u) => u.role === 'founder' || u.role === 'gestionnaire' || u.job_role === 'posteur').map((u) => u.id));
    TEAM.admins = dedup(admins);
    TEAM.monteurs = dedup(monteurs);
    _rebuildTeamIndex();
  } catch (e) {
    // Fallback statique conservé — pas de crash si API down
    console.warn('[TasksV2] hydrateTeam failed, using static fallback', e);
  }
}
function useTeamVersion() {
  const [, setV] = useState(0);
  useEffect(() => {
    const cb = () => setV((x) => x + 1);
    _teamSubs.add(cb);
    return () => { _teamSubs.delete(cb); };
  }, []);
}
function memberById(id) {
  return ALL_TEAM.find((m) => m.id === id) || (id ? { id, name: id, initial: (id[0] || '?').toUpperCase() } : null);
}
// Iter 62dh — Multi-assign : normalise en list les responsables (compat legacy string)
function respsOf(task) {
  if (!task) return [];
  const r = task.responsable;
  if (Array.isArray(r)) return r.filter(Boolean);
  if (typeof r === 'string' && r) return [r];
  return [];
}
// Iter 62dh — Format "vu il y a X" ou "il y a Xh"
function fmtLastSeen(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    const now = Date.now();
    const diff = Math.max(0, now - d.getTime());
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "à l'instant";
    if (mins < 60) return `il y a ${mins} min`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `il y a ${hours}h`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `il y a ${days}j`;
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
  } catch { return null; }
}
// Iter 62dd — Résoud un projet référencé par id (string dans `task.projet`) vers son label.
// Si `projet` n'est pas un id connu, on retombe sur la valeur brute (compat texte libre historique).
function projectLabel(projet, projects) {
  if (!projet) return null;
  const p = (projects || []).find((x) => x.id === projet);
  if (!p) return /^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(projet) ? null : projet; // fallback texte libre (jamais un id brut)
  return p.title || p.name || 'Projet';
}
// Iter 62fv — Retourne le nom du CLIENT d'un projet (prioritaire sur le nom du projet)
// pour le badge "Client à poster" affiché sur les poster_tasks. Si pas de client,
// on retombe sur le nom du projet.
function projectClient(projet, projects) {
  if (!projet) return null;
  const p = (projects || []).find((x) => x.id === projet);
  if (!p) return null;
  return p.client || p.title || p.name || null;
}
function isoLocal(date = new Date()) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}
function fmtDayLong(iso) {
  try {
    const d = new Date(iso + 'T12:00:00');
    return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
  } catch { return iso; }
}

// ─── Root page ───────────────────────────────────────────────────────────────
export default function TasksPage() {
  const { user } = useAuth();
  // Iter 62ff — Au mount, on hydrate la liste d'équipe depuis /api/users
  // (inclut static USERS + dynamic self-signup approuvés comme Amine).
  useEffect(() => { hydrateTeam(); }, []);
  useTeamVersion(); // re-render dès qu'un nouveau user rejoint l'équipe
  // Iter 62dj — Skip entry screen : par défaut on affiche la vue de l'utilisateur connecté.
  // Bouton "Changer" ouvre le picker d'équipe pour switch vers un autre membre.
  const [profile, setProfile] = useState(() => localStorage.getItem('asa_tasks_v2_profile') || user?.id || 'rayan');
  const [showSwitcher, setShowSwitcher] = useState(false);
  const [fromNewTask] = useState(() => new URLSearchParams(window.location.search).has('new'));
  const setProfilePersist = (p) => {
    if (!p) return;
    setProfile(p);
    setShowSwitcher(false);
    try { localStorage.setItem('asa_tasks_v2_profile', p); } catch { /* noop */ }
  };

  return (
    <>
      <TasksShell profile={profile} onChangeProfile={() => setShowSwitcher(true)} currentUser={user} />
      {!fromNewTask && <StuckVideosAlert userId={user?.id} nameOf={(id) => memberById(id)?.name || id} />}
      {showSwitcher && (
        <TeamSwitcher current={profile} onClose={() => setShowSwitcher(false)} onChoose={setProfilePersist} />
      )}
    </>
  );
}

// ─── Team switcher modal (Iter 62dj — remplace l'ancien EntryScreen) ────────
// Iter 62fj — Préférence perso : chaque user masque les comptes qu'il ne veut
// pas voir dans son picker (icône poubelle + confirm). Re-ajouter via la zone
// "+ Rajouter une vision" en bas. Stocké en localStorage par user.
const HIDDEN_MEMBERS_KEY = 'asa_tasks_v2_hidden_members';
export function readHiddenMembers() {
  try {
    const raw = localStorage.getItem(HIDDEN_MEMBERS_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch { return new Set(); }
}
export function writeHiddenMembers(set) {
  try { localStorage.setItem(HIDDEN_MEMBERS_KEY, JSON.stringify(Array.from(set))); } catch { /* noop */ }
}

function TeamSwitcher({ current, onClose, onChoose }) {
  useTeamVersion();
  const [teamStatus, setTeamStatus] = useState({});
  const [search, setSearch] = useState('');
  const [hidden, setHidden] = useState(() => readHiddenMembers());
  const [showAddBack, setShowAddBack] = useState(false);
  useEffect(() => {
    api.get('/asa-tasks/team-status').then((r) => setTeamStatus(r.data || {})).catch(() => setTeamStatus({}));
  }, []);
  const hideMember = (id) => {
    if (!window.confirm("Masquer ce compte de ta vue des tâches ?\n\nTu pourras le réafficher via '+ Rajouter une vision' ci-dessous.")) return;
    const next = new Set(hidden); next.add(id);
    setHidden(next); writeHiddenMembers(next);
  };
  const unhideMember = (id) => {
    const next = new Set(hidden); next.delete(id);
    setHidden(next); writeHiddenMembers(next);
  };
  const list = (arr) => arr.filter((m) => !hidden.has(m.id) && m.name.toLowerCase().includes(search.toLowerCase()));
  const hiddenMembers = ALL_TEAM.filter((m) => hidden.has(m.id));
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 110, background: 'rgba(6,12,26,0.72)', backdropFilter: 'blur(10px)', display: 'grid', placeItems: 'center', padding: 18, overflow: 'auto' }}>
      <div onClick={(e) => e.stopPropagation()} className="scale-in" style={{ width: '100%', maxWidth: 560, background: `linear-gradient(180deg, ${SURFACE} 0%, rgba(14,22,44,0.98) 100%)`, borderRadius: 22, border: `1px solid ${BORDER}`, boxShadow: '0 40px 80px -20px rgba(0,0,0,0.7)', padding: 24, maxHeight: '86vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <h2 style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 18, color: T1, margin: 0, flex: 1 }}>Changer de vue</h2>
          <button type="button" onClick={onClose} style={{ background: 'transparent', border: 'none', color: T3, cursor: 'pointer' }}><X size={16} /></button>
        </div>
        <div style={{ position: 'relative', marginBottom: 14 }}>
          <Search size={13} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: T3 }} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un membre…"
            style={{ width: '100%', padding: '10px 12px 10px 34px', borderRadius: 10, background: SURFACE2, border: `1px solid ${BORDER}`, color: T1, outline: 'none', fontFamily: 'Sora', fontWeight: 500, fontSize: 12.5 }}
          />
        </div>
        <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 11, color: T3, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 8 }}>Admins</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
          {list(TEAM.admins).map((m) => <MemberCard key={m.id} member={m} active={m.id === current} lastSeen={teamStatus[m.id]} onSelect={() => onChoose(m.id)} onHide={() => hideMember(m.id)} />)}
        </div>
        <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 11, color: T3, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 8 }}>Monteurs</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {list(TEAM.monteurs).map((m) => <MemberCard key={m.id} member={m} active={m.id === current} lastSeen={teamStatus[m.id]} onSelect={() => onChoose(m.id)} onHide={() => hideMember(m.id)} />)}
        </div>
        {/* "+ Rajouter une vision" — pour réafficher un compte masqué */}
        {hiddenMembers.length > 0 && (
          <>
            <button
              type="button"
              data-testid="add-vision-btn"
              onClick={() => setShowAddBack((v) => !v)}
              style={{
                marginTop: 16, width: '100%', padding: '11px 14px', borderRadius: 12,
                background: 'rgba(62,123,250,0.10)', border: `1px dashed ${BLUE}70`,
                color: BLUE_LIGHT, fontFamily: 'Sora', fontWeight: 700, fontSize: 12.5,
                cursor: 'pointer', letterSpacing: '-0.005em',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}
            >
              + Rajouter une vision ({hiddenMembers.length} masqué{hiddenMembers.length > 1 ? 's' : ''})
            </button>
            {showAddBack && (
              <div data-testid="hidden-members-list" style={{ marginTop: 10, padding: 10, borderRadius: 12, background: SURFACE2, border: `1px solid ${BORDER}` }}>
                <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 10.5, color: T3, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 8 }}>Comptes masqués</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {hiddenMembers.map((m) => (
                    <button
                      key={m.id}
                      data-testid={`unhide-${m.id}`}
                      onClick={() => unhideMember(m.id)}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '7px 10px 7px 4px', borderRadius: 999,
                        background: SURFACE, border: `1px solid ${BORDER}`,
                        color: T1, fontFamily: 'Sora', fontWeight: 700, fontSize: 11.5, cursor: 'pointer',
                      }}
                    >
                      <span style={{ width: 20, height: 20, borderRadius: 999, background: `linear-gradient(180deg, ${BLUE_LIGHT} 0%, ${BLUE} 100%)`, color: NAVY, fontFamily: 'Sora', fontWeight: 800, fontSize: 10, display: 'grid', placeItems: 'center' }}>{m.initial}</span>
                      <span>{m.name}</span>
                      <span style={{ color: BLUE_LIGHT, marginLeft: 2 }}>+</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Entry screen — DEPRECATED Iter 62dj (remplacé par TeamSwitcher) ────────
// Le code de EntryScreen a été supprimé. On saute désormais l'écran de choix :
// dès la connexion, on affiche directement le kanban du user. Le bouton
// "Changer" ouvre <TeamSwitcher> pour switch vers une autre vue.

function MemberCard({ member, lastSeen, active, onSelect, onHide }) {
  const seenLabel = fmtLastSeen(lastSeen);
  return (
    <div
      style={{
        position: 'relative',
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '13px 14px', borderRadius: 14,
        background: active ? 'rgba(62,123,250,0.16)' : SURFACE2,
        border: `1px solid ${active ? BLUE : BORDER}`,
        cursor: 'pointer', textAlign: 'left',
        transition: 'all 0.15s var(--ease-out)',
      }}
      data-testid={`entry-member-${member.id}`}
      onClick={onSelect}
      onMouseEnter={(e) => { if (!active) { e.currentTarget.style.borderColor = BLUE; e.currentTarget.style.background = 'rgba(62,123,250,0.10)'; } }}
      onMouseLeave={(e) => { if (!active) { e.currentTarget.style.borderColor = BORDER; e.currentTarget.style.background = SURFACE2; } }}
    >
      <span style={{
        width: 36, height: 36, borderRadius: 10, flexShrink: 0,
        background: `linear-gradient(180deg, ${BLUE_LIGHT} 0%, ${BLUE} 100%)`,
        display: 'grid', placeItems: 'center',
        color: NAVY, fontFamily: 'Sora', fontWeight: 800, fontSize: 15,
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.35)',
      }}>{member.initial}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 13.5, color: T1, letterSpacing: '-0.005em' }}>{member.name}</div>
        <div data-testid={`entry-member-${member.id}-lastseen`} style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 10.5, color: seenLabel ? T2 : T3, letterSpacing: '-0.005em', marginTop: 3, textTransform: 'uppercase' }}>
          {seenLabel ? `Vu · ${seenLabel}` : 'Jamais connecté'}
        </div>
      </div>
      {active && <span style={{ color: BLUE_LIGHT, fontWeight: 800, fontSize: 13 }}>✓</span>}
      {onHide && !active && (
        <button
          type="button"
          data-testid={`hide-member-${member.id}`}
          onClick={(e) => { e.stopPropagation(); onHide(); }}
          style={{
            position: 'absolute', top: 6, right: 6,
            width: 24, height: 24, borderRadius: 7,
            background: 'rgba(255,77,77,0.08)', border: '1px solid rgba(255,77,77,0.25)',
            color: '#FF7B7B', cursor: 'pointer',
            display: 'grid', placeItems: 'center',
            opacity: 0.6, transition: 'opacity 0.12s var(--ease-out)',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
          onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.6'; }}
          title="Masquer de ma vue"
        >
          <Trash2 size={11} />
        </button>
      )}
    </div>
  );
}

// ─── Shell (tabs Kanban / Calendrier) ────────────────────────────────────────
const slotRange = (sl) => `${sl.start}–${sl.end === '24:00' ? '00:00' : sl.end}`;

function SlotInfo({ slot }) {
  const [open, setOpen] = useState(false);
  return (
    <span style={{ position: 'relative' }}>
      <span role="button" tabIndex={0} data-testid={`process-slot-info-${slot.id}`}
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        style={{ display: 'inline-grid', placeItems: 'center', width: 17, height: 17, borderRadius: 999, border: `1px solid ${T3}`, color: T2, fontFamily: 'Sora', fontWeight: 800, fontSize: 10, cursor: 'pointer' }}>i</span>
      {open && (
        <div data-testid={`process-slot-info-pop-${slot.id}`} onClick={(e) => e.stopPropagation()} style={{ position: 'absolute', top: 24, left: -10, zIndex: 50, width: 270, padding: 14, borderRadius: 14, background: '#0F1A36', border: '1px solid rgba(91,190,252,0.35)', boxShadow: '0 20px 40px -10px rgba(0,0,0,0.7)', textAlign: 'left' }}>
          <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 12.5, color: T1 }}>{slotRange(slot)} · {slot.label}</div>
          <ul style={{ margin: '8px 0 0', paddingLeft: 16, fontFamily: 'Lexend', fontSize: 11.5, lineHeight: 1.55, color: T2 }}>
            {slot.items.map((it) => <li key={it}>{it}</li>)}
          </ul>
          <div style={{ fontFamily: 'Lexend', fontSize: 10.5, color: T3, marginTop: 8 }}>
            Tâches validables : {slot.types.map((x) => (TASK_TYPES[x] ? TASK_TYPES[x].label : 'Organisation')).join(', ')}
          </div>
        </div>
      )}
    </span>
  );
}

function ProcessSlotsBar({ proc, active, onPick, ownView }) {
  const [open, setOpen] = useState(false);
  const slots = proc?.slots || [];
  const cur = slots.find((sl) => sl.id === active) || null;
  const others = slots.filter((sl) => sl.id !== cur?.id);
  return (
    <div data-testid="process-slots-bar" style={{ margin: '0 0 14px', maxWidth: 460, position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <span style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 10.5, color: T2, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Créneau horaire</span>
        <span data-testid="process-optional-tag" style={{ padding: '1px 7px', borderRadius: 999, border: `1px solid ${BORDER}`, fontFamily: 'Sora', fontWeight: 700, fontSize: 9.5, color: T3 }}>Optionnel</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div role="button" tabIndex={0} data-testid="process-slot-toggle" onClick={() => setOpen((v) => !v)} style={{
          flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 14, cursor: 'pointer',
          background: cur ? 'rgba(91,190,252,0.10)' : SURFACE2, border: `1.5px solid ${cur ? 'rgba(91,190,252,0.45)' : BORDER}`,
        }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div data-testid="process-selected-slot" style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 13.5, color: cur ? T1 : T2, display: 'flex', alignItems: 'center', gap: 7 }}>
              {cur ? <>Créneau {slotRange(cur)}<SlotInfo slot={cur} /></> : 'Aucun créneau · toutes les tâches'}
            </div>
            <div style={{ fontFamily: 'Lexend', fontSize: 11.5, color: T3, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {cur ? cur.label : 'Choisis un créneau pour ne voir que ses tâches'}
            </div>
          </div>
          <ChevronDown size={18} color={T2} style={{ flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.18s ease' }} />
        </div>
        {cur && (
          <button type="button" data-testid="process-slot-clear" title="Retirer le créneau" onClick={() => { setOpen(false); onPick('all'); }} style={{
            width: 34, height: 34, borderRadius: 10, display: 'grid', placeItems: 'center', cursor: 'pointer', flexShrink: 0,
            background: 'rgba(255,107,107,0.12)', border: '1px solid rgba(255,107,107,0.45)', color: '#FF6B6B',
          }}><X size={16} strokeWidth={2.6} /></button>
        )}
      </div>
      {open && (
        <div data-testid="process-slot-menu" style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 6, zIndex: 40, padding: 6, borderRadius: 14, background: '#0F1A36', border: `1px solid ${BORDER}`, boxShadow: '0 20px 40px -12px rgba(0,0,0,0.7)' }}>
          <div style={{ fontFamily: 'Lexend', fontSize: 11, color: T3, padding: '6px 10px 8px', lineHeight: 1.45 }}>
            Optionnel : quand tu choisis un créneau, tu ne vois que les tâches prévues pour ce créneau. Tu peux valider tes tâches à toute heure.
          </div>
          {others.map((sl) => (
            <div key={sl.id} role="button" tabIndex={0} data-testid={`process-slot-${sl.id}`} onClick={() => { setOpen(false); onPick(sl.id); }}
              style={{ padding: 10, borderRadius: 10, cursor: 'pointer', transition: 'background-color 0.14s' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(91,190,252,0.10)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
              <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 13, color: T1, display: 'flex', alignItems: 'center', gap: 7 }}>Créneau {slotRange(sl)}<SlotInfo slot={sl} /></div>
              <div style={{ fontFamily: 'Lexend', fontSize: 11.5, color: T3, marginTop: 2 }}>{sl.label}</div>
            </div>
          ))}
        </div>
      )}
      {!ownView && !cur && <div style={{ fontFamily: 'Lexend', fontSize: 11, color: T3, marginTop: 6 }}>Aucun créneau choisi par ce membre.</div>}
    </div>
  );
}

function TasksShell({ profile, onChangeProfile, currentUser }) {
  // Iter 62dj — plus de "me" magique. isMyView = tu regardes ta propre vue.
  const isMyView = profile === currentUser?.id;
  const filterUser = profile;
  const isAdmin = ADMIN_IDS.has(currentUser?.id); // Iter 62dq — seuls les admins peuvent switcher de vue
  const isMobile = useIsMobile(); // Iter 62ep — cacher le bouton "i" mobile
  const isMonteur = !isAdmin && (currentUser?.role === 'contributor' || currentUser?.is_monteur === true);
  const [tab, setTab] = useState(() => {
    const saved = localStorage.getItem('asa_tasks_v2_tab') || 'kanban';
    // Iter 62ff — l'onglet "horaire" a été fusionné dans "calendar". Si on
    // restaure cette ancienne valeur, bascule proprement vers "calendar".
    return saved === 'horaire' ? 'calendar' : saved;
  });
  useEffect(() => { try { localStorage.setItem('asa_tasks_v2_tab', tab); } catch { /* noop */ } }, [tab]);
  const [day, setDay] = useState(isoLocal());
  // Iter 62ff — Mode d'affichage DANS l'onglet "Calendrier" :
  //   'month' = grille mois/semaine (défaut), 'day' = vue journalière horaire (Apple style).
  // Déclenché par le modal de choix quand l'utilisateur choisit "Vue journalière".
  const [calendarMode, setCalendarMode] = useState('month');
  // Iter 62fg — Modal plein écran de choix de vue (Kanban vs Journalière) après
  // clic sur un jour. Ouvre uniquement si la préférence est 'ask' (sinon direct).
  const [pickerForDay, setPickerForDay] = useState(null);
  const [allTasks, setTasks] = useState([]);
  // Deux canevas : « Mes tâches » (défaut) et « Supervision » (tâches des monteurs supervisés + vérifications).
  const [canvas, setCanvas] = useState('mine');
  const isSupTask = (t) => t.supervised || t.source === 'supervision';
  const hasSupervision = allTasks.some(isSupTask);
  // Supervision rangée par projet : quel monteur, sur quel projet.
  const supProjects = useMemo(() => {
    const m = new Map();
    allTasks.filter(isSupTask).forEach((t) => {
      if (!t.projet) return;
      const p = m.get(t.projet) || { id: t.projet, name: t.project_name || 'Projet', monteurs: new Set(), open: 0 };
      if (t.supervised) (t.responsable || []).forEach((r) => { if (r !== profile && !['rayan', 'hugo'].includes(r)) p.monteurs.add(r); });
      if (t.status !== 'fini') p.open += 1;
      m.set(t.projet, p);
    });
    return [...m.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [allTasks, profile]);
  const [supProject, setSupProject] = useState(null);
  const activeSup = supProjects.some((p) => p.id === supProject) ? supProject : supProjects[0]?.id;
  // Process journée (Axel) : créneaux horaires ; on n'affiche/valide que les tâches du créneau choisi.
  const [proc, setProc] = useState(null);
  const [slotFilter, setSlotFilter] = useState(null);
  const ownView = profile === currentUser?.id;
  useEffect(() => {
    setSlotFilter(null);
    const url = ownView ? '/daily-process/me' : `/daily-process/me?user_id=${encodeURIComponent(profile)}`;
    const load = () => api.get(url).then((r) => setProc(r.data)).catch(() => setProc(null));
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [profile, ownView]);
  const processOn = !!proc?.process_user_ids?.includes(profile);
  const activeSlot = slotFilter ?? proc?.selected_slot?.id ?? 'all';
  const shownSlot = !processOn || activeSlot === 'all' ? null : proc?.slots?.find((x) => x.id === activeSlot);
  const pickSlot = async (id) => {
    setSlotFilter(id);
    if (!ownView) return;
    try {
      await api.post('/daily-process/slot', { slot: id === 'all' ? null : id });
      setProc((p) => ({ ...p, selected_slot: id === 'all' ? null : p.slots.find((x) => x.id === id) }));
    } catch { toast.error('Impossible de choisir ce créneau'); }
  };
  const tasks = useMemo(() => {
    const base = !hasSupervision && canvas !== 'sup' ? allTasks
      : allTasks.filter((t) => (canvas === 'sup' ? isSupTask(t) && (!activeSup || t.projet === activeSup) : !isSupTask(t)));
    if (!shownSlot || canvas === 'sup') return base;
    return base.filter((t) => isPosterTask(t) || shownSlot.types.includes(effectiveType(t)));
  }, [allTasks, canvas, hasSupervision, activeSup, shownSlot]);
  const isPosteur = currentUser?.job_role === 'posteur' || tasks.some((t) => isPosterTask(t) && (Array.isArray(t.responsable) ? t.responsable : [t.responsable]).includes(currentUser?.id));
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // task detail panel
  const [showCreate, setShowCreate] = useState(false);
  const [preselectHeure, setPreselectHeure] = useState(null); // Iter 62fe — préremplir heure_debut depuis vue horaire
  const [showIADoc, setShowIADoc] = useState(false);
  const [showInfo, setShowInfo] = useState(false); // Iter 62dp — modal "Comment ça marche"
  // Iter 62dm — Auto-open task if `?open=<id>` (deep-link depuis notifs)
  const [searchParams, setSearchParams] = useSearchParams();

  const reload = useCallback(async ({ silent = true } = {}) => {
    // Iter 62de (Rayan msg 2026-09-06) — reload silencieux par défaut : plus
    // de flash "Chargement…" sur les drag&drop / validations. `silent=false`
    // uniquement au tout premier mount pour afficher le spinner initial.
    if (!silent) setLoading(true);
    try {
      // Iter 62dz-b — Rayan msg 2026-02: retour au filtre strict par personne.
      // Chaque vue montre UNIQUEMENT les tâches de la personne sélectionnée.
      const q = filterUser ? `?responsable=${filterUser}` : '';
      const [tRes, pRes] = await Promise.all([
        api.get(`/asa-tasks${q}`),
        api.get('/projects').catch(() => ({ data: [] })),
      ]);
      setTasks(tRes.data || []);
      setProjects(pRes.data || []);
    } catch { setTasks([]); setProjects([]); }
    if (!silent) setLoading(false);
  }, [filterUser]);
  useEffect(() => { reload({ silent: false }); }, [reload]);

  // Iter 62dm — Deep-link `?open=<id>` : ouvrir la tâche puis nettoyer l'URL.
  useEffect(() => {
    if (searchParams.get('new')) {
      setShowCreate(true);
      const next = new URLSearchParams(searchParams);
      next.delete('new');
      setSearchParams(next, { replace: true });
      return;
    }
    const openId = searchParams.get('open');
    if (!openId || allTasks.length === 0) return;
    const t = allTasks.find((x) => x.id === openId);
    if (t && (t.supervised || t.source === 'supervision')) setCanvas('sup');
    if (t) {
      setSelected(t);
      const next = new URLSearchParams(searchParams);
      next.delete('open');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, allTasks, setSearchParams]);

  // Iter 62de — Optimistic patch : maj immédiate côté client sur drag&drop
  // et actions du bouton statut, avant la round-trip API. Zéro flicker.
  const optimisticPatch = useCallback((taskId, patch) => {
    setTasks((prev) => prev.map((t) => t.id === taskId ? { ...t, ...patch } : t));
  }, []);

  const profileMember = memberById(profile);

  return (
    <div className="page-fade" style={{ padding: '28px 34px 60px', minHeight: '100vh' }}>
      {/* Header : titre + tabs + badge profil */}
      <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 22, rowGap: isMobile ? 12 : 22, marginBottom: isMobile ? 14 : 22, flexWrap: 'wrap' }}>
        <h1 style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 26, letterSpacing: '-0.02em', color: T1, margin: 0 }}>Tâches</h1>
        {/* Tabs */}
        <div style={{ display: 'inline-flex', gap: 2, padding: 3, borderRadius: 12, background: SURFACE2, border: `1px solid ${BORDER}` }}>
          {[
            { id: 'kanban', label: 'Kanban' },
            { id: 'calendar', label: 'Calendrier' },
          ].map((t) => {
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                data-testid={`tab-${t.id}`}
                onClick={() => { setTab(t.id); if (t.id === 'calendar') setCalendarMode('month'); }}
                style={{
                  padding: isMobile ? '8px 11px' : '8px 20px', borderRadius: 9, border: 'none',
                  background: active ? `linear-gradient(180deg, ${BLUE_LIGHT} 0%, ${BLUE} 100%)` : 'transparent',
                  color: active ? NAVY : T3,
                  fontFamily: 'Sora', fontWeight: 700, fontSize: 12.5,
                  cursor: 'pointer',
                  boxShadow: active ? '0 4px 12px -4px rgba(62,123,250,0.5), inset 0 1px 0 rgba(255,255,255,0.28)' : 'none',
                }}
              >{t.label}</button>
            );
          })}
        </div>
        <div style={{ display: 'inline-flex', gap: 8 }}>
          <TaskHistoryButton memberById={memberById} initialDate={day} />
          <ManagedSopButtons isAdmin={isAdmin} isMonteur={isMonteur} isPosteur={isPosteur} compact={isMobile} iconOnly />
        </div>
        <div style={{ flex: 1 }} />
        {/* Profile badge — cliquable seulement pour les admins (Iter 62dq) */}
        <button
          data-testid="profile-badge"
          onClick={isAdmin ? onChangeProfile : undefined}
          disabled={!isAdmin}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 10,
            padding: isMobile ? 3 : '7px 14px 7px 7px', borderRadius: 999,
            background: SURFACE2, border: `1px solid ${BORDER}`,
            cursor: isAdmin ? 'pointer' : 'default',
          }}
          title={isAdmin ? "Changer de profil" : (profileMember?.name || '')}
        >
          <span style={{
            width: 28, height: 28, borderRadius: 999, flexShrink: 0,
            background: `linear-gradient(180deg, ${BLUE_LIGHT} 0%, ${BLUE} 100%)`,
            display: 'grid', placeItems: 'center',
            color: NAVY, fontFamily: 'Sora', fontWeight: 800, fontSize: 12,
          }}>{profileMember?.initial || '?'}</span>
          {!isMobile && <span style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 12.5, color: T1 }}>{profileMember?.name || profile}</span>}
          {isAdmin && !isMobile && <span style={{ fontFamily: 'Sora', fontWeight: 500, fontSize: 11.5, color: T3 }}>· changer</span>}
        </button>
        {/* Iter 62dp — Info button "Comment ça marche" — masqué sur mobile
            (déplacé dans le sheet Réglages, cf Iter 62ep) */}
        {!isMobile && (
          <button
            data-testid="task-info-btn"
            onClick={() => setShowInfo(true)}
            style={{
              width: 32, height: 32, borderRadius: 999,
              background: SURFACE2, border: `1px solid ${BORDER}`,
              display: 'grid', placeItems: 'center',
              cursor: 'pointer', color: T2,
              fontFamily: 'Sora', fontWeight: 800, fontSize: 14,
              marginLeft: 4,
            }}
            title="Comment fonctionne le nouveau système"
          >i</button>
        )}
      </div>

      {(
        <div data-testid="canvas-switch" style={{ display: 'inline-flex', gap: 4, padding: 4, margin: '0 0 14px', borderRadius: 999, background: SURFACE2, border: `1px solid ${BORDER}` }}>
          {[['mine', 'Mes tâches', '#5BBEFC'], ['sup', `👁 Supervision · ${allTasks.filter((t) => isSupTask(t) && t.status !== 'fini').length}`, '#F59E0B']].map(([k, label, c]) => (
            <button key={k} data-testid={`canvas-${k}`} onClick={() => setCanvas(k)} style={{
              padding: '7px 16px', borderRadius: 999, border: 'none', cursor: 'pointer', fontFamily: 'Sora', fontWeight: 800, fontSize: 12.5,
              background: canvas === k ? c : 'transparent', color: canvas === k ? '#0B1330' : T2, transition: 'background-color 0.15s, color 0.15s',
            }}>{label}</button>
          ))}
        </div>
      )}
      {processOn && canvas !== 'sup' && (
        <ProcessSlotsBar proc={proc} active={activeSlot} onPick={pickSlot} ownView={ownView} />
      )}
      {canvas === 'sup' && !supProjects.length && (
        <div data-testid="sup-empty" style={{ margin: '0 0 14px', padding: '14px 16px', borderRadius: 12, background: 'rgba(245,158,11,0.08)', border: '1px dashed rgba(245,158,11,0.45)', color: T2, fontFamily: 'Lexend', fontSize: 13 }}>
          Aucun projet supervisé pour l'instant. Pour ajouter un superviseur : <b>Projets → ouvre le projet → Modifier le projet → Superviseurs</b>.
        </div>
      )}
      {canvas === 'sup' && supProjects.length > 0 && (
        <div data-testid="sup-projects" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '0 0 14px' }}>
          {supProjects.map((p) => {
            const on = p.id === activeSup;
            return (
              <button key={p.id} data-testid={`sup-project-${p.id}`} onClick={() => setSupProject(p.id)} style={{
                padding: '9px 14px', borderRadius: 12, cursor: 'pointer', textAlign: 'left',
                background: on ? 'rgba(245,158,11,0.14)' : SURFACE2, border: `1.5px solid ${on ? '#F59E0B' : BORDER}`, color: T1,
                transition: 'background-color 0.15s, border-color 0.15s',
              }}>
                <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 13 }}>{p.name}{p.open ? <span style={{ marginLeft: 6, color: '#F59E0B' }}>· {p.open}</span> : null}</div>
                <div style={{ fontFamily: 'Lexend', fontSize: 11, color: T3, marginTop: 2 }}>
                  {p.monteurs.size ? `Monteur${p.monteurs.size > 1 ? 's' : ''} : ${[...p.monteurs].map((r) => memberById(r)?.name || r).join(', ')}` : 'Vérifications'}
                </div>
              </button>
            );
          })}
        </div>
      )}
      {loading ? (
        <div style={{ height: 400, display: 'grid', placeItems: 'center', color: T3, fontFamily: 'Sora' }}>Chargement…</div>
      ) : tab === 'kanban' ? (
        <KanbanDailyView
          tasks={tasks}
          projects={projects}
          day={day}
          setDay={setDay}
          isMyView={isMyView}
          currentUserId={currentUser?.id}
          filterUser={filterUser}
          onOpenTask={setSelected}
          onCreateTask={() => setShowCreate(true)}
          optimisticPatch={optimisticPatch}
          onOpenIADoc={() => setShowIADoc(true)}
          onOpenInfo={() => setShowInfo(true)}
          reload={reload}
          onSwitchToDayView={() => { setTab('calendar'); setCalendarMode('day'); }}
        />
      ) : tab === 'calendar' ? (
        calendarMode === 'day' ? (
          /* Iter 62ff — Vue journalière intégrée DANS l'onglet Calendrier.
             Déclenchée par le picker modal quand on choisit "Vue journalière". */
          <DayTimelineView
            day={day}
            tasks={tasks}
            isMobile={isMobile}
            onSelectDay={(d) => setDay(d)}
            onOpenTask={(t) => setSelected(t)}
            onCreate={({ day: d, heure_debut }) => {
              setDay(d);
              setPreselectHeure(heure_debut || null);
              setShowCreate(true);
            }}
            onBackToMonth={() => setCalendarMode('month')}
            onSwitchToKanban={() => { setTab('kanban'); setCalendarMode('month'); }}
            reload={reload}
            optimisticPatch={optimisticPatch}
          />
        ) : (
          <CalendarMonthView
            tasks={tasks}
            projects={projects}
            onSelectDay={(d) => { setDay(d); setTab('kanban'); }}
            onDayClick={(iso) => {
              setDay(iso);
              const pref = readDayViewPref();
              if (pref === 'kanban') { setTab('kanban'); }
              else if (pref === 'horaire') { setCalendarMode('day'); }
              else { setPickerForDay(iso); }
            }}
            optimisticPatch={optimisticPatch}
            reload={reload}
          />
        )
      ) : null}

      {selected && (
        <TaskDetailPanel
          key={selected.id}
          initialTask={selected}
          projects={projects}
          currentUserId={currentUser?.id}
          isMyView={isMyView}
          onClose={() => setSelected(null)}
          onChanged={reload}
        />
      )}
      {showCreate && (
        <TaskCreateDialog
          isMyView={isMyView}
          fixedResponsable={isMyView ? (currentUser?.id || null) : filterUser}
          defaultDay={day}
          defaultHeureDebut={preselectHeure}
          projects={projects}
          onClose={() => { setShowCreate(false); setPreselectHeure(null); }}
          onSaved={() => { setShowCreate(false); setPreselectHeure(null); reload(); }}
        />
      )}
      {showIADoc && (
        <IADocDialog
          defaultResponsable={isMyView ? (currentUser?.id || 'rayan') : filterUser}
          onClose={() => setShowIADoc(false)}
          onDone={(n) => { setShowIADoc(false); if (n > 0) reload(); }}
        />
      )}
      {showInfo && <InfoGuideModal onClose={() => setShowInfo(false)} />}
      {pickerForDay && (
        <DayViewPickerModal
          iso={pickerForDay}
          onClose={() => setPickerForDay(null)}
          onPick={(view) => {
            setPickerForDay(null);
            if (view === 'kanban') setTab('kanban');
            else if (view === 'horaire') setCalendarMode('day');
          }}
        />
      )}
    </div>
  );
}

// ─── Info Guide Modal (Iter 62dp/dq) ────────────────────────────────────────
function InfoGuideModal({ onClose }) {
  return (
    <div onClick={onClose} data-testid="task-info-modal" style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(6,12,26,0.78)', backdropFilter: 'blur(12px)', display: 'grid', placeItems: 'center', padding: 18, overflow: 'auto' }}>
      <div onClick={(e) => e.stopPropagation()} className="scale-in" style={{ width: '100%', maxWidth: 620, background: `linear-gradient(180deg, ${SURFACE} 0%, rgba(14,22,44,0.98) 100%)`, borderRadius: 22, border: `1px solid ${BORDER}`, boxShadow: '0 40px 80px -20px rgba(0,0,0,0.7)', padding: 28, maxHeight: '88vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 20 }}>
          <span style={{ width: 44, height: 44, borderRadius: 14, background: 'linear-gradient(135deg, #A78BFA, #5BBEFC)', display: 'grid', placeItems: 'center', color: NAVY, fontFamily: 'Sora', fontWeight: 900, fontSize: 22, flexShrink: 0 }}>i</span>
          <div style={{ flex: 1 }}>
            <h2 style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 22, color: T1, margin: 0, letterSpacing: '-0.01em' }}>Comment ça marche</h2>
            <p style={{ fontFamily: 'Sora', fontWeight: 500, fontSize: 13, color: T3, margin: '4px 0 0', letterSpacing: '-0.005em' }}>Ton nouveau système de tâches en 7 points.</p>
          </div>
          <button type="button" onClick={onClose} style={{ background: 'transparent', border: 'none', color: T3, cursor: 'pointer' }}><X size={18} /></button>
        </div>

        <InfoBlock num="1" title="Ta vue par défaut : Kanban & Calendrier">
          Dès la connexion tu tombes sur <b>ton Kanban perso</b> — tes tâches du jour, rien d&apos;autre. En haut, un onglet <b>Calendrier</b> te permet de voir tes tâches sur tout le mois pour t&apos;organiser à l&apos;avance.
        </InfoBlock>

        <InfoBlock num="2" title="Cadence flexible : autant de vidéos que le projet en demande">
          Chaque vidéo apparaît automatiquement dans ton Kanban <b>à sa vraie date d&apos;échéance</b>. Le rythme s&apos;adapte au projet : <b>0, 1, 2, 4, 6 vidéos par jour</b> — aucune limite. Ce que tu vois affiché reflète exactement la cadence du projet.
        </InfoBlock>

        <InfoBlock num="3" title="Ordre par priorité : plus c'est haut, plus c'est important">
          Dans chaque colonne, les tâches sont <b>triées automatiquement</b> par priorité :
          <span style={{ display: 'block', marginTop: 6, padding: '8px 10px', borderRadius: 8, background: 'rgba(62,123,250,0.10)', border: `1px solid ${BORDER}`, fontSize: 12, color: T2 }}>
            🔴 <b>URGENT</b> tout en haut · 🟠 <b>IMPORTANT</b> ensuite · 🟡 <b>NORMAL</b> puis ⚪ <b>FAIBLE</b> en bas.
          </span>
          À priorité égale, l&apos;ordre <b>vertical compte aussi</b> : une tâche placée <b>plus haut est plus prioritaire</b> qu&apos;une tâche située en dessous. Commence toujours par celle du haut.
        </InfoBlock>

        <InfoBlock num="4" title="Cliquer sur PROJET → ouvre la vidéo">
          Sur une carte, la pastille verte <b>PROJET</b> t&apos;emmène <b>directement</b> sur la fiche détaillée de la vidéo — consignes, drive, dépôt, phases. Plus besoin d&apos;aller la chercher à la main.
        </InfoBlock>

        <InfoBlock num="5" title="Chronomètre">
          Le chrono track le temps que tu passes sur chaque tâche. Objectif : identifier les tâches qui te bouffent trop de temps, garder les plus rapides pour toi et rester efficace au max.
        </InfoBlock>

        <InfoBlock num="6" title="Système de validation">
          La validation existe pour que <b>tous les livrables partent à temps</b> et que tout le monde puisse avancer sereinement. Un contributeur envoie « À valider », un admin passe la tâche en « Fini » ou la renvoie en « En correction » avec des retours clairs.
        </InfoBlock>

        <InfoBlock num="7" title="Notifications & rappels activés">
          Nous avons activé les notifications pour que tu ne rates plus jamais une tâche et que tu saches exactement ce qui t&apos;attend dans la journée. Tu reçois un rappel <b>1h avant</b>, <b>30 min avant</b> et <b>10 min avant</b> l&apos;heure prévue.
        </InfoBlock>

        <button type="button" onClick={onClose} data-testid="info-close" style={{
          marginTop: 18, width: '100%',
          padding: '12px 18px', borderRadius: 12,
          background: `linear-gradient(180deg, ${BLUE_LIGHT} 0%, ${BLUE} 100%)`,
          border: 'none', color: NAVY,
          fontFamily: 'Sora', fontWeight: 800, fontSize: 13.5,
          cursor: 'pointer',
          boxShadow: '0 12px 28px -8px rgba(62,123,250,0.6), inset 0 1px 0 rgba(255,255,255,0.4)',
        }}>Compris, on y va</button>
      </div>
    </div>
  );
}

function InfoBlock({ num, title, children }) {
  return (
    <div style={{ display: 'flex', gap: 12, marginBottom: 14, padding: '13px 14px', borderRadius: 12, background: SURFACE2, border: `1px solid ${BORDER}` }}>
      <span style={{ width: 26, height: 26, borderRadius: 999, background: 'rgba(62,123,250,0.18)', border: '1px solid rgba(62,123,250,0.4)', color: BLUE_LIGHT, fontFamily: 'Sora', fontWeight: 800, fontSize: 12, display: 'grid', placeItems: 'center', flexShrink: 0 }}>{num}</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 13, color: T1, marginBottom: 3, letterSpacing: '-0.005em' }}>{title}</div>
        <div style={{ fontFamily: 'Sora', fontWeight: 500, fontSize: 12.5, color: T2, lineHeight: 1.55, letterSpacing: '-0.005em' }}>{children}</div>
      </div>
    </div>
  );
}

// ─── Kanban daily ────────────────────────────────────────────────────────────
// Iter 62ex — Tri par lot de priorité (P1/P2/P3) puis par priorité classique.
// Iter 62fb — Les tâches vidéo sans lot explicite sont traitées comme n°1
// (Rayan msg : "les vidéos du jour à faire sont automatiquement à 1").
// Ordre auto du jour : création (1, 2, 3…), repart à 1 chaque jour ; un n° choisi à la main ne vaut que pour ce jour.
const dayBucket = (t, day) => (t.priority_bucket && t.priority_bucket_day === day ? t.priority_bucket : null);
const orderForDay = (list, day) => {
  const manual = list.filter((t) => dayBucket(t, day)).sort((a, b) => dayBucket(a, day) - dayBucket(b, day));
  const out = list.filter((t) => !dayBucket(t, day)).sort((a, b) => {
    // Posts : toujours dans l'ordre de leur date prévue (= ordre du projet), puis création.
    const da = isPosterTask(a) ? (a.scheduled_post_date || a.date_debut || '').slice(0, 10) : day;
    const db = isPosterTask(b) ? (b.scheduled_post_date || b.date_debut || '').slice(0, 10) : day;
    return da.localeCompare(db) || (a.created_at || '').localeCompare(b.created_at || '');
  });
  manual.forEach((t) => out.splice(Math.min(dayBucket(t, day) - 1, out.length), 0, t));
  return out.map((t, i) => ({ ...t, _num: i + 1 }));
};
export const TASK_TYPES = {
  montage: { label: 'Montage vidéo', short: 'Montage', color: '#5BBEFC' },
  correction: { label: 'Correction', short: 'Correction', color: '#FF8A8A' },
  developpement: { label: 'Développement', short: 'Dév', color: '#A78BFA' },
};
const effectiveType = (t) => t.task_type || ((t.est_video || t._migrated_from_tasks) ? (t.status === 'a_refaire' ? 'correction' : 'montage') : 'organisation');

function KanbanDailyView({ tasks, projects, day, setDay, isMyView, currentUserId, filterUser, onOpenTask, onCreateTask, optimisticPatch, onOpenIADoc, onOpenInfo, reload, onSwitchToDayView }) {
  const isMobile = useIsMobile();
  // Iter 62dx — Filtre par format (chip bar au-dessus du Kanban).
  const [formatFilter, setFormatFilter] = useState(null);
  // Filter to tasks whose range covers `day`
  const dayTasksRaw = useMemo(() => tasks.filter((t) => ((t.date_debut || '') <= day && day <= (t.date_fin || t.date_debut || '')) || (day === todayLocalIso() && (isLatePost(t) || isCarriedVideo(t) || (t.source === 'supervision' && t.status !== 'fini' && t.date_debut) || (!t.date_debut && !t.source_video_id && (t.status !== 'fini' || (t.updated_at || '').slice(0, 10) === day))))), [tasks, day]);
  // Formats disponibles ce jour-là (uniques, triés) — pour peupler la barre de chips.
  const availableFormats = useMemo(() => {
    const set = new Set();
    dayTasksRaw.forEach((t) => { const f = (t.video_meta?.format || '').trim(); if (f) set.add(f); });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [dayTasksRaw]);
  // Appliquer le filtre sélectionné
  const dayTasks = useMemo(() => {
    if (!formatFilter) return dayTasksRaw;
    return dayTasksRaw.filter((t) => (t.video_meta?.format || '').trim() === formatFilter);
  }, [dayTasksRaw, formatFilter]);
  const cols = ['a_faire', 'en_cours', 'a_valider', 'a_refaire', 'fini'];
  const byStatus = useMemo(() => {
    const m = { a_faire: [], en_cours: [], a_valider: [], a_refaire: [], fini: [] };
    dayTasks.forEach((t) => { (m[t.status] || m.a_faire).push(t); });
    // Iter 62dt — Tri par priorité dans chaque colonne (urgent en tête).
    Object.keys(m).forEach((k) => { m[k] = orderForDay(m[k], day); });
    return m;
  }, [dayTasks, day]);
  // Iter 62du — Sur mobile, un seul statut visible à la fois (tabs en haut).
  const [mobileStatus, setMobileStatus] = useState('a_faire');
  // Iter 62eo — Mobile only: sheet regroupant "IA doc" + filtre format
  const [showMobileMore, setShowMobileMore] = useState(false);

  // Iter 62eo — Mobile : long-press 260ms pour drag (n'entre pas en conflit
  // avec le swipe qui résout en < 260ms). Desktop : 6px de mouvement suffit.
  // Iter 62ep — Sensibilité mobile revue à la hausse (Hugo msg) : delay 160ms
  // au lieu de 260 + tolérance 14px (au lieu de 8) pour tolérer le jitter du
  // doigt. Résultat : le drag part beaucoup plus vite, sans faux positifs
  // vs swipe (le swipe résout typiquement en < 130ms).
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 160, tolerance: 14 } }),
  );
  const [dragged, setDragged] = useState(null);

  // Iter 62ep — Collision detection sur mesure pour le drop des pills mobiles.
  // 1) pointerWithin (le pointeur DANS une pill droppable = match parfait)
  // 2) fallback rectIntersection (la carte dragged INTERSECTE la pill, plus
  //    permissif — utile quand le doigt dépasse un peu la pill)
  // 3) fallback closestCenter (dernier recours pour le sortable list)
  const smartCollisionDetection = useCallback((args) => {
    const pw = pointerWithin(args);
    // Pointeur n'importe où dans une AUTRE colonne → on vise la colonne (pas la carte survolée).
    const fromStatus = args.active?.data?.current?.sortable?.containerId;
    const col = pw.find((c) => STATUS[String(c.id)] && String(c.id) !== fromStatus);
    if (col) return [col];
    if (pw.length > 0) return pw;
    const ri = rectIntersection(args);
    if (ri.length > 0) return ri;
    return closestCenter(args);
  }, []);

  const handleDragEnd = async (e) => {
    setDragged(null);
    const { active, over } = e;
    if (!over) return;
    const task = tasks.find((t) => t.id === active.id);
    if (!task) return;

    // Iter 62fc — Drop sur une AUTRE tâche dans la MÊME colonne = réordre
    // automatique. On réassigne les buckets 1..N basé sur la nouvelle position
    // visuelle. Résultat : premier = n°1, deuxième = n°2, etc.
    const overTask = tasks.find((t) => t.id === String(over.id));
    if (overTask && overTask.id !== task.id && overTask.status === task.status) {
      const colTasks = byStatus[task.status] || [];
      const oldIdx = colTasks.findIndex((t) => t.id === task.id);
      const newIdx = colTasks.findIndex((t) => t.id === overTask.id);
      if (oldIdx < 0 || newIdx < 0 || oldIdx === newIdx) return;
      const reordered = arrayMove(colTasks, oldIdx, newIdx);
      // Optimistic : affecter le bucket = idx + 1 pour tout le monde
      reordered.forEach((t, i) => {
        optimisticPatch?.(t.id, { priority_bucket: i + 1, priority_bucket_day: day });
      });
      // PATCH backend en parallèle
      try {
        await Promise.all(
          reordered.map((t, i) =>
            dayBucket(t, day) !== i + 1
              ? api.patch(`/asa-tasks/${t.id}`, { priority_bucket: i + 1, priority_bucket_day: day })
              : null
          ).filter(Boolean)
        );
        reload();
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Erreur reorder');
        reload();
      }
      return;
    }

    const newStatus = overTask ? overTask.status : String(over.id);
    if (!STATUS[newStatus] || newStatus === task.status) return;
    // Rule : own task cannot be validated to 'fini' by non-admin
    if (newStatus === 'fini' && task.responsable === currentUserId && !SELF_VALIDATOR_IDS.has(currentUserId)) {
      toast.error('Un admin doit valider ta tâche');
      return;
    }
    const payload = await deliveryPayload(task, newStatus, currentUserId);
    if (!payload) return;
    // Iter 62de — Optimistic update AVANT l'API call → zéro flicker
    optimisticPatch?.(task.id, { status: payload.status });
    try {
      await api.patch(`/asa-tasks/${task.id}`, payload); if (payload.delivery) toast.success(deliveryToast(payload));
      reload();
    } catch (err) {
      optimisticPatch?.(task.id, { status: task.status });
      toast.error(err?.response?.data?.detail || 'Erreur');
    }
  };

  const goPrev = () => { const d = new Date(day + 'T12:00:00'); d.setDate(d.getDate() - 1); setDay(isoLocal(d)); };
  const goNext = () => { const d = new Date(day + 'T12:00:00'); d.setDate(d.getDate() + 1); setDay(isoLocal(d)); };
  const isToday = day === isoLocal();

  return (
    <div>
      {/* Day nav — Iter 62du: wrap sur mobile pour éviter le débordement */}
      <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 12, marginBottom: isMobile ? 14 : 20, flexWrap: 'wrap' }}>
        <button
          data-testid="day-prev"
          onClick={goPrev}
          style={{ width: 34, height: 34, borderRadius: 10, background: SURFACE2, border: `1px solid ${BORDER}`, display: 'grid', placeItems: 'center', cursor: 'pointer', color: T2, flexShrink: 0 }}
        ><ChevronLeft size={16} /></button>
        <div data-testid="day-label" style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: isMobile ? 13.5 : 15, color: T1, letterSpacing: '-0.01em', textTransform: 'capitalize' }}>
          {isToday ? "Aujourd'hui — " : ''}{fmtDayLong(day)}
        </div>
        <button
          data-testid="day-next"
          onClick={goNext}
          style={{ width: 34, height: 34, borderRadius: 10, background: SURFACE2, border: `1px solid ${BORDER}`, display: 'grid', placeItems: 'center', cursor: 'pointer', color: T2, flexShrink: 0 }}
        ><ChevronRight size={16} /></button>
        {!isToday && (
          <button data-testid="day-today" onClick={() => setDay(isoLocal())} style={{ marginLeft: 4, padding: '7px 14px', borderRadius: 10, background: 'transparent', border: `1px solid ${BORDER}`, color: T2, fontFamily: 'Sora', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>Aujourd&apos;hui</button>
        )}
        {onSwitchToDayView && (
          <button
            data-testid="kanban-to-day-view"
            onClick={onSwitchToDayView}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '7px 12px', borderRadius: 10,
              background: 'rgba(178,148,245,0.14)', border: `1px solid rgba(178,148,245,0.45)`,
              color: '#B294F5', cursor: 'pointer',
              fontFamily: 'Sora', fontWeight: 700, fontSize: 11.5,
              letterSpacing: '-0.005em',
            }}
            title="Basculer sur la vue journalière horaire"
          >
            <CalendarIcon size={13} /> Journalière
          </button>
        )}
        <div style={{ flex: 1, minWidth: isMobile ? '100%' : 0 }} />
        {/* Iter 62er — Mobile : bouton "Créer une tâche" prend toute la largeur
            avec un CTA géant et évident (Rayan msg "je veux que ça soit plus
            simple à voir sur téléphone"). Desktop reste compact. */}
        <button
          data-testid="task-new"
          onClick={onCreateTask}
          style={{
            position: 'relative',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 10,
            width: isMobile ? 'auto' : 'auto',
            flex: isMobile ? 1 : undefined,
            padding: isMobile ? '14px 18px' : '11px 20px', borderRadius: 14,
            background: `linear-gradient(180deg, ${BLUE_LIGHT} 0%, ${BLUE} 100%)`,
            border: 'none', color: NAVY,
            fontFamily: 'Sora', fontWeight: 800, fontSize: isMobile ? 14 : 12.5,
            cursor: 'pointer', letterSpacing: '-0.005em',
            boxShadow: '0 12px 28px -8px rgba(62,123,250,0.68), 0 2px 4px -1px rgba(62,123,250,0.35), inset 0 1px 0 rgba(255,255,255,0.42), inset 0 -1px 0 rgba(0,0,0,0.10)',
            transition: 'transform 0.14s var(--ease-out), box-shadow 0.14s var(--ease-out)',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 16px 34px -10px rgba(62,123,250,0.78), 0 3px 6px -1px rgba(62,123,250,0.40), inset 0 1px 0 rgba(255,255,255,0.48), inset 0 -1px 0 rgba(0,0,0,0.10)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 12px 28px -8px rgba(62,123,250,0.68), 0 2px 4px -1px rgba(62,123,250,0.35), inset 0 1px 0 rgba(255,255,255,0.42), inset 0 -1px 0 rgba(0,0,0,0.10)'; }}
        >
          <span style={{ display: 'inline-grid', placeItems: 'center', width: isMobile ? 22 : 18, height: isMobile ? 22 : 18, borderRadius: 999, background: 'rgba(11,19,48,0.20)', color: NAVY }}><Plus size={isMobile ? 15 : 12} strokeWidth={3} /></span>
          {isMobile ? 'Créer une tâche' : 'Nouvelle tâche'}
        </button>
        {/* Iter 62eo — Sur mobile, "IA doc" + "Filtre format" sont regroupés
            dans une pill "Réglages" (icône Settings) pour libérer l'espace.
            Sur desktop, garder "IA doc" en évidence. */}
        {isMobile ? (
          <button
            data-testid="task-more-menu"
            onClick={() => setShowMobileMore((v) => !v)}
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 40, height: 40, borderRadius: 12,
              background: SURFACE2, border: `1px solid ${BORDER}`,
              color: T1, cursor: 'pointer',
              transition: 'all 0.15s var(--ease-out)',
            }}
            title="Plus d'options"
            aria-label="Plus d'options"
          >
            <MoreHorizontal size={16} strokeWidth={2.4} />
          </button>
        ) : (
          <button
            data-testid="task-ia-doc"
            onClick={onOpenIADoc}
            style={{
              position: 'relative',
              display: 'inline-flex', alignItems: 'center', gap: 9,
              padding: '11px 20px', borderRadius: 14,
              background: 'linear-gradient(135deg, #A78BFA 0%, #5BBEFC 60%, #B8FFF7 100%)',
              border: 'none', color: NAVY,
              fontFamily: 'Sora', fontWeight: 800, fontSize: 12.5,
              cursor: 'pointer', letterSpacing: '-0.005em',
              boxShadow: '0 12px 28px -8px rgba(167,139,250,0.68), 0 2px 4px -1px rgba(167,139,250,0.35), inset 0 1px 0 rgba(255,255,255,0.48), inset 0 -1px 0 rgba(0,0,0,0.10)',
              transition: 'transform 0.14s var(--ease-out), box-shadow 0.14s var(--ease-out)',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 16px 34px -10px rgba(167,139,250,0.78), 0 3px 6px -1px rgba(167,139,250,0.40), inset 0 1px 0 rgba(255,255,255,0.55), inset 0 -1px 0 rgba(0,0,0,0.10)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 12px 28px -8px rgba(167,139,250,0.68), 0 2px 4px -1px rgba(167,139,250,0.35), inset 0 1px 0 rgba(255,255,255,0.48), inset 0 -1px 0 rgba(0,0,0,0.10)'; }}
            title="Générer des tâches depuis un document via IA"
          >
            <span style={{ display: 'inline-grid', placeItems: 'center', width: 18, height: 18, borderRadius: 999, background: 'rgba(11,19,48,0.20)', color: NAVY }}><Sparkles size={11} strokeWidth={3} /></span>
            IA doc
          </button>
        )}
      </div>

      {/* Iter 62eo — Sheet "Réglages" mobile : IA doc + Filtre format + Aide (62ep) */}
      {isMobile && showMobileMore && (
        <MobileMoreSheet
          onClose={() => setShowMobileMore(false)}
          onIADoc={() => { setShowMobileMore(false); onOpenIADoc && onOpenIADoc(); }}
          onInfo={() => { setShowMobileMore(false); onOpenInfo && onOpenInfo(); }}
          availableFormats={availableFormats}
          formatFilter={formatFilter}
          setFormatFilter={setFormatFilter}
        />
      )}

      {/* Filtre par format (Iter 62dx) — barre horizontale scrollable. Iter 62eo : caché en mobile, déplacé dans la sheet "Réglages". */}
      {!isMobile && availableFormats.length > 0 && (
        <div
          data-testid="format-filter-bar"
          style={{
            display: 'flex', gap: 6, alignItems: 'center',
            overflowX: 'auto', WebkitOverflowScrolling: 'touch',
            marginBottom: 14, paddingBottom: 4,
          }}
        >
          <span style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 10.5, color: T3, letterSpacing: '0.06em', textTransform: 'uppercase', marginRight: 6, flexShrink: 0 }}>Format</span>
          <button
            type="button"
            data-testid="format-filter-all"
            onClick={() => setFormatFilter(null)}
            style={{
              padding: '6px 12px', borderRadius: 999,
              background: !formatFilter ? 'rgba(62,123,250,0.14)' : SURFACE2,
              border: `1px solid ${!formatFilter ? BLUE : BORDER}`,
              color: !formatFilter ? BLUE_LIGHT : T2,
              fontFamily: 'Sora', fontWeight: 700, fontSize: 11.5,
              cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
              letterSpacing: '-0.005em',
            }}
          >Tous <span style={{ fontWeight: 800, opacity: 0.75, marginLeft: 3 }}>{dayTasksRaw.length}</span></button>
          {availableFormats.map((f) => {
            const active = formatFilter === f;
            const count = dayTasksRaw.filter((t) => (t.video_meta?.format || '').trim() === f).length;
            return (
              <button
                key={f}
                type="button"
                data-testid={`format-filter-${f.replace(/\s+/g, '-').toLowerCase()}`}
                onClick={() => setFormatFilter(active ? null : f)}
                style={{
                  padding: '6px 12px', borderRadius: 999,
                  background: active ? 'rgba(167,139,250,0.18)' : SURFACE2,
                  border: `1px solid ${active ? '#B294F5' : BORDER}`,
                  color: active ? '#B294F5' : T2,
                  fontFamily: 'Sora', fontWeight: 700, fontSize: 11.5,
                  cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
                  letterSpacing: '-0.005em', textTransform: 'uppercase',
                }}
              >{f} <span style={{ fontWeight: 800, opacity: 0.75, marginLeft: 3 }}>{count}</span></button>
            );
          })}
        </div>
      )}

      {/* Columns — Iter 62du: mobile = tabs + colonne unique, desktop = grille 5 colonnes */}
      <DndContext sensors={sensors} collisionDetection={smartCollisionDetection} onDragStart={(e) => setDragged(tasks.find((t) => t.id === e.active.id))} onDragEnd={handleDragEnd}>
        {isMobile ? (
          <>
            {/* Iter 62eo — Pills statut = zones DROPPABLES pour drag & drop mobile.
                Chaque pill affiche count, s'illumine au drop, et bascule
                l'onglet actif au tap. Grille 5 colonnes pour tout voir d'un coup. */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
                gap: 5, marginBottom: 12,
              }}
              data-testid="mobile-status-tabs"
            >
              {cols.map((c) => (
                <MobileStatusPill
                  key={c}
                  statusId={c}
                  count={byStatus[c].length}
                  active={mobileStatus === c}
                  isDragging={!!dragged}
                  onTap={() => setMobileStatus(c)}
                />
              ))}
            </div>
            <KanbanColumn
              key={mobileStatus}
              statusId={mobileStatus}
              tasks={byStatus[mobileStatus]}
              projects={projects}
              isMyView={isMyView}
              currentUserId={currentUserId}
              onOpenTask={onOpenTask}
              onCreateTask={onCreateTask}
              optimisticPatch={optimisticPatch}
              reload={reload}
              compact
              suppressDropTarget
            />
          </>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 12, alignItems: 'stretch' }}>
            {cols.map((c) => (
              <KanbanColumn
                key={c}
                statusId={c}
                tasks={byStatus[c]}
                projects={projects}
                isMyView={isMyView}
                currentUserId={currentUserId}
                onOpenTask={onOpenTask}
                onCreateTask={onCreateTask}
                optimisticPatch={optimisticPatch}
                reload={reload}
              />
            ))}
          </div>
        )}
        <DragOverlay>{dragged && <TaskCardGhost task={dragged} />}</DragOverlay>
      </DndContext>
    </div>
  );
}

function KanbanColumn({ statusId, tasks, projects, isMyView, currentUserId, onOpenTask, onCreateTask, optimisticPatch, reload, compact = false, suppressDropTarget = false }) {
  const meta = STATUS[statusId];
  const inner = useDroppable({ id: statusId });
  // Iter 62eo — En mobile, la zone droppable est portée par la pill en haut,
  // pas par la colonne (sinon ça double-drop et le drop hors pill échoue).
  const { setNodeRef, isOver } = suppressDropTarget ? { setNodeRef: undefined, isOver: false } : inner;
  return (
    <div ref={setNodeRef} data-testid={`kanban-col-${statusId}`} style={{
      padding: compact ? '14px 12px 12px' : '18px 14px 14px',
      borderRadius: 18,
      background: SURFACE, border: `1px solid ${isOver ? BLUE : BORDER}`,
      backdropFilter: 'blur(14px)',
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04)',
      minHeight: compact ? 200 : 320,
      transition: 'border-color 0.15s var(--ease-out)',
    }}>
      {!compact && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, paddingLeft: 4 }}>
          <span aria-hidden style={{ width: 8, height: 8, borderRadius: 999, background: meta.color, flexShrink: 0, boxShadow: `0 0 8px ${meta.color}88` }} />
          <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 12.5, color: T1, letterSpacing: '-0.005em' }}>{meta.label}</div>
          <span style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 11, color: T3, marginLeft: 4 }}>{tasks.length}</span>
        </div>
      )}
      <SortableContext id={statusId} items={tasks.map((t) => t.id)} strategy={verticalListSortingStrategy}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minHeight: 100 }}>
          {/* Iter 62dv — Hint "plus c'est haut, plus c'est important" quand ≥ 2 tâches */}
          {tasks.length >= 2 && (
            <div
              data-testid={`col-${statusId}-priority-hint`}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '6px 10px', borderRadius: 8,
                background: 'rgba(62,123,250,0.08)',
                border: '1px dashed rgba(62,123,250,0.24)',
                color: T2,
                fontFamily: 'Sora', fontWeight: 700, fontSize: 10,
                letterSpacing: '0.04em', textTransform: 'uppercase',
              }}
              title="Les tâches sont triées par priorité. Plus une carte est haute, plus elle est prioritaire — commence toujours par celle du haut."
            >
              <span aria-hidden style={{ display: 'inline-flex', color: BLUE_LIGHT }}>↑</span>
              Plus haut = plus prioritaire
            </div>
          )}
          {tasks.length === 0 && (
            <div style={{ padding: '18px 8px', textAlign: 'center', fontFamily: 'Sora', fontSize: 11.5, color: T3, letterSpacing: '-0.005em' }}>Aucune tâche</div>
          )}
          {tasks.map((t) => (
            <TaskCard key={t.id} task={t} projects={projects} isMyView={isMyView} currentUserId={currentUserId} onOpen={() => onOpenTask(t)} optimisticPatch={optimisticPatch} reload={reload} />
          ))}
          <button
            data-testid={`col-${statusId}-add`}
            onClick={onCreateTask}
            style={{
              marginTop: 4, padding: '9px 10px', borderRadius: 10,
              background: 'transparent', border: `1px dashed rgba(62,123,250,0.30)`,
              color: T3, fontFamily: 'Sora', fontWeight: 600, fontSize: 11.5,
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center',
            }}
          ><Plus size={11} strokeWidth={2.4} /> Ajouter</button>
        </div>
      </SortableContext>
    </div>
  );
}

// Iter 62eo — MobileMoreSheet : bottom sheet regroupant les actions secondaires
// (IA doc, filtre par format vidéo) pour libérer de l'espace en haut sur mobile.
function MobileMoreSheet({ onClose, onIADoc, onInfo, availableFormats, formatFilter, setFormatFilter }) {
  const stopPropagation = (e) => e.stopPropagation();
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1200,
        background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        animation: 'fadeIn 0.15s ease-out',
      }}
      data-testid="mobile-more-sheet"
    >
      <div
        onClick={stopPropagation}
        style={{
          width: '100%',
          background: SURFACE,
          borderTopLeftRadius: 22, borderTopRightRadius: 22,
          borderTop: `1px solid ${BORDER}`,
          padding: '14px 18px 24px',
          animation: 'slideUp 0.22s cubic-bezier(0.22, 1, 0.36, 1)',
          boxShadow: '0 -20px 40px -10px rgba(0,0,0,0.55)',
        }}
      >
        <div style={{
          width: 40, height: 4, borderRadius: 999,
          background: 'rgba(255,255,255,0.22)', margin: '0 auto 14px',
        }} />
        <div style={{
          fontFamily: 'Sora', fontWeight: 800, fontSize: 15, color: T1,
          letterSpacing: '-0.01em', marginBottom: 14,
        }}>Réglages</div>

        {/* Action : IA doc */}
        <button
          onClick={onIADoc}
          data-testid="mobile-more-ia-doc"
          style={{
            display: 'flex', alignItems: 'center', gap: 12,
            width: '100%', textAlign: 'left',
            padding: '12px 14px', borderRadius: 12,
            background: 'linear-gradient(135deg, rgba(167,139,250,0.16) 0%, rgba(91,190,252,0.10) 100%)',
            border: '1px solid rgba(167,139,250,0.32)',
            color: T1, fontFamily: 'Sora', fontWeight: 700, fontSize: 13,
            cursor: 'pointer', marginBottom: 10,
          }}
        >
          <span style={{
            width: 32, height: 32, borderRadius: 10, flexShrink: 0,
            background: 'linear-gradient(135deg, #A78BFA 0%, #5BBEFC 100%)',
            display: 'grid', placeItems: 'center', color: NAVY,
            boxShadow: '0 6px 14px -6px rgba(167,139,250,0.55)',
          }}><Sparkles size={14} strokeWidth={2.8} /></span>
          <div>
            <div>Générer via IA doc</div>
            <div style={{ fontSize: 11, color: T3, fontWeight: 500, marginTop: 2 }}>Créer des tâches depuis un brief</div>
          </div>
        </button>

        {/* Iter 62ep — Aide "Comment ça marche" (déplacée depuis le bouton "i" du header mobile) */}
        {onInfo && (
          <button
            onClick={onInfo}
            data-testid="mobile-more-info"
            style={{
              display: 'flex', alignItems: 'center', gap: 12,
              width: '100%', textAlign: 'left',
              padding: '12px 14px', borderRadius: 12,
              background: SURFACE2, border: `1px solid ${BORDER}`,
              color: T1, fontFamily: 'Sora', fontWeight: 700, fontSize: 13,
              cursor: 'pointer', marginBottom: 10,
            }}
          >
            <span style={{
              width: 32, height: 32, borderRadius: 10, flexShrink: 0,
              background: SURFACE, border: `1px solid ${BORDER}`,
              display: 'grid', placeItems: 'center', color: T2,
              fontFamily: 'Sora', fontWeight: 800, fontSize: 15,
            }}>i</span>
            <div>
              <div>Comment ça marche</div>
              <div style={{ fontSize: 11, color: T3, fontWeight: 500, marginTop: 2 }}>Guide du système de tâches</div>
            </div>
          </button>
        )}

        {/* Section : Filtre par format vidéo */}
        {availableFormats.length > 0 && (
          <>
            <div style={{
              fontFamily: 'Sora', fontWeight: 700, fontSize: 11, color: T3,
              letterSpacing: '0.06em', textTransform: 'uppercase',
              margin: '14px 0 8px',
            }}>Filtrer par format</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              <button
                onClick={() => setFormatFilter(null)}
                data-testid="mobile-more-format-all"
                style={{
                  padding: '7px 12px', borderRadius: 999,
                  background: !formatFilter ? 'rgba(62,123,250,0.16)' : SURFACE2,
                  border: `1px solid ${!formatFilter ? BLUE : BORDER}`,
                  color: !formatFilter ? BLUE_LIGHT : T2,
                  fontFamily: 'Sora', fontWeight: 700, fontSize: 11.5,
                  cursor: 'pointer',
                }}
              >Tous</button>
              {availableFormats.map((f) => (
                <button
                  key={f}
                  onClick={() => setFormatFilter(f)}
                  data-testid={`mobile-more-format-${f}`}
                  style={{
                    padding: '7px 12px', borderRadius: 999,
                    background: formatFilter === f ? 'rgba(167,139,250,0.16)' : SURFACE2,
                    border: `1px solid ${formatFilter === f ? '#A78BFA' : BORDER}`,
                    color: formatFilter === f ? '#B294F5' : T2,
                    fontFamily: 'Sora', fontWeight: 700, fontSize: 11.5,
                    cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.03em',
                  }}
                >{f}</button>
              ))}
            </div>
          </>
        )}

        <button
          onClick={onClose}
          style={{
            width: '100%', marginTop: 18, padding: '11px 14px',
            borderRadius: 12, background: SURFACE2, border: `1px solid ${BORDER}`,
            color: T2, fontFamily: 'Sora', fontWeight: 700, fontSize: 12.5,
            cursor: 'pointer',
          }}
        >Fermer</button>
      </div>
      <style>{`
        @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
      `}</style>
    </div>
  );
}

// Iter 62en — Ordre des statuts pour le swipe horizontal mobile.
// On saute 'a_refaire' dans la progression naturelle : c'est un état
// "problème" atteignable via long-press → detail, pas via swipe rapide.
const SWIPE_ORDER = ['a_faire', 'en_cours', 'a_valider', 'fini'];

// Iter 62eo — MobileStatusPill : pill compacte + zone droppable pour le drag&drop.
// Grid 5 colonnes → tous les statuts visibles d'un coup, sans scroll horizontal.
function MobileStatusPill({ statusId, count, active, isDragging, onTap }) {
  const meta = STATUS[statusId];
  const { setNodeRef, isOver } = useDroppable({ id: statusId });
  const canDrop = isDragging;
  return (
    <button
      ref={setNodeRef}
      type="button"
      data-testid={`mobile-tab-${statusId}`}
      onClick={onTap}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        gap: 4, padding: '12px 4px', borderRadius: 12,
        background: isOver
          ? `${meta.color}55`
          : active ? `${meta.color}22` : SURFACE2,
        border: `1.5px solid ${isOver ? meta.color : active ? meta.color : BORDER}`,
        color: active ? meta.color : T2,
        fontFamily: 'Sora', fontWeight: 700, fontSize: 10.5,
        letterSpacing: '-0.005em',
        cursor: 'pointer',
        transition: 'all 0.15s cubic-bezier(0.22, 1, 0.36, 1)',
        transform: (isOver && canDrop) ? 'scale(1.08)' : 'scale(1)',
        boxShadow: isOver ? `0 0 20px ${meta.color}88, inset 0 0 0 1px ${meta.color}` : 'none',
        // Iter 62ep — Zones plus grandes pour faciliter le drop mobile (Hugo msg
        // "drag & drop pas assez sensible"). 56px vs 46 précédemment.
        minHeight: 56,
        overflow: 'hidden',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <span style={{
          width: 5, height: 5, borderRadius: 999,
          background: meta.color,
          boxShadow: active ? `0 0 6px ${meta.color}` : 'none',
          flexShrink: 0,
        }} />
        <span style={{
          fontWeight: 800, fontSize: 12, color: active || isOver ? meta.color : T1,
        }}>{count}</span>
      </div>
      <span style={{
        fontSize: 9.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        maxWidth: '100%', lineHeight: 1.1,
      }}>{meta.label}</span>
    </button>
  );
}

// « En cours depuis le … » : date réelle de passage en cours (sinon chrono, sinon date prévue).
function EnCoursSince({ task }) {
  const since = task.en_cours_since || task.chrono_demarre_a || (task.date_debut ? `${task.date_debut.slice(0, 10)}T12:00:00` : null);
  if (!since) return null;
  const d = new Date(since);
  const days = Math.max(0, Math.floor((new Date(todayLocalIso() + 'T12:00:00') - new Date(d.toDateString() + ' 12:00')) / 86400000));
  const late = days >= 3;
  return (
    <div data-testid={`en-cours-since-${task.id}`} style={{ marginTop: 4, display: 'inline-flex', alignItems: 'center', gap: 4, padding: '1px 7px', borderRadius: 999,
      background: late ? 'rgba(255,107,107,0.14)' : 'rgba(91,190,252,0.12)', border: `1px solid ${late ? 'rgba(255,107,107,0.45)' : 'rgba(91,190,252,0.35)'}`,
      color: late ? '#FF8A8A' : '#5BBEFC', fontFamily: 'Sora', fontWeight: 700, fontSize: 9.5 }}>
      ⏳ En cours depuis {days === 0 ? "aujourd'hui" : `le ${d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} · ${days} j`}
    </div>
  );
}

function TaskCard({ task, projects, isMyView, currentUserId, onOpen, optimisticPatch, reload }) {
  const navigate = useNavigate();
  // Iter 62ez — Rayan msg : "faut que ça remonte direct, pas lent" → réduire
  // la durée du reorder dnd-kit de 250ms (default) à 130ms + easing snappy.
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: task.id,
    transition: { duration: 130, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' },
  });
  const prio = PRIORITY[task.priority] || PRIORITY.normal;
  const posted = isPosted(task);
  const resps = respsOf(task);
  const members = resps.map(memberById).filter(Boolean);
  const projLabel = projectLabel(task.projet, projects);
  const projectExists = task.projet && (projects || []).some((p) => p.id === task.projet);
  const openProject = (e) => {
    e.stopPropagation();
    if (!projectExists) return;
    try {
      localStorage.setItem('asa_open_project', task.projet);
      // Iter 62dm — Deep-link direct sur la vidéo/tâche dans le projet
      localStorage.setItem('asa_open_task_row', task.id);
    } catch { /* noop */ }
    navigate('/projects');
  };

  // ─── Iter 62en — Swipe horizontal (mobile) pour changer de statut ─────────
  const isMobile = useIsMobile();
  const [dx, setDx] = useState(0);
  const [snapping, setSnapping] = useState(false);
  const startX = useRef(0);
  const startY = useRef(0);
  const lockedAxis = useRef(null);
  const swipedRef = useRef(false); // true si le dernier tap était un swipe → bloquer onOpen
  const currentIdx = SWIPE_ORDER.indexOf(task.status);
  const canAdvance = isMobile && currentIdx >= 0 && currentIdx < SWIPE_ORDER.length - 1;
  const canRegress = isMobile && currentIdx > 0;
  const nextStatus = canAdvance ? SWIPE_ORDER[currentIdx + 1] : null;
  const prevStatus = canRegress ? SWIPE_ORDER[currentIdx - 1] : null;
  const THRESHOLD = 90;

  const applyStatus = async (newStatus) => {
    // Rule : own task cannot be validated to 'fini' by non-admin
    if (newStatus === 'fini' && task.responsable === currentUserId && !SELF_VALIDATOR_IDS.has(currentUserId)) {
      toast.error('Un admin doit valider ta tâche');
      return;
    }
    const prev = task.status;
    const payload = await deliveryPayload(task, newStatus, currentUserId);
    if (!payload) return;
    optimisticPatch?.(task.id, { status: payload.status });
    try {
      await api.patch(`/asa-tasks/${task.id}`, payload); if (payload.delivery) toast.success(deliveryToast(payload));
      try { navigator.vibrate && navigator.vibrate(12); } catch {}
      reload && reload();
    } catch (err) {
      optimisticPatch?.(task.id, { status: prev });
      toast.error(err?.response?.data?.detail || 'Erreur');
    }
  };

  const onTouchStart = (e) => {
    if (!isMobile) return;
    const t = e.touches[0];
    startX.current = t.clientX;
    startY.current = t.clientY;
    lockedAxis.current = null;
    swipedRef.current = false;
    setSnapping(false);
  };
  const onTouchMove = (e) => {
    if (!isMobile) return;
    const t = e.touches[0];
    const rawDx = t.clientX - startX.current;
    const rawDy = t.clientY - startY.current;
    if (!lockedAxis.current) {
      if (Math.abs(rawDx) < 8 && Math.abs(rawDy) < 8) return;
      lockedAxis.current = Math.abs(rawDx) > Math.abs(rawDy) ? 'x' : 'y';
    }
    if (lockedAxis.current !== 'x') return;
    if (e.cancelable) e.preventDefault();
    let d = rawDx;
    if (d > 0 && !canAdvance) d = d / 4;
    if (d < 0 && !canRegress) d = d / 4;
    d = Math.max(-140, Math.min(140, d));
    setDx(d);
    if (Math.abs(d) > 12) swipedRef.current = true;
  };
  const onTouchEnd = () => {
    if (!isMobile) return;
    setSnapping(true);
    if (dx > THRESHOLD && canAdvance) applyStatus(nextStatus);
    else if (dx < -THRESHOLD && canRegress) applyStatus(prevStatus);
    setDx(0);
    lockedAxis.current = null;
    // Reset swipedRef un tick plus tard pour que onClick puisse le voir
    setTimeout(() => { swipedRef.current = false; }, 200);
  };

  const rightReveal = isMobile && dx > 0 && canAdvance;
  const leftReveal  = isMobile && dx < 0 && canRegress;
  const reachedThreshold = Math.abs(dx) >= THRESHOLD;
  const revealMeta = rightReveal
    ? STATUS[nextStatus]
    : leftReveal ? STATUS[prevStatus] : null;

  return (
    <motion.div
      layout={!isDragging}
      transition={{ type: 'spring', stiffness: 500, damping: 32, mass: 0.6 }}
      style={{ position: 'relative', overflow: isMobile ? 'hidden' : 'visible', borderRadius: 12 }}
    >
      {isMobile && revealMeta && (
        <div
          style={{
            position: 'absolute', top: 0, bottom: 0,
            [rightReveal ? 'left' : 'right']: 10,
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '0 14px',
            pointerEvents: 'none',
            fontFamily: 'Sora', fontSize: 11.5, fontWeight: 800, letterSpacing: '-0.005em',
            color: revealMeta.color,
            opacity: reachedThreshold ? 1 : 0.55,
            transition: 'opacity 0.12s ease',
            textShadow: reachedThreshold ? `0 0 12px ${revealMeta.color}88` : 'none',
          }}
        >
          {rightReveal ? '→' : ''} {revealMeta.label} {leftReveal ? '←' : ''}
        </div>
      )}
      <div
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
        style={{
          transform: isMobile ? `translateX(${dx}px)` : 'none',
          transition: snapping ? 'transform 0.25s cubic-bezier(0.22, 1, 0.36, 1)' : 'none',
          willChange: isMobile ? 'transform' : 'auto',
        }}
      >
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.3 : (posted ? 0.48 : 1),
        filter: posted ? 'grayscale(0.85)' : 'none',
        padding: '12px 12px 10px 14px', borderRadius: 12,
        // Iter 62ds — Fond dégradé sémantique selon la priorité (rouge/orange/jaune/gris).
        background: task.supervised ? 'linear-gradient(135deg, rgba(245,158,11,0.10) 0%, rgba(245,158,11,0.02) 100%)' : prio.grad,
        border: `1px ${task.supervised ? 'dashed rgba(245,158,11,0.45)' : `solid ${BORDER}`}`,
        borderLeft: `3px solid ${task.supervised ? '#F59E0B' : task.delivery ? (task.delivery === 'drive' ? '#34D399' : '#A78BFA') : prio.accent}`,
        boxShadow: prio.glow,
        cursor: isMobile ? 'pointer' : 'grab',
      }}
      data-testid={`task-card-${task.id}`}
    >
      {/* Iter 62fv — Badge "CLIENT À POSTER : NOM" bien visible pour les poster_tasks.
          Iter 62fw — Si un lien de visionnage existe (preview_url / frameio_link / drive_link),
          le badge affiche aussi un bouton "🔗 Voir la vidéo" qui ouvre le lien directement
          dans un nouvel onglet. Clic sur le badge lui-même = ouvre aussi le lien si dispo. */}
      {isPosterTask(task) && (() => {
        // Iter 62fx — Priorité à l'étiquette libre `client_label`, puis fallback
        // sur le client du projet lié (compat avec les anciennes poster_tasks).
        const client = task.client_label || task.project_client || projectClient(task.projet, projects) || projectLabel(task.projet, projects) || 'Client';
        const link = task.preview_url || task.frameio_link || task.drive_link || null;
        const openLink = (e) => {
          e.stopPropagation();
          e.preventDefault();
          if (link) window.open(link, '_blank', 'noopener,noreferrer');
        };
        return (
          <div
            role={link ? 'button' : undefined}
            onClick={link ? openLink : undefined}
            data-testid={`poster-client-badge-${task.id}`}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              margin: '-4px -4px 10px -6px', padding: '7px 10px',
              borderRadius: 9,
              background: 'linear-gradient(135deg, rgba(245,197,106,0.22) 0%, rgba(217,154,66,0.14) 100%)',
              border: '1px solid rgba(245,197,106,0.4)',
              boxShadow: 'inset 0 0 0 1px rgba(245,197,106,0.08), 0 4px 14px -6px rgba(245,197,106,0.35)',
              cursor: link ? 'pointer' : 'default',
              transition: 'transform 0.12s var(--ease-out), box-shadow 0.12s var(--ease-out)',
            }}
            onMouseEnter={(e) => { if (link) e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            <span style={{ fontSize: 14, lineHeight: 1 }}>📱</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div data-testid={`poster-status-label-${task.id}`} style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 9, color: '#D99A42', letterSpacing: '0.08em', textTransform: 'uppercase', opacity: 0.9 }}>
                {posted ? '✓ Posté · en ligne' : 'Client à poster'}
                {isLatePost(task) && <span data-testid="late-post-badge" style={{ marginLeft: 6, padding: '1px 7px', borderRadius: 999, background: 'rgba(248,113,113,0.18)', border: '1px solid rgba(248,113,113,0.6)', color: '#F87171', fontWeight: 800 }}>En retard · prévu le {new Date(`${task.date_debut}T12:00:00`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}</span>}
              </div>
              <div style={{ fontFamily: 'Sora', fontWeight: 900, fontSize: 15, color: '#F5C56A', letterSpacing: '-0.01em', lineHeight: 1.1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {client}
              </div>
            </div>
            {link && (
              <button
                type="button"
                data-testid={`poster-open-link-${task.id}`}
                onClick={openLink}
                onMouseDown={(e) => e.stopPropagation()}
                title="Ouvrir le lien de visionnage"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  padding: '6px 10px', borderRadius: 7,
                  background: 'linear-gradient(180deg, #F5C56A 0%, #D99A42 100%)',
                  border: 'none', color: '#0B1330',
                  fontFamily: 'Sora', fontWeight: 800, fontSize: 10.5,
                  cursor: 'pointer', flexShrink: 0,
                  boxShadow: '0 4px 10px -3px rgba(245,197,106,0.5)',
                }}
              >
                <ExternalLink size={11} strokeWidth={2.6} /> Voir
              </button>
            )}
          </div>
        );
      })()}
      <button
        onClick={(e) => {
          e.stopPropagation();
          if (swipedRef.current) return; // Ignore le tap si c'était un swipe
          onOpen();
        }}
        onMouseDown={(e) => e.stopPropagation()}
        {...attributes}
        {...listeners}
        style={{ width: '100%', textAlign: 'left', background: 'transparent', border: 'none', padding: 0, cursor: isMobile ? 'pointer' : 'grab' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap', minWidth: 0 }}>
          {/* Iter 62ex — Lot de priorité (P1..P7) : chip compacte, gradient
              distinct par lot. Le user choisit son lot via le detail panel.
              Iter 62ey — Quand un lot est actif, on CACHE le chip
              NORMAL/URGENT (redondant, prend de la place). */}
          {task.status !== 'fini' && (task._num || task.priority_bucket || task.est_video) && (
            (() => {
              const b = task._num || task.priority_bucket || (task.est_video ? 1 : null);
              if (!b) return null;
              const BUCKET_STYLES = {
                1: { bg: 'linear-gradient(135deg, #FF4D4D 0%, #FF7A3B 100%)', color: '#FFF', shadow: '0 4px 12px -3px rgba(255,77,77,0.55), inset 0 1px 0 rgba(255,255,255,0.32)' },
                2: { bg: 'linear-gradient(135deg, #F5A623 0%, #F5C46A 100%)', color: '#0B1330', shadow: '0 4px 12px -3px rgba(245,166,35,0.5), inset 0 1px 0 rgba(255,255,255,0.35)' },
                3: { bg: 'linear-gradient(135deg, #5BBEFC 0%, #A78BFA 100%)', color: '#0B1330', shadow: '0 4px 12px -3px rgba(91,190,252,0.5), inset 0 1px 0 rgba(255,255,255,0.35)' },
                4: { bg: 'linear-gradient(135deg, #22D3B6 0%, #7FDDB8 100%)', color: '#0B1330', shadow: '0 4px 12px -3px rgba(34,211,182,0.5), inset 0 1px 0 rgba(255,255,255,0.35)' },
                5: { bg: 'linear-gradient(135deg, #F472B6 0%, #FB7185 100%)', color: '#FFF', shadow: '0 4px 12px -3px rgba(244,114,182,0.5), inset 0 1px 0 rgba(255,255,255,0.32)' },
                6: { bg: 'linear-gradient(135deg, #06B6D4 0%, #67E8F9 100%)', color: '#0B1330', shadow: '0 4px 12px -3px rgba(6,182,212,0.5), inset 0 1px 0 rgba(255,255,255,0.35)' },
                7: { bg: 'linear-gradient(135deg, #64748B 0%, #94A3B8 100%)', color: '#FFF', shadow: '0 4px 12px -3px rgba(100,116,139,0.5), inset 0 1px 0 rgba(255,255,255,0.28)' },
              };
              const cfg = BUCKET_STYLES[b] || BUCKET_STYLES[7];
              return (
                <motion.span
                  key={b}
                  layout
                  initial={{ scale: 0.7, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.7, opacity: 0 }}
                  transition={{ type: 'spring', stiffness: 480, damping: 22 }}
                  data-testid={`prio-bucket-${task.id}`}
                  style={{
                    display: 'inline-grid', placeItems: 'center', flexShrink: 0,
                    padding: '3px 9px', borderRadius: 8,
                    background: cfg.bg, color: cfg.color,
                    fontFamily: 'Sora', fontWeight: 900, fontSize: 12,
                    letterSpacing: '-0.005em', lineHeight: 1,
                    boxShadow: cfg.shadow,
                    minWidth: 26,
                  }}
                  title={`Tâche n°${b}`}
                >{b}</motion.span>
              );
            })()
          )}
          {/* Chip NORMAL/URGENT masqué quand un lot est actif (redondance visuelle) */}
          {task.validated_slot && ['a_valider', 'fini'].includes(task.status) && (
            <span data-testid={`task-validated-slot-${task.id}`} title={`Passée en ${task.validated_slot.status === 'fini' ? 'Fini' : 'À valider'} à ${task.validated_slot.at}${task.validated_slot.start ? ` (créneau ${slotRange(task.validated_slot)})` : ''}`} style={{ padding: '2px 8px', borderRadius: 999, background: 'rgba(34,211,182,0.12)', border: '1px solid rgba(34,211,182,0.45)', color: '#22D3B6', fontFamily: 'Sora', fontWeight: 800, fontSize: 9.5, flexShrink: 0 }}>
              {task.validated_slot.status === 'fini' ? 'Fini' : 'À valider'}{task.validated_slot.date && task.validated_slot.date !== isoLocal() ? ` le ${task.validated_slot.date.slice(8, 10)}/${task.validated_slot.date.slice(5, 7)}` : ''} à {task.validated_slot.at}{task.validated_slot.start ? ` · créneau ${slotRange(task.validated_slot)}` : ''}
            </span>
          )}
          {TASK_TYPES[effectiveType(task)] && !isPosterTask(task) && (
            <span data-testid={`task-type-chip-${task.id}`} style={{ padding: '2px 8px', borderRadius: 999, background: `${TASK_TYPES[effectiveType(task)].color}1F`, border: `1px solid ${TASK_TYPES[effectiveType(task)].color}66`, color: TASK_TYPES[effectiveType(task)].color, fontFamily: 'Sora', fontWeight: 800, fontSize: 9.5, letterSpacing: '0.04em', flexShrink: 0 }}>{TASK_TYPES[effectiveType(task)].short}</span>
          )}
          {!task._num && !task.priority_bucket && !task.est_video && (
            <span
              aria-hidden
              data-testid={`prio-chip-${task.id}`}
              style={{
                padding: '2px 8px', borderRadius: 999,
                background: `${prio.accent}26`,
                border: `1px solid ${prio.accent}66`,
                color: prio.accent,
                fontFamily: 'Sora', fontWeight: 800, fontSize: 9.5,
                letterSpacing: '0.06em', textTransform: 'uppercase',
                flexShrink: 0,
              }}
            >{prio.label}</span>
          )}
          {/* Iter 62fd — Badge "PROCHAINE" (une seule tâche à la fois) */}
          {task.is_next && task.status !== 'fini' && (
            <motion.span
              layout
              initial={{ scale: 0.7, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', stiffness: 480, damping: 22 }}
              data-testid={`next-chip-${task.id}`}
              style={{
                padding: '2px 9px', borderRadius: 999,
                background: 'linear-gradient(135deg, rgba(91,190,252,0.22) 0%, rgba(167,139,250,0.22) 100%)',
                border: '1px solid rgba(167,139,250,0.55)',
                color: '#B294F5',
                fontFamily: 'Sora', fontWeight: 800, fontSize: 9.5,
                letterSpacing: '0.06em', textTransform: 'uppercase',
                flexShrink: 0, whiteSpace: 'nowrap',
                boxShadow: '0 0 12px -3px rgba(167,139,250,0.55)',
              }}
              title="Ta prochaine tâche à faire"
            >🔜 Prochaine</motion.span>
          )}
          <div style={{ flex: 1, fontFamily: 'Sora', fontWeight: 700, fontSize: 13, color: T1, letterSpacing: '-0.005em', lineHeight: 1.35 }}>
            {task.title}
            {task.status === 'en_cours' && <EnCoursSince task={task} />}
            {(task.delivery || task.source === 'supervision' || task.supervised) && (
              <div style={{ marginTop: 5, display: 'flex', gap: 5, flexWrap: 'wrap', alignItems: 'center' }}>
                {task.supervised && <span data-testid={`supervised-badge-${task.id}`} style={{ padding: '1px 7px', borderRadius: 999, background: 'rgba(245,158,11,0.16)', border: '1px solid rgba(245,158,11,0.5)', color: '#F59E0B', fontFamily: 'Sora', fontWeight: 800, fontSize: 9.5 }}>👁 Supervision · {(task.responsable || []).map((r) => memberById(r)?.name || r).join(', ')}</span>}
                <DeliveryBadge delivery={task.delivery} id={task.id} />
                {task.source === 'supervision' && task.status !== 'fini' && task.supervise_video_id && <PostDayButton videoId={task.supervise_video_id} delivery={task.delivery} compact onDone={reload} />}
              </div>
            )}
            {(task.source === 'gcal' || task.source === 'gtasks' || (!task.date_debut && !task.source_video_id)) && (
              <div style={{ marginTop: 5, display: 'flex', gap: 5, flexWrap: 'wrap', alignItems: 'center' }}>
                <JoinCallButton task={task} compact />
                {task.source === 'gcal' && task.heure_debut && <span data-testid={`call-slot-${task.id}`} style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 10, color: '#8AB4F8', fontWeight: 700 }}>{task.heure_debut}{task.heure_fin ? `–${task.heure_fin}` : ''}</span>}
                {!task.date_debut && <span data-testid={`no-date-badge-${task.id}`} style={{ padding: '1px 6px', borderRadius: 999, background: 'rgba(245,197,106,0.14)', border: '1px solid rgba(245,197,106,0.45)', color: '#F5C56A', fontFamily: 'Sora', fontWeight: 800, fontSize: 9 }}>SANS DATE</span>}
              </div>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: task.status !== 'fini' ? 10 : 0 }}>
          {members.length > 0 && (
            <span style={{ fontFamily: 'Sora', fontWeight: 600, fontSize: 10.5, color: T3 }}>{members.map((m) => m.name).join(' · ')}</span>
          )}
          {/* Iter 62dw — Format vidéo custom (ex: "décor jaune", "podcast intro") */}
          {task.est_video && task.video_meta?.format && (
            <span
              data-testid={`card-format-${task.id}`}
              style={{ padding: '2px 8px', borderRadius: 999, background: 'rgba(167,139,250,0.14)', border: '1px solid rgba(167,139,250,0.32)', fontFamily: 'Sora', fontWeight: 700, fontSize: 9.5, color: '#B294F5', letterSpacing: '0.04em', textTransform: 'uppercase' }}
              title="Format personnalisé"
            >{task.video_meta.format}</span>
          )}
          {/* Iter 62dh — Si projet, on n'affiche que le badge "Projet". Pas de doublon "Vidéo · Projet". */}
          {task.est_video && !projLabel && !task.video_meta?.format && (
            <span style={{ padding: '2px 7px', borderRadius: 999, background: 'rgba(62,123,250,0.14)', border: '1px solid rgba(62,123,250,0.3)', fontFamily: 'Sora', fontWeight: 700, fontSize: 9.5, color: BLUE_LIGHT, letterSpacing: '0.04em', textTransform: 'uppercase' }}>Vidéo</span>
          )}
          {/* Iter 62dp — Badge "Projet" TOUJOURS cliquable. Si le projet est
              introuvable, on ouvre le detail de la tâche dans le Kanban. */}
          {projLabel && (
            <span
              role="button"
              tabIndex={0}
              data-testid={`card-open-project-${task.id}`}
              onClick={(e) => {
                e.stopPropagation();
                if (projectExists) {
                  openProject(e);
                } else {
                  onOpen?.(task);
                  toast.info(`Projet supprimé — j'ouvre les infos de la tâche`);
                }
              }}
              onKeyDown={(e) => { if (e.key === 'Enter') openProject(e); }}
              onMouseDown={(e) => e.stopPropagation()}
              style={{ padding: '2px 7px', borderRadius: 999, background: 'rgba(127,221,184,0.14)', border: '1px solid rgba(127,221,184,0.35)', fontFamily: 'Sora', fontWeight: 700, fontSize: 9.5, color: '#7FDDB8', letterSpacing: '0.04em', textTransform: 'uppercase', cursor: 'pointer' }}
              title={projectExists ? "Ouvrir la vidéo dans le projet" : "Projet supprimé"}
            >Projet</span>
          )}
        </div>
        {projLabel && (
          <div style={{ fontFamily: 'Sora', fontWeight: 500, fontSize: 10.5, color: T3, marginTop: 2, marginBottom: task.status !== 'fini' ? 10 : 0 }}>{projLabel}</div>
        )}
      </button>
      {isPosterTask(task) && !posted && <PosterShiftControls task={task} optimisticPatch={optimisticPatch} reload={reload} />}
    </div>
      </div>
    </motion.div>
  );
}
function shiftIso(iso, n) {
  const d = new Date(iso + 'T12:00:00'); d.setDate(d.getDate() + n); return isoLocal(d);
}
// Décale une poster_task de n jours (la vidéo projet suit côté backend).
async function shiftTaskDays(task, n, optimisticPatch, reload) {
  // Post en retard : « +1 jour » le remet à demain (jamais vers une date passée).
  const late = task.date_debut < todayLocalIso();
  const date_debut = late ? shiftIso(todayLocalIso(), n) : shiftIso(task.date_debut, n);
  const date_fin = date_debut;
  optimisticPatch?.(task.id, { date_debut, date_fin });
  try {
    const r = await api.patch(`/asa-tasks/${task.id}`, { date_debut, date_fin });
    const day = new Date(date_debut + 'T12:00:00').toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
    toast.success(r?.data?.swapped_with ? `Décalée au ${day} · échangée avec « ${r.data.swapped_with} » (pas 2 posts le même jour)` : `Décalée au ${day}`);
    reload?.();
  } catch (err) {
    optimisticPatch?.(task.id, { date_debut: task.date_debut, date_fin: task.date_fin });
    toast.error(err?.response?.data?.detail || 'Erreur');
  }
}

function PosterShiftControls({ task, optimisticPatch, reload }) {
  const today = todayLocalIso();
  const late = (task.date_debut || '') < today;
  const btn = (n, label, testid) => (
    <button
      type="button"
      data-testid={testid}
      disabled={n < 0 && (late || shiftIso(task.date_debut, n) < today)}
      title={n < 0 && (late || shiftIso(task.date_debut, n) < today) ? 'Impossible de déplacer un post vers une date passée' : undefined}
      onClick={(e) => { e.stopPropagation(); shiftTaskDays(task, n, optimisticPatch, reload); }}
      onMouseDown={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
      className="poster-shift-btn"
      style={{
        flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4,
        padding: '6px 8px', borderRadius: 8,
        background: 'rgba(62,123,250,0.10)', border: `1px solid ${BORDER}`,
        color: T2, cursor: n < 0 && (late || shiftIso(task.date_debut, n) < today) ? 'not-allowed' : 'pointer',
        opacity: n < 0 && (late || shiftIso(task.date_debut, n) < today) ? 0.35 : 1,
        fontFamily: 'Sora', fontWeight: 700, fontSize: 10.5,
        transition: 'background-color 0.12s var(--ease-out), color 0.12s var(--ease-out)',
      }}
    >{label}</button>
  );
  return (
    <div data-testid={`poster-shift-${task.id}`} style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, paddingTop: 8, borderTop: `1px dashed ${BORDER}` }}>
      <span style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 9.5, color: T3, letterSpacing: '0.06em', textTransform: 'uppercase', flexShrink: 0 }}>Décaler</span>
      {btn(-1, <><ChevronLeft size={11} strokeWidth={2.6} /> −1 jour</>, `poster-shift-prev-${task.id}`)}
      {btn(1, <>{late ? 'Demain' : '+1 jour'} <ChevronRight size={11} strokeWidth={2.6} /></>, `poster-shift-next-${task.id}`)}
    </div>
  );
}

// Tâche « 📱 Poster… » : uniquement Titre, Description, Lien Drive + Décaler + Valider.
function PosterMetaEditor({ task, meta, onDone }) {
  const [title, setTitle] = useState(meta.title || '');
  const [desc, setDesc] = useState(meta.description || '');
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/asa-tasks/${task.id}/post-meta`, { title: title.trim(), description: desc });
      toast.success('Titre & description mis à jour');
      onDone(true);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); setSaving(false); }
  };
  const field = { width: '100%', padding: '9px 11px', borderRadius: 10, background: 'rgba(255,255,255,0.04)', border: `1px solid ${BORDER}`, color: T1, fontFamily: 'Lexend', fontSize: 13, outline: 'none', boxSizing: 'border-box' };
  return (
    <div data-testid="poster-meta-editor" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <input data-testid="poster-edit-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Titre de la vidéo (vide = nom du concept)" style={{ ...field, fontWeight: 700 }} />
      <textarea data-testid="poster-edit-desc" value={desc} onChange={(e) => setDesc(e.target.value)} rows={5} placeholder="Description du post" style={{ ...field, resize: 'vertical', fontSize: 12.5 }} />
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" onClick={() => onDone(false)} style={{ ...field, width: 'auto', cursor: 'pointer', fontFamily: 'Sora', fontWeight: 700, fontSize: 12 }}>Annuler</button>
        <button type="button" data-testid="poster-edit-save" onClick={save} disabled={saving} style={{ flex: 1, padding: '9px 12px', borderRadius: 10, border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg, #5BBEFC 0%, #3E7BFA 100%)', color: NAVY, fontFamily: 'Sora', fontWeight: 800, fontSize: 12 }}>{saving ? '…' : 'Enregistrer'}</button>
      </div>
    </div>
  );
}

function PosterAssignee({ task, canReassign, onChanged }) {
  useTeamVersion();
  const current = (Array.isArray(task.responsable) ? task.responsable : [task.responsable]).filter(Boolean)[0] || '';
  const change = async (e) => {
    const id = e.target.value;
    if (!id || id === current) return;
    try {
      await api.patch(`/asa-tasks/${task.id}`, { responsable: [id] });
      toast.success(`Poster → ${ALL_TEAM.find((m) => m.id === id)?.name || id}`);
      onChanged();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erreur'); }
  };
  return (
    <div data-testid="poster-assignee" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 12, background: 'rgba(245,197,106,0.06)', border: '1px solid rgba(245,197,106,0.3)' }}>
      <span style={{ flex: 1, fontFamily: 'Sora', fontWeight: 800, fontSize: 10.5, color: T3, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Poster</span>
      {canReassign ? (
        <select data-testid="poster-assignee-select" value={current} onChange={change} style={{ padding: '7px 10px', borderRadius: 9, background: SURFACE2, border: `1px solid ${BORDER}`, color: T1, fontFamily: 'Sora', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
          {!current && <option value="">Choisir…</option>}
          {ALL_TEAM.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
      ) : (
        <span style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 12.5, color: '#F5C56A' }}>{ALL_TEAM.find((m) => m.id === current)?.name || current || '—'}</span>
      )}
    </div>
  );
}

// Miniature Drive (fichier partagé) : /file/d/ID, ?id=ID ou open?id=ID. Un dossier n'a pas de miniature.
const driveFileId = (url) => (url || '').match(/\/file\/d\/([\w-]{10,})|[?&]id=([\w-]{10,})/)?.slice(1).find(Boolean) || null;

function CoverThumb({ link }) {
  const [failed, setFailed] = useState(false);
  const id = driveFileId(link);
  useEffect(() => setFailed(false), [link]);
  if (!link) return null;
  if (!id || failed) {
    return <div data-testid="poster-cover-thumb-hint" style={{ marginTop: 8, fontFamily: 'Lexend', fontSize: 11, color: T3 }}>{id ? 'Miniature indisponible : partage le fichier en « Tous les utilisateurs disposant du lien ».' : 'Lien vers un dossier : mets le lien de l\'image de cette vidéo (✏️ Modifier) pour voir la miniature.'}</div>;
  }
  return (
    <a href={link} target="_blank" rel="noopener noreferrer" style={{ display: 'block', marginTop: 8 }}>
      <img data-testid="poster-cover-thumb" src={`https://drive.google.com/thumbnail?id=${id}&sz=w480`} alt="Couverture" onError={() => setFailed(true)} style={{ width: '100%', maxWidth: 220, aspectRatio: '9 / 16', objectFit: 'cover', borderRadius: 12, border: `1px solid ${BORDER}`, display: 'block' }} />
    </a>
  );
}

function PosterDriveLink({ link, task, onSaved, field = 'drive_link', label = 'Lien Drive de la vidéo', testid = 'poster-drive' }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(link);
  const save = async () => {
    try {
      await api.patch(`/asa-tasks/${task.id}/post-meta`, { [field]: val.trim() });
      toast.success('Lien enregistré ✓');
      setEditing(false); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };
  return (
    <div data-testid={testid} style={{ padding: '10px 12px', borderRadius: 12, background: 'rgba(255,255,255,0.03)', border: `1px solid ${BORDER}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <ExternalLink size={12} color="#5BBEFC" />
        <span style={{ flex: 1, fontFamily: 'Sora', fontWeight: 800, fontSize: 10.5, color: T3, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{label}</span>
        {!editing && <button type="button" data-testid={`${testid}-edit`} onClick={() => { setVal(link); setEditing(true); }} style={{ padding: '6px 10px', borderRadius: 9, background: 'transparent', border: `1px solid ${BORDER}`, color: T2, cursor: 'pointer', fontFamily: 'Sora', fontWeight: 700, fontSize: 11 }}>{link ? '✏️ Modifier' : '+ Ajouter'}</button>}
        {link && !editing && field === 'covers_link' && <button type="button" data-testid={`${testid}-copy`} onClick={() => { navigator.clipboard.writeText(link); toast.success('Lien copié ✓'); }} style={{ padding: '6px 10px', borderRadius: 9, background: 'transparent', border: `1px solid ${BORDER}`, color: T2, cursor: 'pointer', fontFamily: 'Sora', fontWeight: 700, fontSize: 11 }}>Copier</button>}
        {link && !editing && <a data-testid={`${testid}-open`} href={link} target="_blank" rel="noopener noreferrer" style={{ padding: '6px 11px', borderRadius: 9, background: 'linear-gradient(135deg, #5BBEFC 0%, #3E7BFA 100%)', color: NAVY, fontFamily: 'Sora', fontWeight: 800, fontSize: 11, textDecoration: 'none' }}>Ouvrir</a>}
      </div>
      {editing ? (
        <div style={{ display: 'flex', gap: 6 }}>
          <input data-testid={`${testid}-input`} autoFocus value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && save()} placeholder="https://drive.google.com/…" style={{ flex: 1, padding: '8px 10px', borderRadius: 9, background: SURFACE2, border: `1px solid ${BORDER}`, color: T1, fontFamily: 'Lexend', fontSize: 12 }} />
          <button type="button" data-testid={`${testid}-save`} onClick={save} style={{ padding: '8px 12px', borderRadius: 9, background: 'linear-gradient(135deg, #5BBEFC 0%, #3E7BFA 100%)', border: 'none', color: NAVY, cursor: 'pointer', fontFamily: 'Sora', fontWeight: 800, fontSize: 11.5 }}>OK</button>
          <button type="button" data-testid={`${testid}-cancel`} onClick={() => setEditing(false)} style={{ padding: '8px 10px', borderRadius: 9, background: 'transparent', border: `1px solid ${BORDER}`, color: T2, cursor: 'pointer' }}><X size={13} /></button>
        </div>
      ) : (
        <div data-testid={`${testid}-value`} style={{ fontFamily: 'Lexend', fontSize: 12, color: link ? T2 : T3, wordBreak: 'break-all' }}>{link || 'Pas encore de lien'}</div>
      )}
      {field === 'covers_link' && !editing && <CoverThumb link={link} />}
    </div>
  );
}

function PosterTaskName({ task, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(task.title || '');
  const save = async () => {
    if (!val.trim()) return;
    try {
      await api.patch(`/asa-tasks/${task.id}/post-meta`, { task_name: val.trim() });
      toast.success('Nom de la tâche modifié ✓'); setEditing(false); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };
  if (!editing) {
    return <button type="button" data-testid="poster-task-name-edit" onClick={() => { setVal(task.title || ''); setEditing(true); }} style={{ alignSelf: 'flex-start', padding: '5px 10px', borderRadius: 9, background: 'transparent', border: `1px solid ${BORDER}`, color: T2, cursor: 'pointer', fontFamily: 'Sora', fontWeight: 700, fontSize: 11 }}>✏️ Renommer la tâche</button>;
  }
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      <input data-testid="poster-task-name-input" autoFocus value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && save()} style={{ flex: 1, padding: '8px 10px', borderRadius: 9, background: SURFACE2, border: `1px solid ${BORDER}`, color: T1, fontFamily: 'Sora', fontWeight: 700, fontSize: 13 }} />
      <button type="button" data-testid="poster-task-name-save" onClick={save} style={{ padding: '8px 12px', borderRadius: 9, background: 'linear-gradient(135deg, #5BBEFC 0%, #3E7BFA 100%)', border: 'none', color: NAVY, cursor: 'pointer', fontFamily: 'Sora', fontWeight: 800, fontSize: 11.5 }}>OK</button>
      <button type="button" onClick={() => setEditing(false)} style={{ padding: '8px 10px', borderRadius: 9, background: 'transparent', border: `1px solid ${BORDER}`, color: T2, cursor: 'pointer' }}><X size={13} /></button>
    </div>
  );
}

function PosterMinimalBody({ task, busy, canReassign, canSelfValidate, setStatus, onShifted }) {
  const meta = task.post_meta || {};
  const [editing, setEditing] = useState(false);
  const link = meta.video_url || task.preview_url || task.frameio_link || task.drive_link || '';
  const done = task.status === 'fini';
  const pending = task.status === 'a_valider';
  const isMobile = useIsMobile();
  // Sur téléphone, le bouton « C'est posté » est en haut (sinon caché sous la barre du bas).
  const validateBtn = (
    <button
      type="button"
      data-testid="poster-validate"
      disabled={busy || pending && !canSelfValidate}
      onClick={() => setStatus(done ? 'a_faire' : (canSelfValidate ? 'fini' : 'a_valider'))}
      style={{
        marginTop: isMobile ? 0 : 8, padding: '13px 18px', borderRadius: 12, border: done ? `1px solid ${BORDER}` : 'none', cursor: 'pointer',
        background: done ? 'transparent' : 'linear-gradient(180deg, #34D399 0%, #10B981 100%)',
        color: done ? T2 : NAVY, fontFamily: 'Sora', fontWeight: 800, fontSize: 13.5,
        boxShadow: done ? 'none' : '0 10px 24px -8px rgba(52,211,153,0.6)',
      }}
    >{done ? 'Remettre en « à poster »' : pending && !canSelfValidate ? '🔒 En attente de validation' : canSelfValidate ? '✓ C\'est posté' : '✓ C\'est posté (envoyer en validation)'}</button>
  );
  return (
    <div data-testid="poster-minimal-body" style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingBottom: isMobile ? 90 : 0 }}>
      {isMobile && validateBtn}
      <PosterTaskName task={task} onSaved={onShifted} />
      {!done && <PosterAssignee task={task} canReassign={canReassign} onChanged={onShifted} />}
      {editing ? (
        <PosterMetaEditor task={task} meta={meta} onDone={(saved) => { setEditing(false); if (saved) onShifted(); }} />
      ) : (
        <>
          <CopyField icon={Type} label="Titre de la vidéo" text={meta.title || (task.title || '').replace('📱 Poster ', '')} emptyLabel="—" testid="poster-title" />
          <CopyField icon={AlignLeft} label="Description de la vidéo" text={meta.description || ''} emptyLabel="Sans description" testid="poster-desc" multiline />
          <button type="button" data-testid="poster-edit-btn" onClick={() => setEditing(true)} style={{ alignSelf: 'flex-start', padding: '6px 12px', borderRadius: 9, background: 'transparent', border: `1px solid ${BORDER}`, color: T2, cursor: 'pointer', fontFamily: 'Sora', fontWeight: 700, fontSize: 11.5 }}>✏️ Modifier titre / description</button>
        </>
      )}
      <PosterDriveLink link={link} task={task} onSaved={onShifted} />
      {meta.covers_enabled && <PosterDriveLink link={meta.covers_url || ''} task={task} onSaved={onShifted} field="covers_link" label="Lien des couvertures" testid="poster-covers" />}
      {!done && (
        <div style={{ marginTop: 4 }}>
          <PosterShiftControls task={task} optimisticPatch={null} reload={onShifted} />
        </div>
      )}
      {!isMobile && validateBtn}
    </div>
  );
}

function TaskCardGhost({ task }) {
  return (
    <div style={{ padding: '12px 14px', borderRadius: 12, background: SURFACE2, border: `1px solid ${BLUE}`, minWidth: 240, boxShadow: `0 22px 40px -10px rgba(62,123,250,0.5)` }}>
      <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 13, color: T1 }}>{task.title}</div>
    </div>
  );
}

// ─── Calendar month view ─────────────────────────────────────────────────────
function CalendarMonthView({ tasks, projects, onSelectDay, onDayClick, optimisticPatch, reload }) {
  const isMobile = useIsMobile();
  // Iter 62eo — Sur mobile, défaut = "semaine" (bien plus lisible qu'un mois
  // écrasé où on ne voit que "D..." "S..."). Bascule possible vers Mois.
  const [viewMode, setViewMode] = useState(isMobile ? 'week' : 'month');
  const [cursor, setCursor] = useState(() => {
    const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  // Iter 62eo — Curseur semaine (démarre au lundi de la semaine courante)
  const [weekCursor, setWeekCursor] = useState(() => {
    const d = new Date();
    const dow = (d.getDay() + 6) % 7; // 0 = Mon
    d.setDate(d.getDate() - dow);
    d.setHours(12, 0, 0, 0);
    return d;
  });
  const monthLabel = cursor.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstOffset = (new Date(year, month, 1).getDay() + 6) % 7; // Mon = 0

  const days = [];
  for (let i = 0; i < firstOffset; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) days.push(`${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`);
  while (days.length % 7) days.push(null);

  // Iter 62eo — Semaine : 7 ISO dates à partir du curseur
  const weekDays = useMemo(() => {
    const out = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(weekCursor);
      d.setDate(weekCursor.getDate() + i);
      out.push(isoLocal(d));
    }
    return out;
  }, [weekCursor]);
  const weekLabel = useMemo(() => {
    const start = weekCursor;
    const end = new Date(weekCursor);
    end.setDate(weekCursor.getDate() + 6);
    const opts = { day: 'numeric', month: 'short' };
    return `${start.toLocaleDateString('fr-FR', opts)} — ${end.toLocaleDateString('fr-FR', opts)}`;
  }, [weekCursor]);

  // Post en retard / vidéo en cours non finie → visible chaque jour, de sa date jusqu'à aujourd'hui (comme dans la vue du jour).
  const tasksForDay = (iso) => orderForDay(tasks
    .filter((t) => ((t.date_debut || '') <= iso && iso <= (t.date_fin || t.date_debut || ''))
      || (isLatePost(t) && (t.date_debut || '') <= iso && iso <= todayLocalIso())
      || (isCarriedVideo(t) && iso <= todayLocalIso() && iso >= [(t.date_debut || todayLocalIso()).slice(0, 10), todayLocalIso()].sort()[0])), iso);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 10 } }),
  );
  const [dragged, setDragged] = useState(null);
  const handleDragEnd = async (e) => {
    setDragged(null);
    const { active, over } = e;
    if (!over) return;
    const task = tasks.find((t) => t.id === active.id);
    if (!task) return;
    const newDate = String(over.id);
    if (!newDate.match(/^\d{4}-\d{2}-\d{2}$/) || newDate === task.date_debut) return;
    // Preserve range duration
    try {
      const start = new Date(task.date_debut + 'T12:00:00');
      const end = new Date((task.date_fin || task.date_debut) + 'T12:00:00');
      const diffDays = Math.round((end - start) / 86400000);
      const newStart = newDate;
      const nd = new Date(newDate + 'T12:00:00'); nd.setDate(nd.getDate() + diffDays);
      const newEnd = isoLocal(nd);
      // Iter 62de — Optimistic
      optimisticPatch?.(task.id, { date_debut: newStart, date_fin: newEnd });
      await api.patch(`/asa-tasks/${task.id}`, { date_debut: newStart, date_fin: newEnd });
      reload();
      toast.success('Tâche déplacée');
    } catch (err) {
      optimisticPatch?.(task.id, { date_debut: task.date_debut, date_fin: task.date_fin });
      toast.error(err?.response?.data?.detail || 'Erreur');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        {viewMode === 'month' ? (
          <>
            <button data-testid="cal-prev" onClick={() => setCursor(new Date(year, month - 1, 1))} style={{ width: 34, height: 34, borderRadius: 10, background: SURFACE2, border: `1px solid ${BORDER}`, display: 'grid', placeItems: 'center', cursor: 'pointer', color: T2 }}><ChevronLeft size={16} /></button>
            <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 16, color: T1, textTransform: 'capitalize' }}>{monthLabel}</div>
            <button data-testid="cal-next" onClick={() => setCursor(new Date(year, month + 1, 1))} style={{ width: 34, height: 34, borderRadius: 10, background: SURFACE2, border: `1px solid ${BORDER}`, display: 'grid', placeItems: 'center', cursor: 'pointer', color: T2 }}><ChevronRight size={16} /></button>
          </>
        ) : (
          <>
            <button data-testid="cal-prev-week" onClick={() => {
              const d = new Date(weekCursor); d.setDate(d.getDate() - 7); setWeekCursor(d);
            }} style={{ width: 34, height: 34, borderRadius: 10, background: SURFACE2, border: `1px solid ${BORDER}`, display: 'grid', placeItems: 'center', cursor: 'pointer', color: T2 }}><ChevronLeft size={16} /></button>
            <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 15, color: T1 }}>{weekLabel}</div>
            <button data-testid="cal-next-week" onClick={() => {
              const d = new Date(weekCursor); d.setDate(d.getDate() + 7); setWeekCursor(d);
            }} style={{ width: 34, height: 34, borderRadius: 10, background: SURFACE2, border: `1px solid ${BORDER}`, display: 'grid', placeItems: 'center', cursor: 'pointer', color: T2 }}><ChevronRight size={16} /></button>
          </>
        )}
        <div style={{ flex: 1 }} />
        {/* Iter 62eo — Toggle Semaine / Mois */}
        <div
          style={{
            display: 'inline-flex', gap: 2, padding: 3, borderRadius: 10,
            background: SURFACE2, border: `1px solid ${BORDER}`,
          }}
        >
          {[{ id: 'week', label: 'Semaine' }, { id: 'month', label: 'Mois' }].map((v) => {
            const active = viewMode === v.id;
            return (
              <button
                key={v.id}
                data-testid={`cal-view-${v.id}`}
                onClick={() => setViewMode(v.id)}
                style={{
                  padding: '6px 12px', borderRadius: 8,
                  background: active ? BLUE : 'transparent',
                  color: active ? '#fff' : T2,
                  border: 'none', cursor: 'pointer',
                  fontFamily: 'Sora', fontWeight: 700, fontSize: 11.5,
                  letterSpacing: '-0.005em',
                }}
              >{v.label}</button>
            );
          })}
        </div>
      </div>
      {/* Iter 62di — Container avec width 100% + overflow hidden pour ne JAMAIS dépasser l'écran. */}
      <div style={{ width: '100%', overflow: 'hidden' }}>
        {viewMode === 'week' ? (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={(e) => setDragged(tasks.find((t) => t.id === e.active.id))} onDragEnd={handleDragEnd}>
            {/* Vue Semaine : chaque jour = pleine largeur, tâches lisibles */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {weekDays.map((iso) => (
                <WeekDayRow key={iso} iso={iso} tasks={tasksForDay(iso)} projects={projects} onSelectDay={onSelectDay} />
              ))}
            </div>
            <DragOverlay>{dragged && <TaskCardGhost task={dragged} />}</DragOverlay>
          </DndContext>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 6, marginBottom: 6 }}>
              {['Lun','Mar','Mer','Jeu','Ven','Sam','Dim'].map((d) => (
                <div key={d} style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 10, color: T3, letterSpacing: '0.06em', textTransform: 'uppercase', paddingLeft: 4 }}>{d}</div>
              ))}
            </div>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={(e) => setDragged(tasks.find((t) => t.id === e.active.id))} onDragEnd={handleDragEnd}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 6 }}>
                {days.map((iso, i) => iso ? <CalendarCell key={i} iso={iso} tasks={tasksForDay(iso)} onSelectDay={onSelectDay} onDayClick={onDayClick} /> : <div key={i} style={{ height: 118 }} />)}
              </div>
              <DragOverlay>{dragged && <TaskCardGhost task={dragged} />}</DragOverlay>
            </DndContext>
          </>
        )}
      </div>
    </div>
  );
}
// Iter 62eo — WeekDayRow : ligne pleine largeur par jour, drag&droppable,
// titres et infos parfaitement lisibles. Utilisée en vue Semaine mobile-first.
function WeekDayRow({ iso, tasks, projects, onSelectDay }) {
  const { setNodeRef, isOver } = useDroppable({ id: iso });
  const d = new Date(iso + 'T12:00:00');
  const isToday = iso === isoLocal();
  const dayLabel = d.toLocaleDateString('fr-FR', { weekday: 'short' });
  const dayNum = d.getDate();
  return (
    <div
      ref={setNodeRef}
      onClick={() => onSelectDay(iso)}
      data-testid={`cal-week-day-${iso}`}
      style={{
        padding: '10px 12px', borderRadius: 12, cursor: 'pointer',
        background: isOver ? 'rgba(62,123,250,0.14)' : SURFACE,
        border: `1px solid ${isToday ? BLUE : (isOver ? BLUE : BORDER)}`,
        boxShadow: isToday ? '0 0 12px rgba(62,123,250,0.22)' : 'none',
        transition: 'all 0.15s var(--ease-out)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: tasks.length ? 8 : 0 }}>
        <span style={{
          fontFamily: 'Sora', fontWeight: 800, fontSize: 20, color: isToday ? BLUE_LIGHT : T1,
          letterSpacing: '-0.02em', lineHeight: 1,
        }}>{dayNum}</span>
        <span style={{
          fontFamily: 'Sora', fontWeight: 700, fontSize: 11, color: T3,
          letterSpacing: '0.05em', textTransform: 'uppercase',
        }}>{dayLabel}</span>
        {isToday && (
          <span style={{
            marginLeft: 6, padding: '2px 8px', borderRadius: 999,
            background: `${BLUE}22`, border: `1px solid ${BLUE}66`,
            color: BLUE_LIGHT, fontFamily: 'Sora', fontWeight: 700, fontSize: 9.5,
            letterSpacing: '0.06em', textTransform: 'uppercase',
          }}>Aujourd&apos;hui</span>
        )}
        <div style={{ flex: 1 }} />
        <span style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 11, color: T3 }}>
          {tasks.length ? `${tasks.length} tâche${tasks.length > 1 ? 's' : ''}` : 'Rien'}
        </span>
      </div>
      {tasks.map((t) => <WeekTaskRow key={t.id} task={t} />)}
    </div>
  );
}

// Ligne de tâche draggable (vue Semaine) — drop sur un autre jour = décalage.
function WeekTaskRow({ task: t }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: t.id });
  const prio = PRIORITY[t.priority] || PRIORITY.normal;
  const statusMeta = STATUS[t.status] || STATUS.a_faire;
  const posted = isPosted(t);
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      data-testid={`cal-week-task-${t.id}`}
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '7px 10px', borderRadius: 8, marginTop: 4,
        background: `${statusMeta.color}10`,
        borderLeft: `3px solid ${prio.accent}`,
        border: `1px solid ${statusMeta.color}22`,
        cursor: 'grab',
        opacity: isDragging ? 0.3 : (posted ? 0.45 : 1),
        filter: posted ? 'grayscale(0.85)' : 'none',
        transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
      }}
    >
      <span style={{ width: 5, height: 5, borderRadius: 999, background: statusMeta.color, flexShrink: 0 }} />
      <span style={{
        flex: 1, minWidth: 0,
        fontFamily: 'Sora', fontWeight: 700, fontSize: 12.5, color: T1,
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        letterSpacing: '-0.005em',
        textDecoration: posted ? 'line-through' : 'none',
      }}>{t.title}</span>
      <span style={{
        fontFamily: 'Sora', fontWeight: 600, fontSize: 9.5, color: statusMeta.color,
        letterSpacing: '0.04em', textTransform: 'uppercase', flexShrink: 0,
      }}>{posted ? 'Posté' : statusMeta.label}</span>
    </div>
  );
}

function CalendarCell({ iso, tasks, onSelectDay, onDayClick }) {
  const { setNodeRef, isOver } = useDroppable({ id: iso });
  const dayNum = parseInt(iso.slice(8), 10);
  const isToday = iso === isoLocal();
  const handleClick = (e) => {
    e.stopPropagation();
    // Iter 62fg — Remonte le clic vers le parent qui gère le modal plein écran
    // (ou applique directement la préférence utilisateur si définie).
    if (onDayClick) { onDayClick(iso); return; }
    onSelectDay(iso);
  };
  return (
    <div style={{ position: 'relative' }}>
      <div
        ref={setNodeRef}
        onClick={handleClick}
        data-testid={`cal-day-${iso}`}
        style={{
          minHeight: 118, minWidth: 0,
          padding: '6px 6px 6px', borderRadius: 10,
          background: isOver ? 'rgba(62,123,250,0.10)' : SURFACE,
          border: `1px solid ${isToday ? BLUE : BORDER}`,
          cursor: 'pointer',
          display: 'flex', flexDirection: 'column',
          transition: 'all 0.15s var(--ease-out)',
        }}
      >
        <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 11.5, color: isToday ? BLUE_LIGHT : T2, marginBottom: 4, flexShrink: 0 }}>{dayNum}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1, minHeight: 0 }}>
          {tasks.map((t) => <CalendarChip key={t.id} task={t} />)}
        </div>
      </div>
    </div>
  );
}

// ─── Modal plein écran : choix "Vue Kanban" vs "Vue journalière" ──────────────
// Iter 62fg — Au clic sur un jour depuis le calendrier mois, on floute le fond
// et on propose deux grosses cards animées. Checkbox "toujours cette vue" pour
// mémoriser le choix dans localStorage (réinit possible dans Réglages).
// Préférence : `asa_tasks_v2_day_view_pref` = 'ask' | 'kanban' | 'horaire'.
export const DAY_VIEW_PREF_KEY = 'asa_tasks_v2_day_view_pref';
export function readDayViewPref() {
  try { return localStorage.getItem(DAY_VIEW_PREF_KEY) || 'ask'; } catch { return 'ask'; }
}
export function writeDayViewPref(v) {
  try { localStorage.setItem(DAY_VIEW_PREF_KEY, v); } catch { /* noop */ }
}

function DayViewPickerModal({ iso, onPick, onClose }) {
  const [remember, setRemember] = useState(false);
  const dayLabel = useMemo(() => {
    try {
      const d = new Date(iso + 'T12:00:00');
      return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
    } catch { return iso; }
  }, [iso]);
  const choose = (view) => {
    if (remember) writeDayViewPref(view);
    onPick(view);
  };
  return (
    <div
      onClick={onClose}
      data-testid="day-view-picker-overlay"
      style={{
        position: 'fixed', inset: 0, zIndex: 120,
        background: 'rgba(6,12,26,0.72)',
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
        display: 'grid', placeItems: 'center', padding: 20,
        animation: 'fadeIn 0.18s var(--ease-out)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="scale-in"
        style={{
          width: '100%', maxWidth: 560,
          background: `linear-gradient(180deg, ${SURFACE} 0%, rgba(14,22,44,0.98) 100%)`,
          borderRadius: 24, border: `1px solid ${BORDER}`,
          boxShadow: '0 50px 100px -24px rgba(0,0,0,0.72), 0 0 0 1px rgba(255,255,255,0.04) inset',
          padding: 28,
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 10.5, color: T3, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8 }}>
            Comment veux-tu voir
          </div>
          <h2 style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 22, color: T1, margin: 0, textTransform: 'capitalize', letterSpacing: '-0.02em' }}>
            {dayLabel} ?
          </h2>
        </div>
        {/* 2 cards logos */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <ViewPickerCard
            testid="day-view-pick-kanban"
            icon={<GridIcon size={34} strokeWidth={2} />}
            accent={BLUE_LIGHT}
            title="Vue Kanban"
            subtitle="5 colonnes par statut"
            onClick={() => choose('kanban')}
          />
          <ViewPickerCard
            testid="day-view-pick-horaire"
            icon={<CalendarIcon size={34} strokeWidth={2} />}
            accent="#B294F5"
            title="Vue journalière"
            subtitle="Timeline 0h → 24h"
            onClick={() => choose('horaire')}
          />
        </div>
        {/* Toggle "toujours cette vue" */}
        <label
          data-testid="day-view-remember-toggle"
          style={{
            display: 'flex', alignItems: 'center', gap: 12,
            marginTop: 20, padding: '12px 14px',
            background: SURFACE2, border: `1px solid ${BORDER}`, borderRadius: 12,
            cursor: 'pointer',
          }}
        >
          <button
            type="button"
            role="switch"
            aria-checked={remember}
            onClick={(e) => { e.preventDefault(); setRemember((v) => !v); }}
            style={{
              width: 42, height: 24, borderRadius: 999,
              background: remember ? BLUE : 'rgba(255,255,255,0.14)',
              border: 'none', cursor: 'pointer', padding: 2, flexShrink: 0,
              position: 'relative', transition: 'background 0.15s var(--ease-out)',
            }}
          >
            <span style={{
              display: 'block', width: 20, height: 20, borderRadius: '50%',
              background: '#fff', boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
              transform: `translateX(${remember ? 18 : 0}px)`,
              transition: 'transform 0.18s var(--ease-out)',
            }} />
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 13, color: T1 }}>Toujours ouvrir dans cette vue</div>
            <div style={{ fontFamily: 'Sora', fontWeight: 500, fontSize: 11, color: T3, marginTop: 2 }}>
              Modifiable dans Réglages → Tâches
            </div>
          </div>
        </label>
        <button
          type="button"
          onClick={onClose}
          data-testid="day-view-picker-close"
          style={{
            marginTop: 14, width: '100%', padding: '10px 14px',
            background: 'transparent', border: `1px solid ${BORDER}`, borderRadius: 12,
            color: T2, fontFamily: 'Sora', fontWeight: 700, fontSize: 12.5, cursor: 'pointer',
          }}
        >Annuler</button>
      </div>
    </div>
  );
}

function ViewPickerCard({ testid, icon, accent, title, subtitle, onClick }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
        padding: '24px 16px', borderRadius: 18,
        background: `linear-gradient(180deg, ${accent}14 0%, ${accent}08 100%)`,
        border: `1px solid ${accent}50`,
        color: T1, cursor: 'pointer',
        boxShadow: `0 10px 30px -12px ${accent}44`,
        transition: 'transform 0.18s var(--ease-out), box-shadow 0.18s var(--ease-out), border-color 0.18s var(--ease-out)',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-3px)';
        e.currentTarget.style.boxShadow = `0 18px 44px -14px ${accent}88`;
        e.currentTarget.style.borderColor = `${accent}99`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = `0 10px 30px -12px ${accent}44`;
        e.currentTarget.style.borderColor = `${accent}50`;
      }}
    >
      <div style={{
        width: 68, height: 68, borderRadius: '50%',
        display: 'grid', placeItems: 'center',
        background: `linear-gradient(180deg, ${accent}2E 0%, ${accent}18 100%)`,
        border: `1px solid ${accent}70`,
        color: accent,
      }}>
        {icon}
      </div>
      <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 15, color: T1, letterSpacing: '-0.01em' }}>{title}</div>
      <div style={{ fontFamily: 'Sora', fontWeight: 600, fontSize: 11, color: T3 }}>{subtitle}</div>
    </button>
  );
}
function CalendarChip({ task }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: task.id });
  const meta = STATUS[task.status];
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      onClick={(e) => e.stopPropagation()}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.35 : (isPosted(task) ? 0.42 : 1),
        filter: isPosted(task) ? 'grayscale(0.85)' : 'none',
        textDecoration: isPosted(task) ? 'line-through' : 'none',
        padding: '2px 6px', borderRadius: 5,
        background: meta.bg,
        border: `1px solid ${meta.color}55`,
        fontFamily: 'Sora', fontWeight: 700, fontSize: 9.5, color: meta.color,
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        cursor: 'grab', flexShrink: 0,
        minWidth: 0, maxWidth: '100%',
      }}
      data-testid={`cal-chip-${task.id}`}
    >{task.title}</div>
  );
}

// ─── Task detail (side panel) ────────────────────────────────────────────────
// Iter 62ds — Reçoit `initialTask` (objet complet déjà chargé côté liste) au
// lieu d'un simple `taskId`. Le panel s'affiche INSTANTANÉMENT avec la data
// déjà en mémoire, puis un refresh silencieux tourne en arrière-plan pour
// récupérer les champs manquants (commentaires, historique, documents).
function TaskDetailPanel({ initialTask, projects, currentUserId, isMyView, onClose, onChanged }) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const taskId = initialTask?.id;
  const [task, setTask] = useState(initialTask);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [chronoTick, setChronoTick] = useState(0);
  const [respPickerOpen, setRespPickerOpen] = useState(false);
  const [editing, setEditing] = useState(false); // Iter 62ds — mode édition inline
  const [showMoreBuckets, setShowMoreBuckets] = useState(!!(initialTask?.priority_bucket && initialTask.priority_bucket > 3)); // Iter 62ey
  const [documents, setDocuments] = useState([]);
  const [uploadBusy, setUploadBusy] = useState(false);
  // Iter 62eo — Compacte le panel mobile : "Voir plus" pour Commentaires + Documents.
  // Ouvert par défaut sur desktop (grand écran = tout visible).
  const [showExtras, setShowExtras] = useState(!isMobile);

  const load = useCallback(async () => {
    try { const r = await api.get(`/asa-tasks/${taskId}`); setTask(r.data); } catch { /* keep initial */ }
  }, [taskId]);
  const loadDocuments = useCallback(async () => {
    try { const r = await api.get(`/asa-tasks/${taskId}/documents`); setDocuments(r.data || []); }
    catch { setDocuments([]); }
  }, [taskId]);
  // Iter 62ds — Refresh silencieux (le panel est déjà rendu avec initialTask).
  useEffect(() => { load(); loadDocuments(); }, [load, loadDocuments]);
  useEffect(() => {
    if (task?.chrono_demarre_a) {
      const iv = setInterval(() => setChronoTick((x) => x + 1), 1000);
      return () => clearInterval(iv);
    }
  }, [task?.chrono_demarre_a]);

  if (!task) return null;
  const resps = respsOf(task);
  const isOwn = resps.includes(currentUserId);
  const cannotValidate = task.status === 'a_valider' && isOwn && !SELF_VALIDATOR_IDS.has(currentUserId);
  const members = resps.map(memberById).filter(Boolean);
  const prio = PRIORITY[task.priority] || PRIORITY.normal;
  const stat = STATUS[task.status];
  const projectExists = task.projet && (projects || []).some((p) => p.id === task.projet);
  const openProject = () => {
    if (!projectExists) return;
    try {
      localStorage.setItem('asa_open_project', task.projet);
      localStorage.setItem('asa_open_task_row', task.id);
    } catch { /* noop */ }
    navigate('/projects');
  };
  const updateResps = async (newResps) => {
    if (!newResps.length) { toast.error('Au moins un responsable'); return; }
    try {
      await api.patch(`/asa-tasks/${task.id}`, { responsable: newResps });
      await load();
      onChanged?.();
      toast.success('Assignation mise à jour');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
    }
  };
  const toggleResp = (id) => {
    const s = new Set(resps);
    if (s.has(id)) s.delete(id); else s.add(id);
    updateResps(Array.from(s));
  };

  // Compute elapsed
  let elapsedSec = task.temps_passe || 0;
  if (task.chrono_demarre_a) {
    try {
      const dt = new Date(task.chrono_demarre_a);
      elapsedSec += Math.max(0, Math.floor((Date.now() - dt.getTime()) / 1000));
    } catch { /* noop */ }
  }
  // trigger re-render every tick when running
  void chronoTick;
  const fmtTime = (s) => {
    const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  };

  const setStatus = async (newStatus) => {
    if (newStatus === 'fini' && cannotValidate) { toast.error('Un admin doit valider ta tâche'); return; }
    const payload = await deliveryPayload(task, newStatus, currentUserId);
    if (!payload) return;
    setBusy(true);
    try { await api.patch(`/asa-tasks/${task.id}`, payload); if (payload.delivery) toast.success(deliveryToast(payload)); await load(); onChanged?.(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Erreur'); }
    setBusy(false);
  };

  // Iter 62ex — Assigner / retirer le lot de priorité (P1/P2/P3)
  const setBucket = async (bucket) => {
    setBusy(true);
    try {
      const nextBucket = task.priority_bucket === bucket ? null : bucket;
      await api.patch(`/asa-tasks/${task.id}`, { priority_bucket: nextBucket, priority_bucket_day: nextBucket ? isoLocal() : undefined });
      await load(); onChanged?.();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erreur'); }
    setBusy(false);
  };

  // Iter 62fd — Marquer / démarquer la "prochaine tâche" (max une par user)
  const toggleNext = async () => {
    setBusy(true);
    try {
      const nextVal = !task.is_next;
      await api.patch(`/asa-tasks/${task.id}`, { is_next: nextVal });
      await load(); onChanged?.();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erreur'); }
    setBusy(false);
  };

  const toggleChecklist = async (idx) => {
    // Iter 62dd — checklist retirée du render mais handler gardé pour compat (no-op si absent)
    if (!task.checklist) return;
    const cl = task.checklist.map((c, i) => i === idx ? { ...c, fait: !c.fait } : c);
    await api.patch(`/asa-tasks/${task.id}`, { checklist: cl });
    load(); onChanged?.();
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    await api.post(`/asa-tasks/${task.id}/comments`, { texte: comment.trim() });
    setComment(''); load();
  };

  const pauseResume = async () => {
    if (task.chrono_demarre_a) {
      await api.post(`/asa-tasks/${task.id}/chrono/pause`);
    } else if (task.status === 'en_cours') {
      await api.post(`/asa-tasks/${task.id}/chrono/resume`);
    } else {
      // Set to en_cours first
      await api.patch(`/asa-tasks/${task.id}`, { status: 'en_cours' });
    }
    load(); onChanged?.();
  };

  const deleteTask = async () => {
    if (!window.confirm('Supprimer cette tâche ?')) return;
    await api.delete(`/asa-tasks/${task.id}`);
    toast.success('Tâche supprimée');
    onChanged?.(); onClose();
  };

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(6,12,26,0.55)', backdropFilter: 'blur(4px)', zIndex: 100 }} />
      <div
        data-testid="task-detail-panel"
        className="scale-in"
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'fixed', top: 0, right: 0,
          height: '100vh', width: isMobile ? '100vw' : 'min(560px, 100vw)',
          zIndex: 101, background: NAVY,
          borderLeft: isMobile ? 'none' : `1px solid ${BORDER}`,
          overflowY: 'auto', padding: isMobile ? '18px 16px 100px' : 26,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
          <span style={{ padding: '4px 10px', borderRadius: 999, background: stat.bg, border: `1px solid ${stat.color}55`, color: stat.color, fontFamily: 'Sora', fontWeight: 700, fontSize: 11 }}>{stat.label}</span>
          <span style={{ padding: '4px 10px', borderRadius: 999, background: `${prio.color}22`, border: `1px solid ${prio.color}55`, color: prio.color, fontFamily: 'Sora', fontWeight: 700, fontSize: 11 }}>{prio.label}</span>
          {task.projet && (
            projectExists ? (
              <button
                type="button"
                data-testid="detail-open-project"
                onClick={openProject}
                style={{ padding: '4px 10px', borderRadius: 999, background: 'rgba(62,123,250,0.14)', border: '1px solid rgba(62,123,250,0.35)', color: BLUE_LIGHT, fontFamily: 'Sora', fontWeight: 700, fontSize: 11, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }}
                title="Ouvrir le projet"
              >{projectLabel(task.projet, projects) || task.project_name || 'Projet'} <ExternalLink size={11} strokeWidth={2.6} /></button>
            ) : (
              <span style={{ padding: '4px 10px', borderRadius: 999, background: SURFACE2, border: `1px solid ${BORDER}`, color: T2, fontFamily: 'Sora', fontWeight: 600, fontSize: 11 }}>{projectLabel(task.projet, projects) || task.project_name || 'Projet'}</span>
            )
          )}
          {/* Iter 62dh — plus de doublon "Vidéo · Projet". Un badge unique "One-shot" pour les vidéos sans projet, sinon rien. */}
          {task.est_video && !task.projet && (
            <span style={{ padding: '4px 10px', borderRadius: 999, background: SURFACE2, border: `1px solid ${BORDER}`, color: T3, fontFamily: 'Sora', fontWeight: 600, fontSize: 10.5, letterSpacing: '0.04em', textTransform: 'uppercase' }}>Vidéo · One-shot</span>
          )}
          <div style={{ flex: 1 }} />
          <button data-testid="detail-close" onClick={onClose} style={{ width: 30, height: 30, borderRadius: 8, background: 'transparent', border: 'none', color: T3, cursor: 'pointer', display: 'grid', placeItems: 'center' }}><X size={16} /></button>
        </div>
        <h2 style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 22, color: T1, letterSpacing: '-0.015em', margin: '0 0 10px' }}>{task.title}</h2>
        {task.description && !isPosterTask(task) && (
          <p style={{ fontFamily: 'Sora', fontWeight: 500, fontSize: 13.5, color: T2, letterSpacing: '-0.005em', margin: '0 0 22px', whiteSpace: 'pre-wrap' }}>{task.description}</p>
        )}
        {isPosterTask(task) ? (
          <PosterMinimalBody
            task={task}
            busy={busy}
            canReassign={ADMIN_IDS.has(currentUserId)}
            canSelfValidate={!isOwn || ADMIN_IDS.has(currentUserId) || SELF_VALIDATOR_IDS.has(currentUserId)}
            setStatus={setStatus}
            onShifted={() => { load(); onChanged?.(); }}
          />
        ) : (<>
        {/* Meta */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 22 }}>
          <div style={{ padding: 12, borderRadius: 12, background: SURFACE, border: `1px solid ${BORDER}`, position: 'relative' }}>
            <div style={{ fontFamily: 'Sora', fontWeight: 600, fontSize: 10.5, color: T3, letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 6 }}>Assigné à</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
              {members.length > 0 ? members.map((m) => (
                <span key={m.id} data-testid={`detail-resp-${m.id}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 9px 3px 3px', borderRadius: 999, background: SURFACE2, border: `1px solid ${BORDER}` }}>
                  <span style={{ width: 20, height: 20, borderRadius: 999, background: `linear-gradient(180deg, ${BLUE_LIGHT} 0%, ${BLUE} 100%)`, color: NAVY, fontFamily: 'Sora', fontWeight: 800, fontSize: 10, display: 'grid', placeItems: 'center' }}>{m.initial}</span>
                  <span style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 11.5, color: T1 }}>{m.name}</span>
                </span>
              )) : <span style={{ fontFamily: 'Sora', fontSize: 12, color: T3 }}>—</span>}
              <button
                type="button"
                data-testid="detail-resp-add"
                onClick={() => setRespPickerOpen((o) => !o)}
                style={{ width: 22, height: 22, borderRadius: 999, background: 'rgba(62,123,250,0.14)', border: '1px solid rgba(62,123,250,0.35)', color: BLUE_LIGHT, cursor: 'pointer', display: 'grid', placeItems: 'center' }}
                title="Ajouter/retirer un responsable"
              ><Plus size={12} strokeWidth={2.6} /></button>
            </div>
            {respPickerOpen && (
              <div data-testid="detail-resp-picker" style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 6, padding: 8, borderRadius: 12, background: NAVY, border: `1px solid ${BORDER}`, boxShadow: '0 20px 40px -10px rgba(0,0,0,0.7)', zIndex: 5 }}>
                {ALL_TEAM.map((m) => {
                  const active = resps.includes(m.id);
                  return (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => toggleResp(m.id)}
                      style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 8, border: 'none', background: active ? 'rgba(62,123,250,0.14)' : 'transparent', color: T1, fontFamily: 'Sora', fontWeight: 600, fontSize: 12, cursor: 'pointer', marginBottom: 2 }}
                    >
                      <span style={{ width: 20, height: 20, borderRadius: 999, background: `linear-gradient(180deg, ${BLUE_LIGHT} 0%, ${BLUE} 100%)`, color: NAVY, fontFamily: 'Sora', fontWeight: 800, fontSize: 10, display: 'grid', placeItems: 'center' }}>{m.initial}</span>
                      <span style={{ flex: 1, textAlign: 'left' }}>{m.name}</span>
                      {active && <span style={{ color: BLUE_LIGHT, fontWeight: 800 }}>✓</span>}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <MetaTile label="Période" value={(() => {
            if (!task.date_debut) return 'Sans date';
            const dateStr = task.date_debut === task.date_fin ? task.date_debut : `${task.date_debut} → ${task.date_fin}`;
            const hd = task.heure_debut, hf = task.heure_fin;
            if (hd && hf) return `${dateStr} · ${hd} → ${hf}`;
            if (hd) return `${dateStr} · dès ${hd}`;
            if (hf) return `${dateStr} · deadline ${hf}`;
            return dateStr;
          })()} />
        </div>

        {/* Chrono */}
        <div style={{ padding: 14, borderRadius: 12, background: SURFACE, border: `1px solid ${BORDER}`, marginBottom: 22 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: 'Sora', fontWeight: 600, fontSize: 11, color: T3, letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 4 }}>Temps passé</div>
              <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 24, color: T1, letterSpacing: '-0.02em' }}>{fmtTime(elapsedSec)}</div>
            </div>
            <button
              data-testid="chrono-toggle"
              onClick={pauseResume}
              style={{
                width: 44, height: 44, borderRadius: 12,
                background: task.chrono_demarre_a ? 'rgba(245,196,106,0.14)' : `linear-gradient(180deg, ${BLUE_LIGHT} 0%, ${BLUE} 100%)`,
                border: task.chrono_demarre_a ? '1px solid rgba(245,196,106,0.4)' : 'none',
                color: task.chrono_demarre_a ? '#F5C46A' : NAVY,
                cursor: 'pointer', display: 'grid', placeItems: 'center',
              }}
              title={task.chrono_demarre_a ? 'Pause' : 'Reprendre'}
            >{task.chrono_demarre_a ? <Pause size={16} /> : <Play size={16} />}</button>
          </div>
        </div>

        {/* Video block */}
        {task.post_meta && (
          <div data-testid="post-copy-section" style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
            <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 10.5, color: T3, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              {task.post_meta.description !== null ? 'À copier-coller pour poster' : 'Titre de la vidéo'}
            </div>
            <CopyField icon={Type} label="Titre" text={task.post_meta.title} testid="post-copy-title" />
            {task.post_meta.description !== null && (
              <CopyField icon={AlignLeft} label="Description" text={task.post_meta.description} testid="post-copy-desc" multiline />
            )}
          </div>
        )}
        {task.est_video && task.video_meta && (
          <div style={{ padding: 14, borderRadius: 12, background: SURFACE, border: `1px solid ${BORDER}`, marginBottom: 22 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
              <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 11.5, color: BLUE_LIGHT, letterSpacing: '0.04em', textTransform: 'uppercase' }}>Vidéo</div>
              {task.video_meta.format && (
                <span data-testid="detail-format" style={{ padding: '3px 10px', borderRadius: 999, background: 'rgba(167,139,250,0.14)', border: '1px solid rgba(167,139,250,0.35)', fontFamily: 'Sora', fontWeight: 800, fontSize: 10.5, color: '#B294F5', letterSpacing: '0.04em', textTransform: 'uppercase' }}>{task.video_meta.format}</span>
              )}
            </div>
            {task.video_meta.lien_rushs && <LinkRow label="Lien de la vidéo" href={task.video_meta.lien_rushs} />}
            {task.video_meta.dossier_travail && <LinkRow label="Lien de dépôt" href={task.video_meta.dossier_travail} />}
            {task.video_meta.instructions_montage && (
              <div style={{ marginTop: 8, fontFamily: 'Sora', fontWeight: 500, fontSize: 12.5, color: T2, whiteSpace: 'pre-wrap' }}><span style={{ color: T3 }}>Indication : </span>{task.video_meta.instructions_montage}</div>
            )}
          </div>
        )}

        {/* Iter 62eo — Wrap Comments + Documents dans un "Voir plus" collapsible mobile.
            Desktop : ouvert par défaut. Mobile : pillable pour compacter. */}
        {isMobile && (
          <button
            type="button"
            onClick={() => setShowExtras((v) => !v)}
            data-testid="detail-toggle-extras"
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              width: '100%', padding: '10px 12px', marginBottom: 14,
              borderRadius: 10, background: SURFACE2, border: `1px solid ${BORDER}`,
              color: T2, fontFamily: 'Sora', fontWeight: 700, fontSize: 12.5,
              cursor: 'pointer', letterSpacing: '-0.005em',
            }}
          >
            <ChevronRight size={14} style={{
              transform: showExtras ? 'rotate(90deg)' : 'rotate(0deg)',
              transition: 'transform 0.15s var(--ease-out)',
            }} />
            <span style={{ flex: 1, textAlign: 'left' }}>
              {showExtras ? 'Masquer les détails' : 'Voir plus'}
            </span>
            <span style={{ fontSize: 10.5, color: T3, fontWeight: 500 }}>
              {(task.comments || []).length} comm · {documents.length} docs
            </span>
          </button>
        )}
        {showExtras && (
          <>
        {/* Comments */}
        <div style={{ marginBottom: 22 }}>
          <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 11.5, color: T3, letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 10 }}>Commentaires</div>
          {(task.comments || []).map((c) => (
            <div key={c.id} style={{ padding: '10px 12px', borderRadius: 10, background: SURFACE2, border: `1px solid ${BORDER}`, marginBottom: 8 }}>
              <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 12, color: T1 }}>{c.auteur_nom}</div>
              <div style={{ fontFamily: 'Sora', fontWeight: 500, fontSize: 12.5, color: T2, marginTop: 3, whiteSpace: 'pre-wrap' }}>{c.texte}</div>
            </div>
          ))}
          <form onSubmit={submitComment} style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Ajouter un commentaire…" style={{ flex: 1, padding: '9px 12px', borderRadius: 10, background: SURFACE2, border: `1px solid ${BORDER}`, color: T1, outline: 'none', fontFamily: 'Sora', fontSize: 12.5 }} />
            <button type="submit" data-testid="add-comment" style={{ padding: '9px 14px', borderRadius: 10, background: BLUE, border: 'none', color: '#fff', fontFamily: 'Sora', fontWeight: 700, fontSize: 12.5, cursor: 'pointer' }}>Poster</button>
          </form>
        </div>

        {/* Documents (Iter 62ds) — Upload/list/download/delete réels */}
        <DocumentsSection
          taskId={task.id}
          documents={documents}
          busy={uploadBusy}
          setBusy={setUploadBusy}
          onChanged={loadDocuments}
        />
          </>
        )}

        {/* History (Iter 62dg — pliable) */}
        <HistorySection historique={task.historique || []} />

        {/* Iter 62fd — Toggle « Prochaine tâche » (max une par user) */}
        <button
          data-testid="detail-toggle-next"
          onClick={toggleNext}
          disabled={busy}
          style={{
            width: '100%', padding: '10px 14px', borderRadius: 12,
            marginBottom: 14,
            background: task.is_next
              ? 'linear-gradient(135deg, rgba(91,190,252,0.20) 0%, rgba(167,139,250,0.20) 100%)'
              : SURFACE2,
            border: task.is_next ? '1.5px solid rgba(167,139,250,0.6)' : `1.5px solid ${BORDER}`,
            color: task.is_next ? '#B294F5' : T2,
            fontFamily: 'Sora', fontWeight: 700, fontSize: 12.5,
            letterSpacing: '-0.005em', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            boxShadow: task.is_next ? '0 6px 18px -6px rgba(167,139,250,0.5)' : 'none',
            transition: 'all 0.15s var(--ease-out)',
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 16 }}>🔜</span>
            <span>{task.is_next ? 'C\'est ma prochaine tâche' : 'Marquer comme prochaine tâche'}</span>
          </span>
          <span style={{
            width: 20, height: 20, borderRadius: 6,
            background: task.is_next ? '#B294F5' : 'transparent',
            border: `1.5px solid ${task.is_next ? '#B294F5' : BORDER}`,
            display: 'grid', placeItems: 'center',
          }}>
            {task.is_next && <Check size={12} style={{ color: NAVY }} strokeWidth={3} />}
          </span>
        </button>

        {!isPosterTask(task) && <TaskTypePicker value={task.task_type || effectiveType(task)} disabled={busy} onPick={async (v) => {
          setBusy(true);
          try { await api.patch(`/asa-tasks/${task.id}`, { task_type: v }); await load(); onChanged?.(); }
          catch (err) { toast.error(err?.response?.data?.detail || 'Erreur'); }
          setBusy(false);
        }} />}
        {/* Iter 62ex/ey/ez — Sélecteur de N°1..N°7 (P4-P7 masqués sauf demande) */}
        <div style={{
          fontFamily: 'Sora', fontWeight: 800, fontSize: 11,
          color: T2, textTransform: 'uppercase', letterSpacing: '0.08em',
          marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6,
        }}>Ordre du jour <span style={{ fontWeight: 500, fontSize: 10, color: T3, textTransform: 'none', letterSpacing: 0 }}>· automatique (ordre de création, repart à 1 chaque jour) — choisis un n° pour le changer aujourd'hui</span></div>
        {(() => {
          const ALL_BUCKETS = [
            { id: 1, label: '1', bg: 'linear-gradient(135deg, #FF4D4D 0%, #FF7A3B 100%)', txt: '#FFF', shadow: '0 6px 18px -6px rgba(255,77,77,0.55), inset 0 1px 0 rgba(255,255,255,0.32)' },
            { id: 2, label: '2', bg: 'linear-gradient(135deg, #F5A623 0%, #F5C46A 100%)', txt: '#0B1330', shadow: '0 6px 18px -6px rgba(245,166,35,0.55), inset 0 1px 0 rgba(255,255,255,0.4)' },
            { id: 3, label: '3', bg: 'linear-gradient(135deg, #5BBEFC 0%, #A78BFA 100%)', txt: '#0B1330', shadow: '0 6px 18px -6px rgba(91,190,252,0.55), inset 0 1px 0 rgba(255,255,255,0.4)' },
            { id: 4, label: '4', bg: 'linear-gradient(135deg, #22D3B6 0%, #7FDDB8 100%)', txt: '#0B1330', shadow: '0 6px 18px -6px rgba(34,211,182,0.55), inset 0 1px 0 rgba(255,255,255,0.4)' },
            { id: 5, label: '5', bg: 'linear-gradient(135deg, #F472B6 0%, #FB7185 100%)', txt: '#FFF', shadow: '0 6px 18px -6px rgba(244,114,182,0.55), inset 0 1px 0 rgba(255,255,255,0.32)' },
            { id: 6, label: '6', bg: 'linear-gradient(135deg, #06B6D4 0%, #67E8F9 100%)', txt: '#0B1330', shadow: '0 6px 18px -6px rgba(6,182,212,0.55), inset 0 1px 0 rgba(255,255,255,0.4)' },
            { id: 7, label: '7', bg: 'linear-gradient(135deg, #64748B 0%, #94A3B8 100%)', txt: '#FFF', shadow: '0 6px 18px -6px rgba(100,116,139,0.55), inset 0 1px 0 rgba(255,255,255,0.28)' },
          ];
          const visible = showMoreBuckets ? ALL_BUCKETS : ALL_BUCKETS.slice(0, 3);
          return (
            <>
              <div data-testid="detail-bucket-pills" style={{ display: 'grid', gridTemplateColumns: `repeat(${visible.length + 1}, minmax(0, 1fr))`, gap: 6, marginBottom: showMoreBuckets ? 8 : 14 }}>
                {visible.map((b) => {
                  const active = dayBucket(task, isoLocal()) === b.id;
                  return (
                    <motion.button
                      key={b.id}
                      layout
                      whileTap={{ scale: 0.94 }}
                      transition={{ type: 'spring', stiffness: 500, damping: 26 }}
                      data-testid={`detail-bucket-${b.id}`}
                      onClick={() => setBucket(b.id)}
                      disabled={busy}
                      style={{
                        display: 'grid', placeItems: 'center',
                        padding: '11px 4px', borderRadius: 11,
                        background: active ? b.bg : `${SURFACE2}`,
                        border: active ? 'none' : `1.5px solid ${BORDER}`,
                        color: active ? b.txt : T2,
                        fontFamily: 'Sora', fontWeight: 900, fontSize: 14,
                        letterSpacing: '-0.005em',
                        cursor: 'pointer', minHeight: 46,
                        boxShadow: active ? b.shadow : 'none',
                        transform: active ? 'scale(1.02)' : 'scale(1)',
                        transition: 'background 0.2s var(--ease-out), box-shadow 0.2s var(--ease-out), color 0.2s var(--ease-out)',
                      }}
                      title={`Tâche n°${b.label}`}
                    >{b.label}</motion.button>
                  );
                })}
                <motion.button
                  layout
                  whileTap={{ scale: 0.94 }}
                  data-testid="detail-bucket-clear"
                  onClick={() => setBucket(null)}
                  disabled={busy || !task.priority_bucket}
                  style={{
                    display: 'grid', placeItems: 'center',
                    padding: '11px 4px', borderRadius: 11,
                    background: 'transparent',
                    border: `1.5px dashed ${BORDER}`,
                    color: T3,
                    fontFamily: 'Sora', fontWeight: 700, fontSize: 11,
                    cursor: task.priority_bucket ? 'pointer' : 'default',
                    opacity: task.priority_bucket ? 1 : 0.4,
                    minHeight: 46,
                  }}
                  title="Retirer l'ordre de priorité"
                >Aucun</motion.button>
              </div>
              <button
                type="button"
                data-testid="detail-bucket-more"
                onClick={() => setShowMoreBuckets((v) => !v)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '4px 10px', borderRadius: 8,
                  background: 'transparent', border: 'none',
                  color: T3, fontFamily: 'Sora', fontWeight: 600, fontSize: 10.5,
                  letterSpacing: '-0.005em', cursor: 'pointer',
                  marginBottom: 14,
                }}
              >{showMoreBuckets ? '− Masquer n°4-n°7' : '+ Ajouter n°4 à n°7 (si besoin)'}</button>
            </>
          );
        })()}

        {/* Actions */}
        {/* Iter 62eq — Les 5 pills deviennent l'action PRIMAIRE du panel :
            header explicite, pills grandes et évidentes → un tap = statut changé
            (avant/arrière, peu importe l'ordre). Le bouton "Valider" en gros
            n'apparaît QUE quand la tâche est déjà "à valider" (contexte adapté). */}
        <div style={{
          fontFamily: 'Sora', fontWeight: 800, fontSize: 11,
          color: T2, textTransform: 'uppercase', letterSpacing: '0.08em',
          marginBottom: 8,
        }}>Changer le statut</div>
        <div
          data-testid="detail-status-pills"
          style={{
            display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
            gap: 6, marginBottom: 14,
          }}
        >
          {Object.entries(STATUS).map(([id, s]) => {
            const active = task.status === id;
            const disabled = busy || (id === 'fini' && cannotValidate);
            return (
              <button
                key={id}
                data-testid={`detail-status-pill-${id}`}
                onClick={() => !disabled && !active && setStatus(id)}
                disabled={disabled || active}
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  gap: 5, padding: '10px 4px', borderRadius: 12,
                  background: active
                    ? `linear-gradient(180deg, ${s.color}33 0%, ${s.color}18 100%)`
                    : `${s.color}0D`,
                  border: `1.5px solid ${active ? s.color : `${s.color}44`}`,
                  color: active ? s.color : s.color,
                  fontFamily: 'Sora', fontWeight: 800, fontSize: 11,
                  letterSpacing: '-0.005em',
                  cursor: (disabled || active) ? 'default' : 'pointer',
                  opacity: disabled ? 0.35 : 1,
                  transition: 'all 0.15s var(--ease-out)',
                  minHeight: 64,
                  boxShadow: active ? `0 6px 20px -8px ${s.color}88, inset 0 1px 0 ${s.color}22` : 'none',
                  transform: active ? 'scale(1.02)' : 'scale(1)',
                }}
              >
                <span style={{
                  width: 8, height: 8, borderRadius: 999, background: s.color,
                  boxShadow: active ? `0 0 10px ${s.color}` : 'none',
                }} />
                <span style={{ fontSize: 10.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%', lineHeight: 1.1 }}>
                  {s.label}
                </span>
                {active && (
                  <span style={{ fontSize: 8.5, fontWeight: 700, opacity: 0.7, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Actuel</span>
                )}
              </button>
            );
          })}
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {task.status === 'a_valider' && (
            <button
              data-testid="detail-validate"
              onClick={() => setStatus('fini')}
              disabled={busy || cannotValidate}
              style={{
                flex: 1,
                padding: '11px 18px', borderRadius: 12,
                background: cannotValidate ? 'rgba(245,196,106,0.14)' : `linear-gradient(180deg, ${BLUE_LIGHT} 0%, ${BLUE} 100%)`,
                border: cannotValidate ? '1px solid rgba(245,196,106,0.4)' : 'none',
                color: cannotValidate ? '#F5C46A' : NAVY,
                fontFamily: 'Sora', fontWeight: 800, fontSize: 12.5,
                cursor: cannotValidate ? 'not-allowed' : 'pointer',
                boxShadow: cannotValidate ? 'none' : '0 8px 22px -6px rgba(62,123,250,0.55), inset 0 1px 0 rgba(255,255,255,0.28)',
              }}
            >{cannotValidate ? '🔒 Attente admin' : '✓ Valider la tâche'}</button>
          )}
          <button data-testid="detail-edit" onClick={() => setEditing(true)} style={{ flex: task.status === 'a_valider' ? 'none' : 1, width: task.status === 'a_valider' ? 44 : 'auto', height: 44, borderRadius: 12, background: 'rgba(91,190,252,0.10)', border: '1px solid rgba(91,190,252,0.32)', color: BLUE_LIGHT, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: task.status === 'a_valider' ? 0 : '0 14px', fontFamily: 'Sora', fontWeight: 700, fontSize: 12.5 }} title="Modifier la tâche"><Edit2 size={15} />{task.status !== 'a_valider' && <span>Modifier</span>}</button>
          <button data-testid="detail-delete" onClick={deleteTask} style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(255,107,107,0.10)', border: '1px solid rgba(255,107,107,0.32)', color: '#FF7B7B', cursor: 'pointer', display: 'grid', placeItems: 'center', flexShrink: 0 }} title="Supprimer la tâche"><Trash2 size={15} /></button>
        </div>
        </>)}
      </div>
      {editing && (
        <TaskEditDialog
          task={task}
          isMyView={isMyView}
          projects={projects}
          onClose={() => setEditing(false)}
          onSaved={async () => { setEditing(false); await load(); onChanged?.(); toast.success('Tâche mise à jour'); }}
        />
      )}
    </>
  );
}
function MetaTile({ label, value }) {
  return (
    <div style={{ padding: 12, borderRadius: 12, background: SURFACE, border: `1px solid ${BORDER}` }}>
      <div style={{ fontFamily: 'Sora', fontWeight: 600, fontSize: 10.5, color: T3, letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 4 }}>{label}</div>
      <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 13, color: T1 }}>{value}</div>
    </div>
  );
}

function HistorySection({ historique }) {
  const [open, setOpen] = useState(false);
  const count = historique.length;
  return (
    <div style={{ marginBottom: 22 }}>
      <button
        data-testid="history-toggle"
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 8,
          padding: '10px 12px', borderRadius: 10,
          background: SURFACE2, border: `1px solid ${BORDER}`,
          color: T2, cursor: 'pointer',
        }}
      >
        <span style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 11.5, color: T3, letterSpacing: '0.04em', textTransform: 'uppercase' }}>Historique</span>
        <span style={{ fontFamily: 'Sora', fontWeight: 600, fontSize: 11, color: T3 }}>{count}</span>
        <div style={{ flex: 1 }} />
        <span style={{ transform: open ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s var(--ease-out)', display: 'inline-flex' }}><ChevronRight size={14} color={T3} /></span>
      </button>
      {open && (
        <div style={{ marginTop: 8, paddingTop: 4 }}>
          {historique.slice().reverse().map((h) => (
            <div key={h.id} style={{ display: 'flex', gap: 10, padding: '5px 4px', fontFamily: 'Sora', fontWeight: 500, fontSize: 11.5, color: T3 }}>
              <span style={{ width: 4, height: 4, borderRadius: 999, background: BLUE, marginTop: 8, flexShrink: 0 }} />
              <div style={{ flex: 1 }}>{h.action}</div>
              <div>{new Date(h.date).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
function LinkRow({ label, href }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      title={href}
      style={{
        display: 'block',
        padding: '5px 0',
        fontFamily: 'Sora', fontWeight: 600, fontSize: 12.5, color: BLUE_LIGHT,
        textDecoration: 'none',
        // Iter 62eo — Fix débordement des URLs longs (Frame.io, Drive avec query params).
        // wordBreak break-all coupe même au milieu d'un mot, overflowWrap anywhere
        // gère les chaînes sans espaces. maxWidth 100% + overflow hidden sur le
        // conteneur parent en amont. On limite aussi la hauteur à 2 lignes avec
        // ellipsis pour éviter les liens géants qui pushent le panel.
        maxWidth: '100%', overflow: 'hidden',
        wordBreak: 'break-all', overflowWrap: 'anywhere',
        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
        textOverflow: 'ellipsis',
      }}
    >
      <span style={{ color: T3 }}>{label} : </span>{href}
    </a>
  );
}

// ─── Create dialog ───────────────────────────────────────────────────────────
// Iter 62dj — 3 types simples : Montage, Réunion, Documents.
const TEMPLATES = [
  { id: 'montage',   label: 'Montage',   priority: 'important', est_video: true,  instructions: 'Cut serré, punchy, hook first 3s.' },
  { id: 'reunion',   label: 'Réunion',   priority: 'normal',    est_video: false, instructions: '' },
  { id: 'documents', label: 'Documents', priority: 'normal',    est_video: false, instructions: '' },
];

function TaskTypePicker({ value, onPick, disabled }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 11, color: T2, textTransform: 'uppercase', letterSpacing: '0.08em', margin: '8px 0 6px' }}>Type de tâche</div>
      <div data-testid="task-type-picker" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {Object.entries(TASK_TYPES).map(([id, t]) => {
          const on = value === id;
          return (
            <button key={id} type="button" disabled={disabled} data-testid={`task-type-${id}`} onClick={() => onPick(id)} style={{ padding: '7px 13px', borderRadius: 10, cursor: 'pointer', fontFamily: 'Sora', fontWeight: 700, fontSize: 12, background: on ? `${t.color}22` : 'transparent', border: `1px solid ${on ? t.color : BORDER}`, color: on ? t.color : T2, transition: 'background-color 0.15s, border-color 0.15s' }}>{t.label}</button>
          );
        })}
      </div>
    </div>
  );
}

function TaskCreateDialog({ isMyView, fixedResponsable, defaultDay, defaultHeureDebut, projects, onClose, onSaved }) {
  useTeamVersion();
  const isMobile = useIsMobile();
  // Iter 62ff — Mode "Rapide" vs "Complète". Rapide = juste titre + détails + date + heure.
  // Complète = formulaire actuel avec tout (priorité, multi-assign, vidéo, etc.).
  const [mode, setMode] = useState('quick');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  // Iter 62dh — multi-assign
  const [responsables, setResponsables] = useState(() => fixedResponsable ? [fixedResponsable] : [ALL_TEAM[0]?.id || 'rayan']);
  const [priority, setPriority] = useState('normal');
  const [taskType, setTaskType] = useState(null);
  const [dateDebut, setDateDebut] = useState(defaultDay || isoLocal());
  const [multiJours, setMultiJours] = useState(false);
  const [noDate, setNoDate] = useState(false);
  const [dateFin, setDateFin] = useState(defaultDay || isoLocal());
  // Iter 62dj — heures optionnelles (HH:MM) pour reminders
  // Iter 62fe — préremplir si fournie depuis la vue horaire
  const [heureDebut, setHeureDebut] = useState(defaultHeureDebut || '');
  const [heureFin, setHeureFin] = useState('');
  const [estVideo, setEstVideo] = useState(false);
  const [videoMeta, setVideoMeta] = useState({ lien_rushs: '', dossier_travail: '', instructions_montage: '', format: '' });
  const [notes, setNotes] = useState('');
  const [detailsOpen, setDetailsOpen] = useState(false);
  // Iter 62eo — "En savoir plus" collapsible pour compacter la modale mobile.
  // Regroupe : multi-jours + tâche vidéo (+ champs vidéo). Ouvert par défaut si on
  // édite une tâche déjà multi-jours ou vidéo.
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [busy, setBusy] = useState(false);

  const toggleResp = (id) => {
    setResponsables((prev) => prev.includes(id) ? (prev.length === 1 ? prev : prev.filter((x) => x !== id)) : [...prev, id]);
  };

  const applyTemplate = (tpl) => {
    if (!tpl) return;
    setTitle(tpl.label);
    setPriority(tpl.priority);
    setEstVideo(tpl.est_video);
    setVideoMeta((v) => ({ ...v, instructions_montage: tpl.instructions }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { toast.error('Titre requis'); return; }
    if (!responsables.length) { toast.error('Au moins un responsable'); return; }
    setBusy(true);
    try {
      await api.post('/asa-tasks', {
        title: title.trim(),
        description: description.trim(),
        responsable: responsables,
        priority,
        task_type: taskType,
        date_debut: noDate ? null : dateDebut,
        date_fin: noDate ? null : (multiJours ? dateFin : dateDebut),
        heure_debut: noDate ? null : (heureDebut || null),
        heure_fin: noDate ? null : (heureFin || null),
        projet: null,
        notes: notes.trim(),
        est_video: estVideo,
        video_meta: estVideo ? videoMeta : null,
      });
      toast.success('Tâche créée');
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
    }
    setBusy(false);
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 110, background: 'rgba(6,12,26,0.72)', backdropFilter: 'blur(10px)', display: 'grid', placeItems: isMobile ? 'stretch' : 'center', padding: isMobile ? 0 : 18, overflow: 'auto' }}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="scale-in" style={{ width: '100%', maxWidth: isMobile ? '100%' : 560, background: isMobile ? NAVY : `linear-gradient(180deg, ${SURFACE} 0%, rgba(14,22,44,0.98) 100%)`, borderRadius: isMobile ? 0 : 22, border: isMobile ? 'none' : `1px solid ${BORDER}`, boxShadow: isMobile ? 'none' : '0 40px 80px -20px rgba(0,0,0,0.7)', padding: isMobile ? '16px 14px 24px' : 28, maxHeight: isMobile ? '100vh' : '90vh', minHeight: isMobile ? '100vh' : 'auto', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: isMobile ? 10 : 14 }}>
          <h2 style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: isMobile ? 16 : 20, color: T1, margin: 0, flex: 1, letterSpacing: '-0.01em' }}>Nouvelle tâche</h2>
          <button type="button" onClick={onClose} style={{ background: SURFACE2, border: `1px solid ${BORDER}`, color: T1, cursor: 'pointer', width: 32, height: 32, borderRadius: 10, display: 'grid', placeItems: 'center' }}><X size={15} /></button>
        </div>
        {/* Iter 62ff — Switch Rapide / Complète (c'est le 1er choix que fait l'user) */}
        <div data-testid="cr-mode-switch" style={{ display: 'inline-flex', width: '100%', gap: 2, padding: 3, borderRadius: 12, background: SURFACE2, border: `1px solid ${BORDER}`, marginBottom: isMobile ? 10 : 14 }}>
          {[
            { id: 'quick', label: '⚡ Tâche rapide', hint: 'Titre + heure, go' },
            { id: 'full',  label: '🧩 Tâche complète', hint: 'Priorité, vidéo, …' },
          ].map((m) => {
            const active = mode === m.id;
            return (
              <button
                key={m.id}
                type="button"
                data-testid={`cr-mode-${m.id}`}
                onClick={() => setMode(m.id)}
                style={{
                  flex: 1, padding: isMobile ? '8px 10px' : '10px 14px', borderRadius: 10, border: 'none',
                  background: active ? `linear-gradient(180deg, ${BLUE_LIGHT} 0%, ${BLUE} 100%)` : 'transparent',
                  color: active ? NAVY : T2,
                  fontFamily: 'Sora', fontWeight: 800, fontSize: isMobile ? 11.5 : 12.5,
                  cursor: 'pointer', letterSpacing: '-0.005em',
                  boxShadow: active ? '0 4px 12px -4px rgba(62,123,250,0.5), inset 0 1px 0 rgba(255,255,255,0.28)' : 'none',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
                }}
              >
                <span>{m.label}</span>
                <span style={{ fontSize: isMobile ? 9.5 : 10.5, fontWeight: 600, opacity: 0.85 }}>{m.hint}</span>
              </button>
            );
          })}
        </div>
        {/* Templates (seulement en mode complète) */}
        {mode === 'full' && (
        <div style={{ marginBottom: isMobile ? 10 : 18 }}>
          <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: isMobile ? 10 : 11, color: T3, letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: isMobile ? 5 : 8 }}>Type</div>
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
            {TEMPLATES.map((tpl) => (
              <button key={tpl.id} type="button" data-testid={`tpl-${tpl.id}`} onClick={() => applyTemplate(tpl)} style={{ padding: isMobile ? '5px 10px' : '7px 14px', borderRadius: isMobile ? 8 : 10, background: SURFACE2, border: `1px solid ${BORDER}`, color: T2, fontFamily: 'Sora', fontWeight: 700, fontSize: isMobile ? 11 : 12, cursor: 'pointer' }}>{tpl.label}</button>
            ))}
          </div>
        </div>
        )}

        <FieldLabel compact={isMobile}>Titre</FieldLabel>
        <input data-testid="cr-title" value={title} onChange={(e) => setTitle(e.target.value)} autoFocus placeholder="Ex. Montage épisode 4" style={isMobile ? inputStyleMobile : inputStyle} />

        <TaskTypePicker value={taskType} onPick={(v) => setTaskType((cur) => (cur === v ? null : v))} />

        <FieldLabel compact={isMobile}>Description</FieldLabel>
        <textarea data-testid="cr-desc" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Détail optionnel…" rows={2} style={{ ...(isMobile ? inputStyleMobile : inputStyle), resize: 'vertical', minHeight: isMobile ? 44 : 60 }} />

        {isMyView && mode === 'full' && (
          <>
            <FieldLabel compact={isMobile}>Assigné à <span style={{ color: T3, fontWeight: 500 }}>({responsables.length})</span></FieldLabel>
            <div data-testid="cr-resps" style={{ display: 'flex', flexWrap: 'wrap', gap: isMobile ? 4 : 6, marginBottom: isMobile ? 8 : 14 }}>
              {ALL_TEAM.map((m) => {
                const active = responsables.includes(m.id);
                return (
                  <button
                    key={m.id}
                    type="button"
                    data-testid={`cr-resp-${m.id}`}
                    onClick={() => toggleResp(m.id)}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: isMobile ? 4 : 6, padding: isMobile ? '3px 9px 3px 3px' : '5px 12px 5px 5px', borderRadius: 999, background: active ? 'rgba(62,123,250,0.16)' : SURFACE2, border: `1px solid ${active ? BLUE : BORDER}`, color: active ? T1 : T2, fontFamily: 'Sora', fontWeight: 700, fontSize: isMobile ? 10.5 : 11.5, cursor: 'pointer' }}
                  >
                    <span style={{ width: isMobile ? 18 : 22, height: isMobile ? 18 : 22, borderRadius: 999, background: `linear-gradient(180deg, ${BLUE_LIGHT} 0%, ${BLUE} 100%)`, color: NAVY, fontFamily: 'Sora', fontWeight: 800, fontSize: isMobile ? 9 : 10, display: 'grid', placeItems: 'center' }}>{m.initial}</span>
                    <span>{m.name}</span>
                    {active && <span style={{ color: BLUE_LIGHT, marginLeft: 2 }}>✓</span>}
                  </button>
                );
              })}
            </div>
          </>
        )}

        {mode === 'full' && (<>
        <FieldLabel compact={isMobile}>Priorité</FieldLabel>
        <div style={{ display: 'flex', gap: isMobile ? 4 : 6, flexWrap: 'wrap', marginBottom: isMobile ? 8 : 14 }}>
          {Object.entries(PRIORITY).map(([id, p]) => {
            const active = priority === id;
            return (
              <button key={id} type="button" data-testid={`cr-prio-${id}`} onClick={() => setPriority(id)} style={{ padding: isMobile ? '5px 11px' : '7px 14px', borderRadius: isMobile ? 8 : 10, background: active ? `${p.color}22` : 'transparent', border: `1px solid ${active ? p.color : BORDER}`, color: active ? p.color : T2, fontFamily: 'Sora', fontWeight: 700, fontSize: isMobile ? 11 : 12, cursor: 'pointer' }}>{p.label}</button>
            );
          })}
        </div>
        </>)}

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <FieldLabel compact={isMobile}>{mode === 'quick' ? 'Date & heure' : 'Deadline'}</FieldLabel>
          <button type="button" data-testid="cr-no-date-toggle" onClick={() => setNoDate((v) => !v)} style={{ padding: '4px 10px', borderRadius: 999, background: noDate ? 'rgba(245,197,106,0.16)' : 'transparent', border: `1px solid ${noDate ? '#F5C56A' : BORDER}`, color: noDate ? '#F5C56A' : T2, fontFamily: 'Sora', fontWeight: 700, fontSize: 11, cursor: 'pointer' }}>{noDate ? '✓ Sans date' : 'Sans date'}</button>
        </div>
        {noDate ? (
          <div data-testid="cr-no-date-info" style={{ fontFamily: 'Sora', fontSize: isMobile ? 10.5 : 11.5, color: '#F5C56A', margin: '4px 0 10px', lineHeight: 1.4 }}>
            Pas de date ni d'heure : la tâche reste dans ta journée chaque jour jusqu'à ce qu'elle soit finie, et part dans Google Tasks.
          </div>
        ) : (<>
        <div style={{ display: 'grid', gridTemplateColumns: mode === 'quick' ? '1fr 1fr' : '1fr 1fr 1fr', gap: isMobile ? 5 : 8, marginBottom: 4 }}>
          <input data-testid="cr-date" type="date" value={dateDebut} onChange={(e) => { setDateDebut(e.target.value); if (!multiJours) setDateFin(e.target.value); }} style={isMobile ? inputStyleMobile : inputStyle} />
          <input data-testid="cr-heure-debut" type="time" value={heureDebut} onChange={(e) => setHeureDebut(e.target.value)} placeholder="Heure" style={isMobile ? inputStyleMobile : inputStyle} title={mode === 'quick' ? 'Heure (optionnelle)' : 'Heure de début (optionnel)'} />
          {mode === 'full' && (
            <input data-testid="cr-heure-fin" type="time" value={heureFin} onChange={(e) => setHeureFin(e.target.value)} placeholder="Fin" style={isMobile ? inputStyleMobile : inputStyle} title="Heure de rendu (optionnel)" />
          )}
        </div>
        <div style={{ fontFamily: 'Sora', fontWeight: 500, fontSize: isMobile ? 9.5 : 10.5, color: T3, marginBottom: isMobile ? 6 : 10, letterSpacing: '-0.005em', lineHeight: 1.3 }}>
          {mode === 'quick' ? "Heure optionnelle. Si renseignée, elle apparaîtra dans la vue journalière." : "Renseigne une heure pour recevoir des rappels 1h, 30min et 10min avant."}
        </div>
        </>)}

        {/* Iter 62eo — Options avancées repliées par défaut (moins de boutons visibles). */}
        {mode === 'full' && (<>
        <button
          type="button"
          onClick={() => setShowAdvanced((v) => !v)}
          data-testid="cr-advanced-toggle"
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            width: '100%', padding: isMobile ? '7px 10px' : '10px 12px', marginTop: isMobile ? 4 : 6,
            borderRadius: 10, background: SURFACE2, border: `1px solid ${BORDER}`,
            color: T2, fontFamily: 'Sora', fontWeight: 700, fontSize: isMobile ? 11.5 : 12.5,
            cursor: 'pointer', letterSpacing: '-0.005em',
            transition: 'all 0.15s var(--ease-out)',
          }}
        >
          <ChevronRight size={13} style={{
            transform: showAdvanced ? 'rotate(90deg)' : 'rotate(0deg)',
            transition: 'transform 0.15s var(--ease-out)',
          }} />
          <span style={{ flex: 1, textAlign: 'left' }}>Options avancées</span>
          <span style={{ fontSize: isMobile ? 9.5 : 10.5, color: T3, fontWeight: 500 }}>
            multi-jours · vidéo
          </span>
        </button>

        {showAdvanced && (
          <div style={{ marginTop: isMobile ? 6 : 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: isMobile ? '7px 0 3px' : '10px 0 4px' }}>
          <input id="multi" data-testid="cr-multi" type="checkbox" checked={multiJours} onChange={(e) => setMultiJours(e.target.checked)} style={{ accentColor: BLUE }} />
          <label htmlFor="multi" style={{ fontFamily: 'Sora', fontWeight: 600, fontSize: isMobile ? 11.5 : 12.5, color: T2, cursor: 'pointer' }}>Sur plusieurs jours d&apos;affilée ?</label>
        </div>
        {multiJours && (
          <>
            <FieldLabel compact={isMobile}>Jusqu&apos;au</FieldLabel>
            <input data-testid="cr-date-end" type="date" value={dateFin} onChange={(e) => setDateFin(e.target.value)} style={isMobile ? inputStyleMobile : inputStyle} min={dateDebut} />
          </>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: isMobile ? '7px 0 3px' : '10px 0 4px' }}>
          <input id="video" data-testid="cr-video" type="checkbox" checked={estVideo} onChange={(e) => setEstVideo(e.target.checked)} style={{ accentColor: BLUE }} />
          <label htmlFor="video" style={{ fontFamily: 'Sora', fontWeight: 600, fontSize: isMobile ? 11.5 : 12.5, color: T2, cursor: 'pointer' }}>C&apos;est une tâche vidéo ?</label>
        </div>
        {estVideo && (
          <div style={{ padding: isMobile ? 9 : 12, borderRadius: 12, background: SURFACE2, border: `1px solid ${BORDER}`, marginTop: 4 }}>
            <input data-testid="cr-format" placeholder="Format (ex: décor jaune, podcast intro…)" value={videoMeta.format || ''} onChange={(e) => setVideoMeta({ ...videoMeta, format: e.target.value })} style={{ ...(isMobile ? inputStyleMobile : inputStyle), marginBottom: 5 }} />
            <input data-testid="cr-rushs" placeholder="Lien de la vidéo" value={videoMeta.lien_rushs} onChange={(e) => setVideoMeta({ ...videoMeta, lien_rushs: e.target.value })} style={{ ...(isMobile ? inputStyleMobile : inputStyle), marginBottom: 5 }} />
            <input data-testid="cr-dossier" placeholder="Lien de dépôt" value={videoMeta.dossier_travail} onChange={(e) => setVideoMeta({ ...videoMeta, dossier_travail: e.target.value })} style={{ ...(isMobile ? inputStyleMobile : inputStyle), marginBottom: 5 }} />
            <textarea data-testid="cr-instr" placeholder="Indication" value={videoMeta.instructions_montage} onChange={(e) => setVideoMeta({ ...videoMeta, instructions_montage: e.target.value })} rows={2} style={{ ...(isMobile ? inputStyleMobile : inputStyle), resize: 'vertical' }} />
          </div>
        )}
          </div>
        )}

        <button type="button" onClick={() => setDetailsOpen(!detailsOpen)} style={{ marginTop: isMobile ? 8 : 12, padding: '6px 0', background: 'transparent', border: 'none', color: T3, fontFamily: 'Sora', fontWeight: 600, fontSize: isMobile ? 11 : 12, cursor: 'pointer' }}>{detailsOpen ? '− Masquer' : '+ Ajouter plus de détails'}</button>
        {detailsOpen && (
          <textarea data-testid="cr-notes" placeholder="Notes / liens" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} style={{ ...(isMobile ? inputStyleMobile : inputStyle), resize: 'vertical' }} />
        )}
        </>)}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: isMobile ? 8 : 10, marginTop: isMobile ? 14 : 22, position: isMobile ? 'sticky' : 'static', bottom: isMobile ? 0 : 'auto', paddingTop: isMobile ? 10 : 0, background: isMobile ? `linear-gradient(180deg, transparent 0%, ${NAVY} 40%)` : 'transparent' }}>
          <button type="button" onClick={onClose} style={{ padding: isMobile ? '11px 16px' : '12px 18px', borderRadius: 12, background: 'transparent', border: `1px solid ${BORDER}`, color: T1, fontFamily: 'Sora', fontWeight: 700, fontSize: isMobile ? 12.5 : 13, cursor: 'pointer' }}>Annuler</button>
          <button type="submit" data-testid="cr-submit" disabled={busy} style={{ padding: isMobile ? '11px 16px' : '12px 18px', borderRadius: 12, background: `linear-gradient(180deg, ${BLUE_LIGHT} 0%, ${BLUE} 100%)`, border: 'none', color: NAVY, fontFamily: 'Sora', fontWeight: 800, fontSize: isMobile ? 12.5 : 13, cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.7 : 1, boxShadow: '0 8px 22px -6px rgba(62,123,250,0.55), inset 0 1px 0 rgba(255,255,255,0.28)' }}>{busy ? 'Création…' : (mode === 'quick' ? '⚡ Créer' : 'Créer la tâche')}</button>
        </div>
      </form>
    </div>
  );
}
function FieldLabel({ children, compact }) {
  return <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: compact ? 10 : 11.5, color: T3, letterSpacing: '-0.005em', marginBottom: compact ? 4 : 6, marginTop: compact ? 8 : 12, textTransform: compact ? 'uppercase' : 'none' }}>{children}</div>;
}
const inputStyle = {
  width: '100%',
  padding: '10px 12px', borderRadius: 10,
  background: SURFACE2, border: `1px solid ${BORDER}`,
  color: T1, outline: 'none',
  fontFamily: 'Sora', fontWeight: 500, fontSize: 13,
  letterSpacing: '-0.005em',
};
// Iter 62er — Variante compacte input pour la vue mobile (padding réduit, fontSize=16
// pour éviter le zoom automatique iOS Safari qui casse le layout).
const inputStyleMobile = {
  width: '100%',
  padding: '8px 10px', borderRadius: 9,
  background: SURFACE2, border: `1px solid ${BORDER}`,
  color: T1, outline: 'none',
  fontFamily: 'Sora', fontWeight: 500, fontSize: 16,
  letterSpacing: '-0.005em',
};

// ─── IA doc dialog (Iter 62dh — multi-assign preview + commit) ──────────────
// Iter 62dy — Rendu réutilisable via `commitFn` optionnel (utilisé par le
// module Projets pour créer directement des vidéos de projet plutôt que des
// asa_tasks standalone). Sans `commitFn`, fallback = commit asa_tasks classique.
export function IADocDialog({ defaultResponsable, onClose, onDone, title, subtitle, commitFn, commitLabel }) {
  const [step, setStep] = useState('compose'); // 'compose' | 'preview'
  const [text, setText] = useState('');
  // Iter 62dh — overrideResps: [] = Auto (l'IA détecte). Sinon list forcée.
  const [overrideResps, setOverrideResps] = useState([]);
  const [drafts, setDrafts] = useState([]); // {..., responsables: [id]}
  const [selected, setSelected] = useState(new Set()); // idx of tasks to commit
  const [busy, setBusy] = useState(false);
  // Iter 62fw — Mode "Séquence de posts" : chaque tâche créée sera marquée
  // comme poster_task (source='video_to_post') avec un libellé client libre.
  // Iter 62fx — Plus de lien forcé à un projet : étiquette client libre et réutilisable.
  const [isPostSequence, setIsPostSequence] = useState(false);
  const [clientLabel, setClientLabel] = useState('');
  const [existingLabels, setExistingLabels] = useState([]);
  useEffect(() => {
    api.get('/asa-tasks/client-labels').then((r) => {
      setExistingLabels(r.data?.labels || []);
    }).catch(() => {});
  }, []);

  const toggleOverride = (id) => {
    setOverrideResps((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const runPreview = async (e) => {
    e?.preventDefault?.();
    if (!text.trim() || text.trim().length < 20) {
      toast.error('Colle au moins 20 caractères');
      return;
    }
    setBusy(true);
    try {
      const r = await api.post('/asa-tasks/from-doc/preview', {
        text: text.trim(),
        default_responsable: defaultResponsable || 'rayan',
      });
      const list = r.data?.drafts || [];
      if (!list.length) { toast.error("L'IA n'a extrait aucune tâche"); setBusy(false); return; }
      // Apply override if user has forced any responsables
      const applied = list.map((d) => ({
        ...d,
        responsables: overrideResps.length ? [...overrideResps] : (d.responsables && d.responsables.length ? d.responsables : [defaultResponsable || 'rayan']),
      }));
      setDrafts(applied);
      setSelected(new Set(applied.map((_, i) => i)));
      setStep('preview');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur IA');
    }
    setBusy(false);
  };

  const commit = async () => {
    let tasks = drafts.filter((_, i) => selected.has(i));
    if (!tasks.length) { toast.error('Sélectionne au moins une tâche'); return; }
    // Iter 62fw/62fx — Si séquence de posts activée, marquer chaque task comme poster_task
    // avec l'étiquette client libre (plus de lien forcé à un projet).
    if (isPostSequence) {
      const label = (clientLabel || '').trim();
      if (!label) {
        toast.error("Renseigne une étiquette client (ex: Nash, PodMax, ou un nouveau nom)");
        return;
      }
      tasks = tasks.map((t) => ({
        ...t,
        source: 'video_to_post',
        client_label: label,
        est_video: true,
      }));
    }
    setBusy(true);
    try {
      let count = 0;
      if (commitFn) {
        count = await commitFn(tasks);
      } else {
        const r = await api.post('/asa-tasks/from-doc/commit', { tasks });
        count = r.data?.count || 0;
      }
      toast.success(`${count} ${commitLabel || 'tâche'}${count > 1 ? 's' : ''} créée${count > 1 ? 's' : ''}`);
      onDone?.(count);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || 'Erreur');
    }
    setBusy(false);
  };

  const toggleIdx = (idx) => {
    setSelected((prev) => {
      const s = new Set(prev);
      if (s.has(idx)) s.delete(idx); else s.add(idx);
      return s;
    });
  };
  const updateDraft = (idx, patch) => {
    setDrafts((prev) => prev.map((d, i) => i === idx ? { ...d, ...patch } : d));
  };
  const toggleDraftResp = (idx, memberId) => {
    setDrafts((prev) => prev.map((d, i) => {
      if (i !== idx) return d;
      const cur = d.responsables || [];
      if (cur.includes(memberId)) {
        return cur.length === 1 ? d : { ...d, responsables: cur.filter((x) => x !== memberId) };
      }
      return { ...d, responsables: [...cur, memberId] };
    }));
  };

  // Iter 62ex — Réordonner manuellement les drafts IA (Rayan msg :
  // "je voudrais qu'on puisse rechanger l'ordre si l'IA a mal fait l'ordre")
  const moveDraft = (idx, dir) => {
    setDrafts((prev) => {
      const nextIdx = idx + dir;
      if (nextIdx < 0 || nextIdx >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[nextIdx]] = [next[nextIdx], next[idx]];
      return next;
    });
    // Move la sélection avec la row (via son idx)
    setSelected((prev) => {
      const nextIdx = idx + dir;
      if (nextIdx < 0 || nextIdx >= drafts.length) return prev;
      const s = new Set(prev);
      const hadIdx = s.has(idx);
      const hadNext = s.has(nextIdx);
      // Swap presence
      if (hadIdx !== hadNext) {
        if (hadIdx) { s.delete(idx); s.add(nextIdx); }
        else { s.delete(nextIdx); s.add(idx); }
      }
      return s;
    });
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 110, background: 'rgba(6,12,26,0.72)', backdropFilter: 'blur(10px)', display: 'grid', placeItems: 'center', padding: 18, overflow: 'auto' }}>
      <div onClick={(e) => e.stopPropagation()} className="scale-in" style={{ width: '100%', maxWidth: 760, background: `linear-gradient(180deg, ${SURFACE} 0%, rgba(14,22,44,0.98) 100%)`, borderRadius: 22, border: `1px solid ${BORDER}`, boxShadow: '0 40px 80px -20px rgba(0,0,0,0.7)', padding: 28, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <span style={{ width: 34, height: 34, borderRadius: 10, background: 'linear-gradient(135deg, #A78BFA, #5BBEFC)', display: 'grid', placeItems: 'center', color: NAVY }}><Sparkles size={16} strokeWidth={2.6} /></span>
          <h2 style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 20, color: T1, margin: 0, flex: 1 }}>
            {step === 'compose' ? (title || 'IA doc — Générer des tâches') : `Preview · ${drafts.length} ${commitLabel || 'tâche'}${drafts.length > 1 ? 's' : ''} extraite${drafts.length > 1 ? 's' : ''}`}
          </h2>
          <button type="button" onClick={onClose} style={{ background: 'transparent', border: 'none', color: T3, cursor: 'pointer' }}><X size={16} /></button>
        </div>

        {step === 'compose' ? (
          <form onSubmit={runPreview}>
            <p style={{ fontFamily: 'Sora', fontWeight: 500, fontSize: 13, color: T2, margin: '4px 0 16px', letterSpacing: '-0.005em' }}>
              {subtitle || "Colle un brief. L'IA extrait les tâches puis tu confirmes avant qu'elles apparaissent dans le Kanban."}
            </p>
            <textarea
              data-testid="ia-doc-text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Ex.&#10;Lundi : Axel monte l'épisode 4 Mathis, cut serré.&#10;Mardi : Léa livre 3 thumbnails.&#10;Jeudi urgent : Reel Instagram Jessie 9:16."
              rows={9}
              autoFocus
              style={{ width: '100%', padding: '12px 14px', borderRadius: 12, background: SURFACE2, border: `1px solid ${BORDER}`, color: T1, outline: 'none', fontFamily: 'Sora', fontWeight: 500, fontSize: 13, resize: 'vertical', minHeight: 180, marginBottom: 14 }}
            />
            {/* Iter 62fw — Toggle "Séquence de posts" : marque toutes les tâches
                créées comme poster_tasks rattachées à un projet client. */}
            <div style={{
              marginBottom: 14, padding: 12, borderRadius: 12,
              background: isPostSequence
                ? 'linear-gradient(135deg, rgba(245,197,106,0.12) 0%, rgba(217,154,66,0.06) 100%)'
                : SURFACE2,
              border: `1px solid ${isPostSequence ? 'rgba(245,197,106,0.4)' : BORDER}`,
              transition: 'all 0.15s var(--ease-out)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}
                   onClick={() => setIsPostSequence((v) => !v)}
                   data-testid="ia-doc-toggle-post-sequence">
                <div style={{
                  width: 38, height: 22, borderRadius: 999, flexShrink: 0,
                  background: isPostSequence ? 'linear-gradient(180deg, #F5C56A, #D99A42)' : SURFACE,
                  border: `1px solid ${isPostSequence ? '#D99A42' : BORDER}`,
                  position: 'relative', transition: 'all 0.18s var(--ease-out)',
                }}>
                  <div style={{
                    position: 'absolute', top: 2, left: isPostSequence ? 18 : 2,
                    width: 16, height: 16, borderRadius: 999,
                    background: isPostSequence ? '#0B1330' : T2,
                    transition: 'all 0.18s cubic-bezier(0.22, 1, 0.36, 1)',
                  }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 13, color: T1, letterSpacing: '-0.005em' }}>
                    📱 Séquence de posts
                  </div>
                  <div style={{ fontFamily: 'Sora', fontWeight: 500, fontSize: 11, color: T3, marginTop: 2 }}>
                    {isPostSequence
                      ? 'Les tâches créées seront des "à poster" rattachées au client ci-dessous.'
                      : 'Active pour créer une série de posts pour un client.'}
                  </div>
                </div>
              </div>
              {isPostSequence && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 10.5, color: T3, letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 6 }}>Étiquette client</div>
                  <input
                    data-testid="ia-doc-sequence-client-label"
                    value={clientLabel}
                    onChange={(e) => setClientLabel(e.target.value)}
                    placeholder="Ex. Nash, PodMax, Real, ou un nouveau client…"
                    style={{
                      width: '100%', padding: '9px 12px', borderRadius: 9,
                      background: SURFACE2, border: `1px solid ${BORDER}`,
                      color: T1, outline: 'none',
                      fontFamily: 'Sora', fontWeight: 600, fontSize: 12.5,
                      marginBottom: existingLabels.length > 0 ? 8 : 0,
                    }}
                  />
                  {existingLabels.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 2 }}>
                      {existingLabels.slice(0, 12).map((l) => {
                        const active = clientLabel.trim().toLowerCase() === l.label.toLowerCase();
                        return (
                          <button
                            key={l.label}
                            type="button"
                            data-testid={`ia-doc-label-chip-${l.label}`}
                            onClick={() => setClientLabel(l.label)}
                            style={{
                              padding: '4px 10px', borderRadius: 999,
                              background: active ? 'rgba(245,197,106,0.22)' : SURFACE2,
                              border: `1px solid ${active ? 'rgba(245,197,106,0.55)' : BORDER}`,
                              color: active ? '#F5C56A' : T2,
                              fontFamily: 'Sora', fontWeight: 700, fontSize: 11,
                              cursor: 'pointer', letterSpacing: '-0.005em',
                            }}
                          >
                            {l.label} <span style={{ opacity: 0.55, fontWeight: 500 }}>· {l.count}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
            {/* Override responsables (multi-assign) */}
            <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 11.5, color: T3, letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 8 }}>Assigner à</div>
            <p style={{ fontFamily: 'Sora', fontWeight: 500, fontSize: 11.5, color: T3, margin: '0 0 8px', letterSpacing: '-0.005em' }}>
              {overrideResps.length === 0 ? 'Auto — l\u2019IA détecte le prénom dans le brief. Clique pour forcer des personnes.' : `${overrideResps.length} personne${overrideResps.length > 1 ? 's' : ''} forcée${overrideResps.length > 1 ? 's' : ''}.`}
            </p>
            <div data-testid="ia-doc-override-resps" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 20 }}>
              {ALL_TEAM.map((m) => {
                const active = overrideResps.includes(m.id);
                return (
                  <button
                    key={m.id}
                    type="button"
                    data-testid={`ia-doc-override-${m.id}`}
                    onClick={() => toggleOverride(m.id)}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px 5px 5px', borderRadius: 999, background: active ? 'rgba(62,123,250,0.16)' : SURFACE2, border: `1px solid ${active ? BLUE : BORDER}`, color: active ? T1 : T2, fontFamily: 'Sora', fontWeight: 700, fontSize: 11.5, cursor: 'pointer' }}
                  >
                    <span style={{ width: 22, height: 22, borderRadius: 999, background: `linear-gradient(180deg, ${BLUE_LIGHT} 0%, ${BLUE} 100%)`, color: NAVY, fontFamily: 'Sora', fontWeight: 800, fontSize: 10, display: 'grid', placeItems: 'center' }}>{m.initial}</span>
                    <span>{m.name}</span>
                    {active && <span style={{ color: BLUE_LIGHT, marginLeft: 2 }}>✓</span>}
                  </button>
                );
              })}
              {overrideResps.length > 0 && (
                <button type="button" data-testid="ia-doc-override-clear" onClick={() => setOverrideResps([])} style={{ padding: '5px 10px', borderRadius: 999, background: 'transparent', border: `1px dashed ${BORDER}`, color: T3, fontFamily: 'Sora', fontWeight: 600, fontSize: 11, cursor: 'pointer' }}>Auto</button>
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 10 }}>
              <button type="button" onClick={onClose} style={{ padding: '12px 18px', borderRadius: 12, background: 'transparent', border: `1px solid ${BORDER}`, color: T1, fontFamily: 'Sora', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>Annuler</button>
              <button type="submit" data-testid="ia-doc-preview-btn" disabled={busy} style={{ padding: '12px 18px', borderRadius: 12, background: 'linear-gradient(135deg, #A78BFA, #5BBEFC)', border: 'none', color: NAVY, fontFamily: 'Sora', fontWeight: 800, fontSize: 13, cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.7 : 1, boxShadow: '0 8px 22px -6px rgba(167,139,250,0.55), inset 0 1px 0 rgba(255,255,255,0.28)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                {busy ? 'Analyse en cours…' : (<><Sparkles size={13} strokeWidth={2.6} /> Analyser le doc</>)}
              </button>
            </div>
          </form>
        ) : (
          <>
            <p style={{ fontFamily: 'Sora', fontWeight: 500, fontSize: 13, color: T2, margin: '4px 0 14px', letterSpacing: '-0.005em' }}>
              Décoche celles à ignorer, ajuste les assignés (clique + pour ajouter d&apos;autres personnes), puis confirme.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 18 }}>
              {drafts.map((d, i) => (
                <IADocDraftRow
                  key={i}
                  idx={i}
                  total={drafts.length}
                  draft={d}
                  checked={selected.has(i)}
                  onToggle={() => toggleIdx(i)}
                  onToggleResp={(mid) => toggleDraftResp(i, mid)}
                  onMove={(dir) => moveDraft(i, dir)}
                />
              ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 10 }}>
              <button type="button" onClick={() => setStep('compose')} style={{ padding: '12px 18px', borderRadius: 12, background: 'transparent', border: `1px solid ${BORDER}`, color: T1, fontFamily: 'Sora', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>← Modifier</button>
              <button type="button" data-testid="ia-doc-commit-btn" onClick={commit} disabled={busy || selected.size === 0} style={{ padding: '12px 18px', borderRadius: 12, background: 'linear-gradient(135deg, #A78BFA, #5BBEFC)', border: 'none', color: NAVY, fontFamily: 'Sora', fontWeight: 800, fontSize: 13, cursor: busy ? 'wait' : 'pointer', opacity: (busy || selected.size === 0) ? 0.55 : 1, boxShadow: '0 8px 22px -6px rgba(167,139,250,0.55), inset 0 1px 0 rgba(255,255,255,0.28)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                {busy ? 'Création…' : `Créer ${selected.size} tâche${selected.size > 1 ? 's' : ''}`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Iter 62dh — Ligne de preview IA doc avec picker multi-assign inline.
function IADocDraftRow({ idx, total, draft, checked, onToggle, onToggleResp, onMove }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const resps = draft.responsables || [];
  const members = resps.map(memberById).filter(Boolean);
  return (
    <div data-testid={`ia-doc-draft-${idx}`} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '11px 12px', borderRadius: 12, background: SURFACE2, border: `1px solid ${checked ? BLUE : BORDER}`, position: 'relative' }}>
      {/* Iter 62ex — Position + réordre manuel (Rayan msg : "on puisse rechanger l'ordre si l'IA a mal fait") */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, paddingTop: 2 }}>
        <button
          type="button"
          data-testid={`ia-doc-up-${idx}`}
          onClick={() => onMove?.(-1)}
          disabled={idx === 0}
          style={{ width: 22, height: 18, borderRadius: 5, background: 'rgba(167,139,250,0.10)', border: '1px solid rgba(167,139,250,0.28)', color: '#B294F5', cursor: idx === 0 ? 'not-allowed' : 'pointer', opacity: idx === 0 ? 0.3 : 1, display: 'grid', placeItems: 'center', padding: 0 }}
          title="Monter"
        ><ChevronUp size={12} /></button>
        <div style={{ width: 26, height: 20, borderRadius: 6, background: 'rgba(28,38,80,0.6)', border: '1px solid rgba(50,64,120,0.5)', color: T1, display: 'grid', placeItems: 'center', fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, fontSize: 11 }}>{String(idx + 1).padStart(2, '0')}</div>
        <button
          type="button"
          data-testid={`ia-doc-down-${idx}`}
          onClick={() => onMove?.(1)}
          disabled={idx === total - 1}
          style={{ width: 22, height: 18, borderRadius: 5, background: 'rgba(167,139,250,0.10)', border: '1px solid rgba(167,139,250,0.28)', color: '#B294F5', cursor: idx === total - 1 ? 'not-allowed' : 'pointer', opacity: idx === total - 1 ? 0.3 : 1, display: 'grid', placeItems: 'center', padding: 0 }}
          title="Descendre"
        ><ChevronDown size={12} /></button>
      </div>
      <input type="checkbox" checked={checked} onChange={onToggle} style={{ accentColor: BLUE, marginTop: 4, width: 15, height: 15 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 13, color: T1, marginBottom: 6 }}>{draft.title}</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ padding: '2px 7px', borderRadius: 999, background: `${PRIORITY[draft.priority]?.color || BLUE}22`, border: `1px solid ${PRIORITY[draft.priority]?.color || BLUE}55`, color: PRIORITY[draft.priority]?.color || BLUE, fontFamily: 'Sora', fontWeight: 700, fontSize: 9.5, textTransform: 'uppercase' }}>{PRIORITY[draft.priority]?.label || draft.priority}</span>
          <span style={{ fontFamily: 'Sora', fontWeight: 500, fontSize: 11, color: T3 }}>{draft.date_debut}{draft.date_fin && draft.date_fin !== draft.date_debut ? ` → ${draft.date_fin}` : ''}</span>
          {draft.est_video && <span style={{ padding: '2px 7px', borderRadius: 999, background: 'rgba(62,123,250,0.14)', border: '1px solid rgba(62,123,250,0.3)', fontFamily: 'Sora', fontWeight: 700, fontSize: 9.5, color: BLUE_LIGHT, textTransform: 'uppercase' }}>Vidéo</span>}
          {draft.est_video && draft.format && (
            <span data-testid={`ia-doc-draft-${idx}-format`} style={{ padding: '2px 8px', borderRadius: 999, background: 'rgba(167,139,250,0.14)', border: '1px solid rgba(167,139,250,0.32)', fontFamily: 'Sora', fontWeight: 800, fontSize: 9.5, color: '#B294F5', letterSpacing: '0.04em', textTransform: 'uppercase' }}>{draft.format}</span>
          )}
          {/* Multi-assign chips + add button */}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginLeft: 4 }}>
            <span style={{ fontFamily: 'Sora', fontWeight: 600, fontSize: 10.5, color: T3, letterSpacing: '0.04em', textTransform: 'uppercase' }}>Assigné :</span>
            {members.map((m) => (
              <span key={m.id} data-testid={`ia-doc-draft-${idx}-resp-${m.id}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px 2px 3px', borderRadius: 999, background: 'rgba(62,123,250,0.14)', border: '1px solid rgba(62,123,250,0.35)' }}>
                <span style={{ width: 16, height: 16, borderRadius: 999, background: `linear-gradient(180deg, ${BLUE_LIGHT} 0%, ${BLUE} 100%)`, color: NAVY, fontFamily: 'Sora', fontWeight: 800, fontSize: 8.5, display: 'grid', placeItems: 'center' }}>{m.initial}</span>
                <span style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 10.5, color: T1 }}>{m.name}</span>
              </span>
            ))}
            <button
              type="button"
              data-testid={`ia-doc-draft-${idx}-add-resp`}
              onClick={() => setPickerOpen((o) => !o)}
              style={{ width: 20, height: 20, borderRadius: 999, background: 'rgba(62,123,250,0.14)', border: '1px solid rgba(62,123,250,0.35)', color: BLUE_LIGHT, cursor: 'pointer', display: 'grid', placeItems: 'center' }}
              title="Assigner à d'autres personnes"
            ><Plus size={11} strokeWidth={2.8} /></button>
          </div>
        </div>
        {draft.description && <div style={{ fontFamily: 'Sora', fontWeight: 500, fontSize: 11.5, color: T3, marginTop: 5, whiteSpace: 'pre-wrap' }}>{draft.description}</div>}
        {pickerOpen && (
          <div data-testid={`ia-doc-draft-${idx}-picker`} style={{ marginTop: 8, padding: 8, borderRadius: 10, background: NAVY, border: `1px solid ${BORDER}` }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
              {ALL_TEAM.map((m) => {
                const active = resps.includes(m.id);
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => onToggleResp(m.id)}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px 4px 4px', borderRadius: 999, background: active ? 'rgba(62,123,250,0.18)' : SURFACE2, border: `1px solid ${active ? BLUE : BORDER}`, color: active ? T1 : T2, fontFamily: 'Sora', fontWeight: 700, fontSize: 10.5, cursor: 'pointer' }}
                  >
                    <span style={{ width: 18, height: 18, borderRadius: 999, background: `linear-gradient(180deg, ${BLUE_LIGHT} 0%, ${BLUE} 100%)`, color: NAVY, fontFamily: 'Sora', fontWeight: 800, fontSize: 9, display: 'grid', placeItems: 'center' }}>{m.initial}</span>
                    <span>{m.name}</span>
                    {active && <span style={{ color: BLUE_LIGHT, marginLeft: 1 }}>✓</span>}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}



// ─── Documents section (Iter 62ds) ──────────────────────────────────────────
// Upload/list/download/delete réels via Emergent Object Storage.
function DocumentsSection({ taskId, documents, busy, setBusy, onChanged }) {
  const [dragOver, setDragOver] = useState(false);
  const doUpload = async (files) => {
    if (!files || !files.length) return;
    setBusy(true);
    let ok = 0, fail = 0;
    for (const file of Array.from(files)) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        await api.post(`/asa-tasks/${taskId}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        ok++;
      } catch (err) {
        fail++;
        toast.error(err?.response?.data?.detail || `Upload « ${file.name} » échoué`);
      }
    }
    setBusy(false);
    if (ok) toast.success(`${ok} document${ok > 1 ? 's' : ''} ajouté${ok > 1 ? 's' : ''}`);
    if (ok || fail) onChanged?.();
  };
  const onPick = (e) => { doUpload(e.target.files); e.target.value = ''; };
  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    doUpload(e.dataTransfer.files);
  };
  const openDoc = async (doc) => {
    try {
      const r = await api.get(`/asa-tasks/${taskId}/documents/${doc.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error("Impossible d'ouvrir le document");
    }
  };
  const downloadDoc = async (doc) => {
    try {
      const r = await api.get(`/asa-tasks/${taskId}/documents/${doc.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = doc.filename || 'document';
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) {
      toast.error("Téléchargement impossible");
    }
  };
  const deleteDoc = async (doc) => {
    if (!window.confirm(`Retirer « ${doc.filename} » ?`)) return;
    try {
      await api.delete(`/asa-tasks/${taskId}/documents/${doc.id}`);
      toast.success('Document retiré');
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
    }
  };
  const fmtSize = (bytes) => {
    if (!bytes && bytes !== 0) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };
  return (
    <div style={{ marginBottom: 22 }} data-testid="documents-section">
      <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 11.5, color: T3, letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 10 }}>
        Documents {documents.length > 0 && <span style={{ color: T2, marginLeft: 4 }}>· {documents.length}</span>}
      </div>
      <label
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '12px 14px', borderRadius: 12,
          background: dragOver ? 'rgba(62,123,250,0.14)' : SURFACE2,
          border: `1px dashed ${dragOver ? BLUE : BORDER}`,
          color: T2, cursor: busy ? 'wait' : 'pointer',
          transition: 'background 0.15s var(--ease-out), border-color 0.15s var(--ease-out)',
          fontFamily: 'Sora', fontWeight: 600, fontSize: 12.5,
        }}
        data-testid="doc-dropzone"
      >
        <Paperclip size={14} strokeWidth={2.4} color={BLUE_LIGHT} />
        <span style={{ flex: 1 }}>{busy ? 'Envoi en cours…' : 'Glisse un fichier ici, ou clique pour choisir'}</span>
        <input
          type="file"
          multiple
          onChange={onPick}
          disabled={busy}
          style={{ display: 'none' }}
          data-testid="doc-input"
        />
      </label>
      {documents.length > 0 && (
        <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {documents.map((doc) => (
            <div key={doc.id} data-testid={`doc-row-${doc.id}`} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10, background: SURFACE2, border: `1px solid ${BORDER}` }}>
              <FileIcon size={14} strokeWidth={2.2} color={BLUE_LIGHT} />
              <button
                type="button"
                onClick={() => openDoc(doc)}
                data-testid={`doc-open-${doc.id}`}
                style={{ flex: 1, textAlign: 'left', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, minWidth: 0 }}
                title="Ouvrir le document"
              >
                <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 12.5, color: T1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{doc.filename || 'document'}</div>
                <div style={{ fontFamily: 'Sora', fontWeight: 500, fontSize: 10.5, color: T3, marginTop: 2 }}>
                  {fmtSize(doc.size)} {doc.uploaded_by_name ? `· ${doc.uploaded_by_name}` : ''}
                </div>
              </button>
              <button
                type="button"
                onClick={() => downloadDoc(doc)}
                data-testid={`doc-download-${doc.id}`}
                style={{ width: 32, height: 32, borderRadius: 8, background: 'transparent', border: `1px solid ${BORDER}`, color: T2, cursor: 'pointer', display: 'grid', placeItems: 'center' }}
                title="Télécharger"
              ><Download size={13} /></button>
              <button
                type="button"
                onClick={() => deleteDoc(doc)}
                data-testid={`doc-delete-${doc.id}`}
                style={{ width: 32, height: 32, borderRadius: 8, background: 'transparent', border: '1px solid rgba(255,107,107,0.28)', color: '#FF7B7B', cursor: 'pointer', display: 'grid', placeItems: 'center' }}
                title="Retirer"
              ><Trash2 size={13} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ─── Task edit dialog (Iter 62ds) ───────────────────────────────────────────
// Réutilise l'UI du TaskCreateDialog mais pré-remplie et appelant PATCH.
function TaskEditDialog({ task, isMyView, projects, onClose, onSaved }) {
  const [title, setTitle] = useState(task.title || '');
  const [description, setDescription] = useState(task.description || '');
  const [responsables, setResponsables] = useState(respsOf(task).length ? respsOf(task) : ['rayan']);
  const [priority, setPriority] = useState(task.priority || 'normal');
  const [dateDebut, setDateDebut] = useState(task.date_debut || isoLocal());
  const [noDate, setNoDate] = useState(!task.date_debut);
  const [multiJours, setMultiJours] = useState((task.date_fin || task.date_debut) !== task.date_debut);
  const [dateFin, setDateFin] = useState(task.date_fin || task.date_debut || isoLocal());
  const [heureDebut, setHeureDebut] = useState(task.heure_debut || '');
  const [heureFin, setHeureFin] = useState(task.heure_fin || '');
  const [estVideo, setEstVideo] = useState(!!task.est_video);
  const [videoMeta, setVideoMeta] = useState({
    lien_rushs: task.video_meta?.lien_rushs || '',
    dossier_travail: task.video_meta?.dossier_travail || '',
    instructions_montage: task.video_meta?.instructions_montage || '',
    format: task.video_meta?.format || '',
  });
  const [projet, setProjet] = useState(task.projet || '');
  const [notes, setNotes] = useState(task.notes || '');
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const toggleResp = (id) => {
    setResponsables((prev) => prev.includes(id) ? (prev.length === 1 ? prev : prev.filter((x) => x !== id)) : [...prev, id]);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { toast.error('Titre requis'); return; }
    if (!responsables.length) { toast.error('Au moins un responsable'); return; }
    setBusy(true);
    try {
      await api.patch(`/asa-tasks/${task.id}`, {
        title: title.trim(),
        description: description.trim(),
        responsable: responsables,
        priority,
        ...(noDate ? { clear_date: true } : {
          date_debut: dateDebut,
          date_fin: multiJours ? dateFin : dateDebut,
          heure_debut: heureDebut || null,
          heure_fin: heureFin || null,
        }),
        projet: projet || null,
        notes: notes.trim(),
        est_video: estVideo,
        video_meta: estVideo ? videoMeta : null,
      });
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
    }
    setBusy(false);
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 120, background: 'rgba(6,12,26,0.72)', backdropFilter: 'blur(10px)', display: 'grid', placeItems: 'center', padding: 18, overflow: 'auto' }}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="scale-in" data-testid="edit-task-dialog" style={{ width: '100%', maxWidth: 560, background: `linear-gradient(180deg, ${SURFACE} 0%, rgba(14,22,44,0.98) 100%)`, borderRadius: 22, border: `1px solid ${BORDER}`, boxShadow: '0 40px 80px -20px rgba(0,0,0,0.7)', padding: 28, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
          <h2 style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 20, color: T1, margin: 0, flex: 1 }}>Modifier la tâche</h2>
          <button type="button" onClick={onClose} style={{ background: 'transparent', border: 'none', color: T3, cursor: 'pointer' }}><X size={16} /></button>
        </div>

        <FieldLabel>Titre</FieldLabel>
        <input data-testid="ed-title" value={title} onChange={(e) => setTitle(e.target.value)} autoFocus style={inputStyle} />

        <FieldLabel>Description</FieldLabel>
        <textarea data-testid="ed-desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} style={{ ...inputStyle, resize: 'vertical', minHeight: 60 }} />

        <FieldLabel>Assigné à <span style={{ color: T3, fontWeight: 500 }}>({responsables.length})</span></FieldLabel>
        <div data-testid="ed-resps" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
          {ALL_TEAM.map((m) => {
            const active = responsables.includes(m.id);
            return (
              <button
                key={m.id}
                type="button"
                data-testid={`ed-resp-${m.id}`}
                onClick={() => toggleResp(m.id)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px 5px 5px', borderRadius: 999, background: active ? 'rgba(62,123,250,0.16)' : SURFACE2, border: `1px solid ${active ? BLUE : BORDER}`, color: active ? T1 : T2, fontFamily: 'Sora', fontWeight: 700, fontSize: 11.5, cursor: 'pointer' }}
              >
                <span style={{ width: 22, height: 22, borderRadius: 999, background: `linear-gradient(180deg, ${BLUE_LIGHT} 0%, ${BLUE} 100%)`, color: NAVY, fontFamily: 'Sora', fontWeight: 800, fontSize: 10, display: 'grid', placeItems: 'center' }}>{m.initial}</span>
                <span>{m.name}</span>
                {active && <span style={{ color: BLUE_LIGHT, marginLeft: 2 }}>✓</span>}
              </button>
            );
          })}
        </div>

        <FieldLabel>Priorité</FieldLabel>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
          {Object.entries(PRIORITY).map(([id, p]) => {
            const active = priority === id;
            return (
              <button key={id} type="button" data-testid={`ed-prio-${id}`} onClick={() => setPriority(id)} style={{ padding: '7px 14px', borderRadius: 10, background: active ? `${p.color}22` : 'transparent', border: `1px solid ${active ? p.color : BORDER}`, color: active ? p.color : T2, fontFamily: 'Sora', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>{p.label}</button>
            );
          })}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <FieldLabel>Deadline</FieldLabel>
          {!task.source_video_id && <button type="button" data-testid="ed-no-date-toggle" onClick={() => setNoDate((v) => !v)} style={{ padding: '4px 10px', borderRadius: 999, background: noDate ? 'rgba(245,197,106,0.16)' : 'transparent', border: `1px solid ${noDate ? '#F5C56A' : BORDER}`, color: noDate ? '#F5C56A' : T2, fontFamily: 'Sora', fontWeight: 700, fontSize: 11, cursor: 'pointer' }}>{noDate ? '✓ Sans date' : 'Sans date'}</button>}
        </div>
        {noDate && <div data-testid="ed-no-date-info" style={{ fontFamily: 'Sora', fontSize: 11.5, color: '#F5C56A', margin: '4px 0 10px' }}>Sans date ni heure · visible chaque jour jusqu'à la fin · envoyée dans Google Tasks.</div>}
        <div style={{ display: noDate ? 'none' : 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 4 }}>
          <input data-testid="ed-date" type="date" value={dateDebut} onChange={(e) => { setDateDebut(e.target.value); if (!multiJours) setDateFin(e.target.value); }} style={inputStyle} />
          <input data-testid="ed-heure-debut" type="time" value={heureDebut} onChange={(e) => setHeureDebut(e.target.value)} style={inputStyle} />
          <input data-testid="ed-heure-fin" type="time" value={heureFin} onChange={(e) => setHeureFin(e.target.value)} style={inputStyle} />
        </div>

        <div style={{ display: noDate ? 'none' : 'flex', alignItems: 'center', gap: 10, padding: '10px 0 4px' }}>
          <input id="ed-multi" data-testid="ed-multi" type="checkbox" checked={multiJours} onChange={(e) => setMultiJours(e.target.checked)} style={{ accentColor: BLUE }} />
          <label htmlFor="ed-multi" style={{ fontFamily: 'Sora', fontWeight: 600, fontSize: 12.5, color: T2, cursor: 'pointer' }}>Sur plusieurs jours d&apos;affilée ?</label>
        </div>
        {multiJours && (
          <>
            <FieldLabel>Jusqu&apos;au</FieldLabel>
            <input data-testid="ed-date-end" type="date" value={dateFin} onChange={(e) => setDateFin(e.target.value)} style={inputStyle} min={dateDebut} />
          </>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0 4px' }}>
          <input id="ed-video" data-testid="ed-video" type="checkbox" checked={estVideo} onChange={(e) => setEstVideo(e.target.checked)} style={{ accentColor: BLUE }} />
          <label htmlFor="ed-video" style={{ fontFamily: 'Sora', fontWeight: 600, fontSize: 12.5, color: T2, cursor: 'pointer' }}>C&apos;est une tâche vidéo ?</label>
        </div>
        {estVideo && (
          <div style={{ padding: 12, borderRadius: 12, background: SURFACE2, border: `1px solid ${BORDER}`, marginTop: 4 }}>
            <input data-testid="ed-format" placeholder="Format (ex: décor jaune, podcast intro…)" value={videoMeta.format || ''} onChange={(e) => setVideoMeta({ ...videoMeta, format: e.target.value })} style={{ ...inputStyle, marginBottom: 6 }} />
            <input data-testid="ed-rushs" placeholder="Lien de la vidéo" value={videoMeta.lien_rushs} onChange={(e) => setVideoMeta({ ...videoMeta, lien_rushs: e.target.value })} style={{ ...inputStyle, marginBottom: 6 }} />
            <input data-testid="ed-dossier" placeholder="Lien de dépôt" value={videoMeta.dossier_travail} onChange={(e) => setVideoMeta({ ...videoMeta, dossier_travail: e.target.value })} style={{ ...inputStyle, marginBottom: 6 }} />
            <textarea data-testid="ed-instr" placeholder="Indication" value={videoMeta.instructions_montage} onChange={(e) => setVideoMeta({ ...videoMeta, instructions_montage: e.target.value })} rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
          </div>
        )}

        {(projects && projects.length > 0) && (
          <>
            <FieldLabel>Projet</FieldLabel>
            <select data-testid="ed-projet" value={projet} onChange={(e) => setProjet(e.target.value)} style={inputStyle}>
              <option value="">— Aucun —</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name || p.title || p.id}</option>)}
            </select>
          </>
        )}

        <button type="button" onClick={() => setDetailsOpen(!detailsOpen)} style={{ marginTop: 12, padding: '7px 0', background: 'transparent', border: 'none', color: T3, fontFamily: 'Sora', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>{detailsOpen ? '− Masquer' : '+ Notes internes'}</button>
        {detailsOpen && (
          <textarea data-testid="ed-notes" placeholder="Notes / liens" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 10, marginTop: 22 }}>
          <button type="button" onClick={onClose} style={{ padding: '12px 18px', borderRadius: 12, background: 'transparent', border: `1px solid ${BORDER}`, color: T1, fontFamily: 'Sora', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>Annuler</button>
          <button type="submit" data-testid="ed-submit" disabled={busy} style={{ padding: '12px 18px', borderRadius: 12, background: `linear-gradient(180deg, ${BLUE_LIGHT} 0%, ${BLUE} 100%)`, border: 'none', color: NAVY, fontFamily: 'Sora', fontWeight: 800, fontSize: 13, cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.7 : 1, boxShadow: '0 8px 22px -6px rgba(62,123,250,0.55), inset 0 1px 0 rgba(255,255,255,0.28)' }}>{busy ? 'Enregistrement…' : 'Enregistrer'}</button>
        </div>
      </form>
    </div>
  );
}
